/**
 * Incident Analysis Component
 * Last updated: July 15, 2026
 */

import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../services/authFetch';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Divider,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Checkbox,
  ListItemText,
  OutlinedInput,
  TablePagination,
  TextField,
  Tooltip as MUITooltip,
  Tabs,
  Tab,
  Fab,
  CircularProgress,
  Snackbar,
  Link,
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  Warning,
  Error as ErrorIcon,
  Info,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  Compare,
  VolumeUp,
  Close,
  GetApp as DownloadIcon,
  ViewModule,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Search,
  Sort,
  ArrowUpward,
  ArrowDownward,
  Share,
  SmartToy,
  PictureAsPdf as PictureAsPdfIcon,
  KeyboardArrowUp,
  InfoOutlined,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  ComposedChart,
  Area,
  AreaChart
} from 'recharts';
import ReactMarkdown from 'react-markdown';
import { fetchPortfolioNames, fetchAllIncidents, fetchAllIncidentsWithDateRange } from '../services/reliabilityService';
import { INCIDENTS } from '../config/api';
import { useGlobalFilters } from '../contexts/FilterContext';
import { PDFExportService } from '../services/pdfExportService';

interface IncidentData {
  id: string;
  title: string;
  priority: 'P1' | 'P2' | 'P3' | 'P4';
  portfolio: string;
  application: string;
  createdDate: string;
  resolvedDate?: string;
  status: string; // Use string to allow any status from API
  assignedTo: string;
  rootCause: string;
  impact: 'High' | 'Medium' | 'Low';
  number: string;
  platform: string;
  outage_duration?: number;
  contactType: string;
  capturedByAlert: boolean; // New field for alert capture status
  causedByChange: boolean; // New field for change-related incidents
  changeNumber?: string; // Store the change number for tooltip
  closeCode: string; // Closure code from ServiceNow (e.g., "Self-Recovered")
  short_description?: string; // Short description from API
  caused_by_app?: string; // Application that caused the incident
  caused_by_platform?: string; // Platform that caused the incident
  caused_by_portfolio?: string; // Portfolio that caused the incident
  mttr?: number; // Mean Time To Resolution in seconds
  mttd?: number; // Mean Time To Detect in seconds
  outage_number?: string; // Outage record number
}

// Hierarchy types for Trends & Analysis table
interface TrendMetricsByPeriod {
  [period: string]: {
    incidentCount: number;
    totalMTTRSeconds: number;
    lostHours: number;
  };
}

interface TrendHierarchyNode  {
  name: string;
  level: 'portfolio' | 'platform' | 'application';
  metrics: TrendMetricsByPeriod;
  records: IncidentData[];
  children?: TrendHierarchyNode[];
}

interface TrendPeriodTarget {
  incidentCount: number;
  mttrAvgMinutes: number;
  lostHours: number;
}

interface MTTRData {
  mttr: string;
  mttd: string;
  number: string;
  priority: number;
}

interface NoiseData {
  ciName: string;
  alertCount: number;
  percentOfTotal: number;
  portfolio: string;
  lastIncident: string;
  avgResolutionTime: number;
}

// AI Insights interfaces
interface AIInsight {
  type: 'critical' | 'warning' | 'info' | 'success';
  title: string;
  description: string;
  action?: string;
  confidence?: number;
  causedByChangePercentage?: number;
}

interface ActionItemsAnalysis {
  action_type_distribution?: Record<string, number>;
  assignee_workload?: Record<string, number>;
  high_priority_actions_count?: number;
  overdue_actions_count?: number;
  total_open_actions?: number;
}

interface AlertEffectivenessImprovement {
  action: string;
  current_rate: string;
  issue: string;
  missed_incidents: number;
  target_rate: string;
}

interface MonitoringMaturityGap {
  application: string;
  improvement_priority: string;
  maturity_score: number;
  missing_capabilities: string[];
}

interface RootCauseMonitoringGapItem {
  action: string;
  incident_count: number;
  missing_capability: string;
  priority: string;
  root_cause: string;
}

interface MonitoringCorrelation {
  applications_analyzed: number;
  incident_prevention_opportunities?: {
    alert_effectiveness_improvements?: AlertEffectivenessImprovement[];
    incident_prevention_potential?: Record<string, unknown>;
    monitoring_maturity_gaps?: MonitoringMaturityGap[];
  };
  monitoring_data_available?: boolean;
  root_cause_monitoring_gaps?: {
    action_items?: RootCauseMonitoringGapItem[];
    correlations_found?: number;
    monitoring_gaps_by_root_cause?: Record<string, unknown>;
  };
}

interface PostmortemInsights {
  action_items_analysis?: ActionItemsAnalysis;
  correlation_summary?: Record<string, unknown>;
  monitoring_correlation?: MonitoringCorrelation;
}

interface IncidentAIInsightsResponse {
  success?: boolean;
  data: {
    // Executive Summary fields
    analysis_period?: string;
    incident_count?: number;
    application_count?: number;
    
    // Application Breakdown fields
    application_breakdown?: {
      total_applications?: number;
      mvc_applications?: number;
      non_mvc_applications?: number;
      applications_with_incidents?: number;
      high_impact_applications?: number;
    };
    
    // Prediction Analysis fields
    prediction_analysis?: {
      summary?: string;
      risk_factors?: {
        factor: string;
        risk_level: string;
        impact_score: number;
      }[];
      recommendations?: {
        recommendation: string;
        priority?: string;
      }[];
    };
    
    analysis_duration_seconds?: number;
    analysis_timestamp?: string;
    scope_name?: string;
    performance_summary?: string;
    escalation_risk?: {
      escalation_risk_score?: number;
      historical_escalation_rate?: string;
      recommended_preventive_action?: string;
    };
    monitoring_insights?: {
      applications_analyzed?: number;
      correlation_analysis?: string;
      high_risk_apps?: string[];
      improvement_priority?: string[];
      monitoring_gaps?: string[];
      technical_debt_assessment?: string;
    };
    postmortem_insights?: {
      action_items_analysis?: {
        // Enhanced action items fields
        total_action_items?: number;
        completed_actions?: number;
        priority_breakdown?: {
          p1?: number;
          p2?: number;
          p3?: number;
          p4?: number;
        };
        status_breakdown?: {
          open?: number;
          in_progress?: number;
          under_review?: number;
          completed?: number;
        };
        // Existing fields
        action_type_distribution?: any;
        assignee_workload?: any;
        busiest_assignees?: string[];
        high_priority_actions_count?: number;
        overdue_actions_count?: number;
        top_action_types?: string[];
        total_open_actions?: number;
      };
      correlation_summary?: {
        data_quality?: string;
        total_open_actions_found?: number;
        total_postmortems_found?: number;
      };
      monitoring_correlation?: {
        applications_analyzed?: number;
        monitoring_data_available?: boolean;
        // Enhanced monitoring correlation fields
        correlation_success_rate?: number;
        mean_time_to_detection?: number;
        // MVC status summary from API
        mvc_status_summary?: {
          total_applications?: number;
          mvc_applications?: number;
          non_mvc_applications?: number;
          monitoring_tracking_available?: number;
          monitoring_tracking_unavailable?: number;
        };
        incident_prevention_opportunities?: {
          alert_effectiveness_improvements?: {
            action: string;
            current_rate: string;
            issue: string;
            missed_incidents: number;
            target_rate: string;
          }[];
          monitoring_maturity_gaps?: {
            application: string;
            improvement_priority: string;
            maturity_score?: number;
            missing_capabilities?: string[];
            required_capabilities?: string[];
            recommendation?: string;
            status?: string;
            incident_count?: number;
          }[];
        };
        root_cause_monitoring_gaps?: {
          action_items?: {
            action: string;
            incident_count?: number;
            missing_capability?: string;
            missing_capabilities?: string[];
            priority: string;
            root_cause: string;
            severity?: string;
            estimated_impact?: string;
          }[];
          correlations_found?: number;
          monitoring_gaps_by_root_cause?: any;
        };
      };
      reliability_postmortem_correlation?: {
        current_portfolio?: {
          action_items_count?: number;
          action_items_summary?: {
            done?: number;
            in_progress?: number;
            total?: number;
          };
          name?: string;
          postmortem_count?: number;
          postmortems?: any[];
        };
        data_source?: string;
        other_portfolios?: {
          action_items_count?: number;
          cross_portfolio_impact?: any;
          portfolios_involved?: string[];
          postmortems?: any[];
          total_count?: number;
        };
        title?: string;
        total_incidents_analyzed?: number;
        total_postmortems_found?: number;
      };
      root_cause_analysis?: {
        change_related_count?: number;
        change_related_percentage?: number;
        completed_count?: number;
        completion_rate?: number;
        completion_status_breakdown?: any;
        enhanced_monitoring_correlation?: {
          alert_effectiveness_correlation?: any;
          correlation_insights_count?: number;
          monitoring_maturity_gaps?: any[];
          reliability_patterns?: any;
        };
        incident_close_codes?: any;
        reliability_correlation?: {
          applications_with_postmortems?: string[];
          monitoring_maturity_vs_incidents?: {
            application: string;
            correlation_insight: string;
            monitoring_maturity_score: number;
            postmortem_count: number;
          }[];
          root_cause_to_monitoring_gaps?: any;
        };
        root_cause_patterns?: any;
        top_root_causes?: string[];
        total_postmortems?: number;
        vendor_related_count?: number;
        vendor_related_percentage?: number;
      };
    };
    prediction?: {
      confidence_score?: number;
      impact_scope?: string;
      predicted_outcome?: string;
      technical_indicators?: string;
      time_to_impact?: string;
    };
    recommendation?: {
      action_priority?: string;
      expected_resolution_time?: string;
      recommended_action?: string;
      success_rate?: number;
    };
    reliability_framework_insights?: {
      application_monitoring_correlation?: any[];
      monitoring_coverage_summary?: {
        monitoring_data_available?: boolean;
        total_applications_analyzed?: number;
      };
    };
    reliability_improvement?: {
      business_justification?: string;
      impact_estimate?: string;
      monitoring_correlation?: string;
      priority_level?: string;
      recommendation?: string;
      technical_specifications?: string;
    };
    root_cause?: {
      confidence_score?: number;
      predicted_root_cause?: string;
      related_components?: string[];
    };
    technical_roadmap?: {
      overview?: string;
      key_initiatives?: {
        initiative: string;
        timeline: string;
        expected_impact: string;
        dependencies?: string[];
      }[];
      immediate_actions?: string[];
      long_term_optimization?: string[];
      resource_requirements?: string;
      short_term_improvements?: string[];
      success_metrics?: string[];
    };
  };
  message?: string;
  timestamp?: string;
}

const IncidentAnalysis: React.FC = () => {
  // Use global filters from context
  const {
    selectedPortfolio: globalPortfolio,
    selectedPlatform: globalPlatform,
    selectedTimeRange,
    customDateRange,
    customDateApplied,
    setSelectedPortfolio: setGlobalPortfolio,
    setSelectedPlatform: setGlobalPlatform,
    setSelectedTimeRange,
    setCustomDateRange,
    setCustomDateApplied,
  } = useGlobalFilters();

  // Use global filters directly (they default to 'all')
  const selectedPlatformPortfolio = globalPortfolio;
  const selectedPlatform = globalPlatform;
  const selectedPortfolio = selectedPlatformPortfolio; // Alias for legacy code
  
  // Local wrapper functions to update global state
  const setSelectedPlatformPortfolio = setGlobalPortfolio;
  const setSelectedPlatform = setGlobalPlatform;

  // Local multi-select state for platform filter
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(
    globalPlatform && globalPlatform !== 'all' ? [globalPlatform] : []
  );

  const [portfolios, setPortfolios] = useState<string[]>([]);
  const [portfolioOptions, setPortfolioOptions] = useState<string[]>([]);
  const [platformOptions, setPlatformOptions] = useState<string[]>([]);
  const [incidents, setIncidents] = useState<IncidentData[]>([]);
  const [allIncidentsUnfiltered, setAllIncidentsUnfiltered] = useState<IncidentData[]>([]); // Store all incidents for dropdown filtering
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCI, setSelectedCI] = useState<string | null>(null);
  const [ciInsightsOpen, setCiInsightsOpen] = useState(false);
  const [selectedClosureCodes, setSelectedClosureCodes] = useState<string[]>([]);
  
  const [selectedApplication, setSelectedApplication] = useState<string | string[]>('all');
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  
  // Pagination state for Recent Incidents
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Search state for Recent Incidents table
  const [searchQuery, setSearchQuery] = useState('');
  
  // Priority filter state for Recent Incidents - now supports multi-selection
  const [selectedPriorityFilter, setSelectedPriorityFilter] = useState<string[]>([]);
  
  // Sorting state for Recent Incidents
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Sorting state for Portfolio-wise Alert Insights table
  const [alertInsightsSortField, setAlertInsightsSortField] = useState<string | null>(null);
  const [alertInsightsSortDirection, setAlertInsightsSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // AI Insights state
  const [aiInsightsOpen, setAiInsightsOpen] = useState(false);
  const [aiInsightsData, setAiInsightsData] = useState<IncidentAIInsightsResponse | null>(null);
  const [aiInsightsLoading, setAiInsightsLoading] = useState(false);
  const [aiInsightsError, setAiInsightsError] = useState<string | null>(null);

  // Sorting state for CI Noise Makers Table
  const [noiseMakersSortField, setNoiseMakersSortField] = useState<string | null>('alertCount');
  const [noiseMakersSortDirection, setNoiseMakersSortDirection] = useState<'asc' | 'desc'>('desc');

  // Tab state for organizing sections
  const [activeTab, setActiveTab] = useState(0);
  
  // Noise Makers tab state (P1/P2 or P3/P4)
  const [noiseMakerPriorityTab, setNoiseMakerPriorityTab] = useState<'P3P4' | 'P1P2'>('P1P2');

  // Snackbar state for notifications
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');

  // Trends & Analysis view toggle state
  const [trendViewMode, setTrendViewMode] = useState<'weekly' | 'monthly' | 'quarterly' | 'yearly'>('weekly');
  const [selectedTrendPriorities, setSelectedTrendPriorities] = useState<string[]>(['P1/P2', 'P3/P4', 'Noise']);
  const [trendTableView, setTrendTableView] = useState<'chart' | 'hierarchy'>('chart'); // Toggle between chart and hierarchy view
  const [expandedTrendNodes, setExpandedTrendNodes] = useState<Set<string>>(new Set());
  const [incidentDetailsOpen, setIncidentDetailsOpen] = useState(false);
  const [selectedPeriodIncidents, setSelectedPeriodIncidents] = useState<IncidentData[]>([]);
  
  // P1/P2 and P3/P4 view mode state (Impacted vs Caused By)
  const [p1p2ViewMode, setP1P2ViewMode] = useState<'impacted' | 'caused'>('caused');
  const [p3p4ViewMode, setP3P4ViewMode] = useState<'impacted' | 'caused'>('impacted');

  // Trend data state for flash cards
  const [p1p2TrendData, setP1p2TrendData] = useState<any>(null);
  const [p3p4TrendData, setP3p4TrendData] = useState<any>(null);

  // MTTR state
  const [mttrData, setMttrData] = useState<Map<string, number>>(new Map());
  const [mttdData, setMttdData] = useState<Map<string, number>>(new Map());
  const [mttrLoading, setMttrLoading] = useState(false);

  // Dialog state for clickable flash cards
  const [incidentDialogOpen, setIncidentDialogOpen] = useState(false);
  const [dialogIncidents, setDialogIncidents] = useState<IncidentData[]>([]);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogPriority, setDialogPriority] = useState<'p1p2' | 'p3p4'>('p1p2');
  const [dialogPage, setDialogPage] = useState(0);
  const [dialogRowsPerPage, setDialogRowsPerPage] = useState(10);

  // Transform API incident data to component format
  // Updated: 2026-03-05
  const transformIncidentData = (apiData: any[]): IncidentData[] => {
    return apiData.map(incident => {
      // Calculate outage duration if not available from API
      let outageDuration = incident.outage_duration;
      
      if (!outageDuration && incident.resolved_at && incident.sys_created_on_value) {
        const resolvedTime = new Date(incident.resolved_at);
        const createdTime = new Date(incident.sys_created_on_value);
        const diffMs = resolvedTime.getTime() - createdTime.getTime();
        outageDuration = Math.round(diffMs / (1000 * 60)); // Convert to minutes
      }

      return {
        id: incident.number,
        title: incident.short_description,
        priority: `P${incident.priority}` as 'P1' | 'P2' | 'P3' | 'P4',
        portfolio: incident.portfolio_name,
        application: incident.cmdb_ci_display_value,
        createdDate: incident.sys_created_on_value,
        resolvedDate: incident.resolved_at,
        status: incident.state_display_value || 'Unknown',
        assignedTo: incident.assignment_group_display_value,
        rootCause: incident.caused_by_display_value || 'Under investigation',
        impact: incident.impact === '1' ? 'High' : incident.impact === '2' ? 'Medium' : 'Low',
        number: incident.number,
        platform: incident.platform_name,
        outage_duration: outageDuration,
        outage_number: incident.outage_number,
        contactType: incident.contact_type_display_value,
        capturedByAlert: incident.contact_type_display_value === 'Alert', // Check if contact type is "Alert"
        causedByChange: incident.caused_by_display_value && incident.caused_by_display_value.includes('CHG'),
        changeNumber: incident.caused_by_display_value && incident.caused_by_display_value.includes('CHG') 
          ? incident.caused_by_display_value 
          : undefined,
        closeCode: incident.close_code || 'Unknown', // Capture close_code from ServiceNow
        short_description: incident.short_description, // Add short_description field from API
        caused_by_app: incident.caused_by_app, // Add caused_by_app field from API
        caused_by_platform: incident.caused_by_platform, // Add caused_by_platform field from API
        caused_by_portfolio: incident.caused_by_portfolio // Add caused_by_portfolio field from API
      };
    });
  };

  // Helper function to calculate date range based on selection
  const getDateRangeFromSelection = (timeRange: string) => {
    const endDate = new Date();
    let startDate = new Date();
    
    switch (timeRange) {
      case '7d':
        startDate.setDate(endDate.getDate() - 7);
        startDate.setHours(0, 0, 0, 0);
        break;
      case '30d':
        startDate.setDate(endDate.getDate() - 30);
        startDate.setHours(0, 0, 0, 0);
        break;
      case '90d':
        startDate.setDate(endDate.getDate() - 90);
        startDate.setHours(0, 0, 0, 0);
        break;
      case '1y':
        // Year to Date: from January 1st of current year to today
        startDate.setFullYear(endDate.getFullYear(), 0, 1); // January 1st of current year
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'custom':
        // Use custom date range if both dates are set
        if (customDateRange.start && customDateRange.end) {
          return {
            start_time: customDateRange.start,
            end_time: customDateRange.end
          };
        }
        // Fall back to 30 days if custom dates are not set
        startDate.setDate(endDate.getDate() - 30);
        startDate.setHours(0, 0, 0, 0);
        break;
      default:
        startDate.setDate(endDate.getDate() - 30);
        startDate.setHours(0, 0, 0, 0);
    }
    
    // Set end date to end of day to include all incidents from today
    endDate.setHours(23, 59, 59, 999);
    
    // Format dates as YYYY-MM-DD for API
    return {
      start_time: startDate.getFullYear() + '-' + 
                  String(startDate.getMonth() + 1).padStart(2, '0') + '-' + 
                  String(startDate.getDate()).padStart(2, '0'),
      end_time: endDate.getFullYear() + '-' + 
                String(endDate.getMonth() + 1).padStart(2, '0') + '-' + 
                String(endDate.getDate()).padStart(2, '0')
    };
  };

  // Function to fetch MTTR data for incidents
  const fetchMTTRData = async (incidentNumbers: string[]) => {
    if (incidentNumbers.length === 0) return;
    
    try {
      setMttrLoading(true);
      console.log('🔄 Fetching MTTR data for all incidents');
      
      const response = await authFetch(INCIDENTS.MTTR, {
        method: 'GET'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch MTTR data: ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('📦 MTTR API Response:', result);
      
      if (result.data && Array.isArray(result.data)) {
        const mttrMap = new Map<string, number>();
        const mttdMap = new Map<string, number>();
        result.data.forEach((item: MTTRData) => {
          const mttrSeconds = parseFloat(item.mttr);
          if (!isNaN(mttrSeconds)) {
            // Store MTTR in seconds
            mttrMap.set(item.number, mttrSeconds);
          }
          const mttdSeconds = parseFloat(item.mttd);
          if (!isNaN(mttdSeconds)) {
            // Store MTTD in seconds
            mttdMap.set(item.number, mttdSeconds);
          }
        });
        setMttrData(mttrMap);
        setMttdData(mttdMap);
        console.log('✅ MTTR data loaded for', mttrMap.size, 'incidents from API');
        console.log('✅ MTTD data loaded for', mttdMap.size, 'incidents from API');
        console.log('📊 Sample MTTR data:', Array.from(mttrMap.entries()).slice(0, 5));
        console.log('📊 Sample MTTD data:', Array.from(mttdMap.entries()).slice(0, 5));
      } else {
        console.warn('⚠️ MTTR API returned no data or invalid format');
      }
    } catch (err) {
      console.error('❌ Error fetching MTTR data:', err);
    } finally {
      setMttrLoading(false);
    }
  };

  // Load data function
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const dateRange = getDateRangeFromSelection(selectedTimeRange);
      console.log('🔄 Loading incidents for date range:', dateRange);
      
      // Fetch P1/P2 trends WITH filters for incident data
      const p1p2TrendPromise = authFetch(INCIDENTS.P1_P2_WITH_TRENDS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start_date: dateRange.start_time,
          end_date: dateRange.end_time,
          page: 1,
          pageSize: 10000
        })
      }).then(res => res.json());

      // Fetch P3/P4 trends WITH filters for incident data
      const p3p4TrendPromise = authFetch(INCIDENTS.P3_P4_WITH_TRENDS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start_date: dateRange.start_time,
          end_date: dateRange.end_time,
          page: 1,
          pageSize: 10000
        })
      }).then(res => res.json());
      
      // Fetch ALL incidents WITHOUT filters to get complete portfolio/platform lists
      const p1p2AllPromise = authFetch(INCIDENTS.P1_P2_WITH_TRENDS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start_date: dateRange.start_time,
          end_date: dateRange.end_time,
          page: 1,
          pageSize: 10000
        })
      }).then(res => res.json());

      const p3p4AllPromise = authFetch(INCIDENTS.P3_P4_WITH_TRENDS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start_date: dateRange.start_time,
          end_date: dateRange.end_time,
          page: 1,
          pageSize: 10000
        })
      }).then(res => res.json());
      
      const [portfolioNames, p1p2Response, p3p4Response, p1p2AllResponse, p3p4AllResponse] = await Promise.all([
        fetchPortfolioNames(),
        p1p2TrendPromise,
        p3p4TrendPromise,
        p1p2AllPromise,
        p3p4AllPromise
      ]);
      
      // Process P1/P2 trend data
      if (p1p2Response.success && p1p2Response.data) {
        setP1p2TrendData(p1p2Response.data);
        console.log('✅ P1/P2 trend data loaded:', p1p2Response.data.trends);
      }

      // Process P3/P4 trend data
      if (p3p4Response.success && p3p4Response.data) {
        setP3p4TrendData(p3p4Response.data);
        console.log('✅ P3/P4 trend data loaded:', p3p4Response.data.trends);
      }

      // Combine all incidents from filtered responses for display
      const allIncidents = [
        ...(p1p2Response.data?.incidents || []),
        ...(p3p4Response.data?.incidents || [])
      ];
      
      // Combine ALL incidents (unfiltered) for extracting dropdown options
      const allIncidentsUnfiltered = [
        ...(p1p2AllResponse.data?.incidents || []),
        ...(p3p4AllResponse.data?.incidents || [])
      ];
      
      const transformedIncidents = transformIncidentData(allIncidents);
      const transformedAllIncidents = transformIncidentData(allIncidentsUnfiltered);
      
      // Extract unique portfolios and platforms from ALL incidents (not filtered)
      const uniquePortfolios = Array.from(new Set(
        transformedAllIncidents
          .map(inc => inc.portfolio || 'Unknown')
          .filter(p => p && p.trim() && p !== 'Unknown')
      )).sort();
      
      const uniquePlatforms = Array.from(new Set(
        transformedAllIncidents
          .map(inc => inc.platform || 'Unknown')
          .filter(p => p && p.trim() && p !== 'Unknown')
      )).sort();
      
      setPortfolios(['all', ...portfolioNames]);
      setPortfolioOptions(uniquePortfolios);
      setPlatformOptions(uniquePlatforms);
      setIncidents(transformedIncidents);
      setAllIncidentsUnfiltered(transformedAllIncidents); // Store all incidents for filtering
      
      console.log('✅ Loaded filtered incidents:', allIncidents.length);
      console.log('✅ Loaded all incidents (for options):', allIncidentsUnfiltered.length);
      console.log('✅ Unique portfolios:', uniquePortfolios.length, uniquePortfolios);
      console.log('✅ Unique platforms:', uniquePlatforms.length, uniquePlatforms);
      
      // Fetch MTTR data for ALL incidents (both filtered and unfiltered)
      const allIncidentNumbers = transformedIncidents.map(inc => inc.number);
      console.log('🎯 Fetching MTTR for all loaded incidents:', allIncidentNumbers.length);
      if (allIncidentNumbers.length > 0) {
        await fetchMTTRData(allIncidentNumbers);
      }
      
      // Validate and reset filters if they don't exist in current data
      if (selectedPlatformPortfolio !== 'all' && !uniquePortfolios.includes(selectedPlatformPortfolio)) {
        console.log('⚠️ Selected portfolio not found in Incident Analysis data, resetting to all');
        setSelectedPlatformPortfolio('all');
      }
      
      const invalidPlatforms = selectedPlatforms.filter(p => !uniquePlatforms.includes(p));
      if (invalidPlatforms.length > 0) {
        console.log('⚠️ Some selected platforms not found in Incident Analysis data, removing them');
        setSelectedPlatforms(prev => prev.filter(p => uniquePlatforms.includes(p)));
      }
    } catch (err) {
      console.error('Error loading incident analysis data:', err);
      setError('Failed to load incident analysis data');
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  // Reload data when time range changes (but not for custom range)
  useEffect(() => {
    if (selectedTimeRange && selectedTimeRange !== 'custom') {
      console.log('⏰ Time range changed to:', selectedTimeRange);
      loadData();
    }
  }, [selectedTimeRange]);

  // Reload data when portfolio filter changes (platform filtering done client-side)
  useEffect(() => {
    if (selectedPlatformPortfolio) {
      console.log('🔄 Portfolio filter changed - reloading data');
      loadData();
    }
  }, [selectedPlatformPortfolio]);

  // Reset pagination when search query or priority filter changes
  useEffect(() => {
    setPage(0);
  }, [searchQuery, selectedPriorityFilter]);

  // Read URL parameters and set filters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    
    const portfolio = urlParams.get('portfolio');
    const platform = urlParams.get('platform');
    const application = urlParams.get('application');
    const timeRange = urlParams.get('timeRange');
    const priority = urlParams.get('priority');
    const startDate = urlParams.get('startDate');
    const endDate = urlParams.get('endDate');
    
    if (portfolio) setSelectedPlatformPortfolio(portfolio);
    if (platform) setSelectedPlatforms([platform]);
    if (application) {
      // Handle both single and multiple applications from URL
      const appList = application.split(',').map(app => app.trim());
      setSelectedApplication(appList.length === 1 ? appList[0] : appList);
    }
    if (timeRange) {
      setSelectedTimeRange(timeRange);
      if (timeRange === 'custom' && startDate && endDate) {
        setCustomDateRange({ start: startDate, end: endDate });
        setShowCustomDatePicker(true);
      }
    }
    if (priority) setSelectedPriorityFilter(priority.split(','));
  }, []);

  // Pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Sort handler for Created At column
  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default descending order
      setSortField(field);
      setSortDirection('desc');
    }
    setPage(0); // Reset pagination when sorting
  };

  // Sort handler for Alert Insights table
  const handleAlertInsightsSort = (field: string) => {
    if (alertInsightsSortField === field) {
      // Toggle direction if same field
      setAlertInsightsSortDirection(alertInsightsSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default descending order
      setAlertInsightsSortField(field);
      setAlertInsightsSortDirection('desc');
    }
  };

  // Sort handler for CI Noise Makers table
  const handleNoiseMakersSort = (field: string) => {
    if (noiseMakersSortField === field) {
      // Toggle direction if same field
      setNoiseMakersSortDirection(noiseMakersSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default descending order
      setNoiseMakersSortField(field);
      setNoiseMakersSortDirection('desc');
    }
  };

  // Tab change handler
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // CSV Export function
  const exportToCSV = () => {
    const csvHeaders = [
      'Incident ID',
      'Description',
      'Affected App',
      'Affected Portfolio',
      'Affected Platform',
      'Caused by App',
      'Caused by Platform',
      'Caused by Portfolio',
      'Created At', 
      'Priority',
      'Status',
      'MTTD(Avg)',
      'MTTR(Avg)',
      'Outage',
      'Alert',
      'Change'
    ];

    const csvData = searchableIncidents.map(incident => {
      // Calculate MTTD duration
      let mttdDuration = 'N/A';
      if ((incident.priority === 'P1' || incident.priority === 'P2') && incident.mttd) {
        const hours = Math.floor(incident.mttd / 3600);
        const minutes = Math.round((incident.mttd % 3600) / 60);
        mttdDuration = `${hours}h ${minutes}m`;
      }

      // Calculate MTTR duration
      let mttrDuration = 'N/A';
      if (incident.mttr) {
        const hours = Math.floor(incident.mttr / 3600);
        const minutes = Math.round((incident.mttr % 3600) / 60);
        mttrDuration = `${hours}h ${minutes}m`;
      }

      // Calculate outage duration (only for P1/P2)
      let outageDuration = 'N/A';
      if ((incident.priority === 'P1' || incident.priority === 'P2') && incident.outage_duration) {
        const hours = Math.floor(incident.outage_duration / 60);
        const minutes = incident.outage_duration % 60;
        outageDuration = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
      }

      return [
        incident.number,
        incident.short_description || incident.title || 'N/A',
        incident.application || 'N/A',
        incident.portfolio || 'N/A',
        incident.platform || 'N/A',
        incident.caused_by_app || 'N/A',
        incident.caused_by_platform || 'N/A',
        incident.caused_by_portfolio || 'N/A',
        new Date(incident.createdDate).toISOString().replace('T', ' ').slice(0, -5) + ' UTC',
        incident.priority,
        incident.status,
        mttdDuration,
        mttrDuration,
        outageDuration,
        incident.capturedByAlert ? 'Yes' : 'No',
        incident.causedByChange ? 'Yes' : 'No'
      ];
    });

    const csvContent = [
      csvHeaders.join(','),
      ...csvData.map(row => 
        row.map(field => 
          typeof field === 'string' && field.includes(',') 
            ? `"${field.replace(/"/g, '""')}"` 
            : field
        ).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `incidents_${selectedTimeRange}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const generateShareLink = () => {
    const baseUrl = `${process.env.REACT_APP_REDIRECT_URL}/incident-analysis`;
    const params = new URLSearchParams();
    
    // Add current filter selections to URL parameters
    if (selectedPlatformPortfolio !== 'all') {
      params.append('portfolio', selectedPlatformPortfolio);
    }
    if (selectedPlatforms.length > 0) {
      params.append('platform', selectedPlatforms[0]);
    }
    if (selectedApplication !== 'all') {
      // Handle both single application (string) and multiple applications (array)
      if (Array.isArray(selectedApplication)) {
        params.append('application', selectedApplication.join(','));
      } else {
        params.append('application', selectedApplication);
      }
    }
    if (selectedTimeRange !== '30d') { // Default time range
      params.append('timeRange', selectedTimeRange);
    }
    if (selectedPriorityFilter.length > 0) {
      params.append('priority', selectedPriorityFilter.join(','));
    }
    if (customDateRange.start && customDateRange.end && selectedTimeRange === 'custom') {
      params.append('startDate', customDateRange.start);
      params.append('endDate', customDateRange.end);
    }
    const shareUrl = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
    
    // Copy to clipboard
    navigator.clipboard.writeText(shareUrl).then(() => {
      setSnackbarMessage('Share link copied to clipboard!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    }).catch((err) => {
      console.error('Failed to copy link: ', err);
      setSnackbarMessage('Failed to copy link. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      // Fallback: show the link in a prompt as backup
      setTimeout(() => {
        prompt('Copy this link to share:', shareUrl);
      }, 100);
    });
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'P1': return '#f44336';
      case 'P2': return '#ff9800';
      case 'P3': return '#2196f3';
      case 'P4': return '#4caf50';
      default: return '#757575';
    }
  };

  const getStatusColor = (status: string) => {
    // Normalize status to handle various API values
    const normalizedStatus = status.toLowerCase();
    
    if (normalizedStatus.includes('closed') || normalizedStatus.includes('resolved')) {
      return '#4caf50'; // Green for closed/resolved
    }
    if (normalizedStatus.includes('progress') || normalizedStatus.includes('assigned') || normalizedStatus.includes('work')) {
      return '#ff9800'; // Orange for in progress
    }
    if (normalizedStatus.includes('hold') || normalizedStatus.includes('pending')) {
      return '#9c27b0'; // Purple for on hold
    }
    if (normalizedStatus.includes('open') || normalizedStatus.includes('new')) {
      return '#f44336'; // Red for open/new
    }
    
    // Default color for unknown statuses
    return '#757575'; // Gray
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return <TrendingUp color="error" />;
      case 'down': return <TrendingDown color="success" />;
      default: return <CheckCircle color="info" />;
    }
  };

  // Derived filter options
  const platformPortfolios = React.useMemo(() => {
    const portfolios = Array.from(new Set(
      incidents.map(incident => {
        const portfolio = incident.portfolio;
        if (!portfolio || 
            portfolio.trim() === '' || 
            portfolio.toLowerCase() === 'portfolio not defined' ||
            portfolio.toLowerCase() === 'unknown') {
          return 'Other';
        }
        return portfolio;
      })
    ));
    return ['all', ...portfolios.sort()];
  }, [incidents]);

  const platforms = React.useMemo(() => {
    const filteredByPortfolio = selectedPlatformPortfolio === 'all' 
      ? incidents 
      : incidents.filter(incident => {
          const portfolio = incident.portfolio;
          const normalizedPortfolio = (!portfolio || 
            portfolio.trim() === '' || 
            portfolio.toLowerCase() === 'portfolio not defined' ||
            portfolio.toLowerCase() === 'unknown') ? 'Other' : portfolio;
          return normalizedPortfolio === selectedPlatformPortfolio;
        });
    
    const platformList = Array.from(new Set(filteredByPortfolio.map(incident => incident.platform || 'Unknown')));
    return ['all', ...platformList.sort()];
  }, [incidents, selectedPlatformPortfolio]);

  const applications = React.useMemo(() => {
    let filtered = incidents;
    
    if (selectedPlatformPortfolio !== 'all') {
      filtered = filtered.filter(incident => {
        const portfolio = incident.portfolio;
        const normalizedPortfolio = (!portfolio || 
          portfolio.trim() === '' || 
          portfolio.toLowerCase() === 'portfolio not defined' ||
          portfolio.toLowerCase() === 'unknown') ? 'Other' : portfolio;
        return normalizedPortfolio === selectedPlatformPortfolio;
      });
    }
    
    if (selectedPlatforms.length > 0) {
      filtered = filtered.filter(incident => selectedPlatforms.includes(incident.platform || 'Unknown'));
    }
    
    const appList = Array.from(new Set(filtered.map(incident => incident.application || 'Unknown')));
    return ['all', ...appList.sort()];
  }, [incidents, selectedPlatformPortfolio, selectedPlatforms]);

  // Filter incidents based on all selected filters
  const filteredIncidents = React.useMemo(() => {
    let filtered = incidents;

    // Portfolio filter (legacy)
    // Match on either portfolio_name OR caused_by_portfolio so that view-mode
    // filtering downstream (viewModeFilteredIncidents) can apply the correct one.
    if (selectedPortfolio !== 'all') {
      filtered = filtered.filter(incident =>
        incident.portfolio === selectedPortfolio ||
        incident.caused_by_portfolio === selectedPortfolio
      );
    }

    // Platform Portfolio filter
    if (selectedPlatformPortfolio !== 'all') {
      filtered = filtered.filter(incident => {
        const checkPortfolio = (portfolio: string | undefined) => {
          const normalized = (!portfolio ||
            portfolio.trim() === '' ||
            portfolio.toLowerCase() === 'portfolio not defined' ||
            portfolio.toLowerCase() === 'unknown') ? 'Other' : portfolio;
          return normalized === selectedPlatformPortfolio;
        };
        return checkPortfolio(incident.portfolio) || checkPortfolio(incident.caused_by_portfolio);
      });
    }

    // Platform filter - multi-select, client-side
    // Match on either platform_name (affected) OR caused_by_platform (causal)
    if (selectedPlatforms.length > 0) {
      filtered = filtered.filter(incident =>
        selectedPlatforms.includes(incident.platform || 'Unknown') ||
        selectedPlatforms.includes(incident.caused_by_platform || 'Unknown')
      );
    }

    // Application filter - match on either affected app OR caused_by_app
    if (selectedApplication !== 'all') {
      // Handle both single application (string) and multiple applications (array)
      if (Array.isArray(selectedApplication)) {
        filtered = filtered.filter(incident =>
          selectedApplication.includes(incident.application || 'Unknown') ||
          selectedApplication.includes(incident.caused_by_app || 'Unknown')
        );
      } else {
        filtered = filtered.filter(incident =>
          (incident.application || 'Unknown') === selectedApplication ||
          (incident.caused_by_app || 'Unknown') === selectedApplication
        );
      }
    }

    // Enrich with MTTR and MTTD data
    const enriched = filtered.map(incident => ({
      ...incident,
      mttr: mttrData.get(incident.number),
      mttd: mttdData.get(incident.number)
    }));
    
    // Log MTTR and MTTD enrichment for debugging
    const withMTTR = enriched.filter(inc => inc.mttr !== undefined).length;
    const withMTTD = enriched.filter(inc => inc.mttd !== undefined).length;
    console.log(`🔢 Filtered incidents: ${enriched.length}, with MTTR: ${withMTTR}, with MTTD: ${withMTTD}`);
    
    return enriched;
  }, [incidents, selectedPortfolio, selectedPlatformPortfolio, selectedPlatforms, selectedApplication, mttrData, mttdData]);

  // Apply view-mode based filtering for use in tabs (Insights, Trends, etc.)
  // Note: View mode filtering only applies to P1/P2 incidents. P3/P4 always uses impacted view.
  const viewModeFilteredIncidents = React.useMemo(() => {
    return filteredIncidents.filter(incident => {
      // Determine which view mode to use based on priority
      const isP1P2 = incident.priority === 'P1' || incident.priority === 'P2';
      // P3/P4 always uses 'impacted' view mode
      const viewMode = isP1P2 ? p1p2ViewMode : 'impacted';
      
      // Apply view-mode based portfolio filtering
      if (selectedPortfolio !== 'all') {
        const portfolioMatch = viewMode === 'impacted' 
          ? incident.portfolio === selectedPortfolio
          : incident.caused_by_portfolio === selectedPortfolio;
        if (!portfolioMatch) return false;
      }
      
      // Apply view-mode based platform filtering
      if (selectedPlatforms.length > 0) {
        const platformMatch = viewMode === 'impacted'
          ? selectedPlatforms.includes(incident.platform || '')
          : selectedPlatforms.includes(incident.caused_by_platform || '');
        if (!platformMatch) return false;
      }
      
      // Apply view-mode based application filtering
      if (selectedApplication !== 'all') {
        const appMatch = viewMode === 'impacted'
          ? (Array.isArray(selectedApplication) ? selectedApplication.includes(incident.application || '') : incident.application === selectedApplication)
          : (Array.isArray(selectedApplication) ? selectedApplication.includes(incident.caused_by_app || '') : incident.caused_by_app === selectedApplication);
        if (!appMatch) return false;
      }
      
      return true;
    });
  }, [filteredIncidents, p1p2ViewMode, selectedPortfolio, selectedPlatforms, selectedApplication]);

  // Apply search filter, priority filter, view mode filter, and sorting on top of existing filters
  const searchableIncidents = React.useMemo(() => {
    let result = filteredIncidents;

    // Apply view-mode based filtering for portfolio/platform/application
    // This ensures the detailed incidents tab respects the view mode from flash cards
    result = result.filter(incident => {
      // Determine which view mode to use based on priority
      const isP1P2 = incident.priority === 'P1' || incident.priority === 'P2';
      const viewMode = isP1P2 ? p1p2ViewMode : p3p4ViewMode;
      
      // Apply view-mode based portfolio filtering
      if (selectedPortfolio !== 'all') {
        const portfolioMatch = viewMode === 'impacted' 
          ? incident.portfolio === selectedPortfolio
          : incident.caused_by_portfolio === selectedPortfolio;
        if (!portfolioMatch) return false;
      }
      
      // Apply view-mode based platform filtering
      if (selectedPlatforms.length > 0) {
        const platformMatch = viewMode === 'impacted'
          ? selectedPlatforms.includes(incident.platform || '')
          : selectedPlatforms.includes(incident.caused_by_platform || '');
        if (!platformMatch) return false;
      }
      
      // Apply view-mode based application filtering
      if (selectedApplication !== 'all') {
        const appMatch = viewMode === 'impacted'
          ? (Array.isArray(selectedApplication) ? selectedApplication.includes(incident.application || '') : incident.application === selectedApplication)
          : (Array.isArray(selectedApplication) ? selectedApplication.includes(incident.caused_by_app || '') : incident.caused_by_app === selectedApplication);
        if (!appMatch) return false;
      }
      
      return true;
    });

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(incident => {
        // Search across all relevant fields
        const searchableFields = [
          incident.number,
          incident.title,
          incident.priority,
          incident.portfolio,
          incident.application,
          incident.platform,
          incident.status,
          incident.assignedTo,
          incident.rootCause,
          incident.impact,
          new Date(incident.createdDate).toLocaleDateString(),
          incident.resolvedDate ? new Date(incident.resolvedDate).toLocaleDateString() : '',
          incident.capturedByAlert ? 'Yes' : 'No', // Add captured by alert as searchable text
          incident.causedByChange ? 'Yes' : 'No', // Add caused by change as searchable text
          incident.changeNumber || '' // Add change number for searching
        ];

        return searchableFields.some(field => 
          field && field.toString().toLowerCase().includes(query)
        );
      });
    }

    // Apply priority filter
    if (selectedPriorityFilter.length > 0) {
      result = result.filter(incident => selectedPriorityFilter.includes(incident.priority));
    }

    // Apply sorting
    if (sortField) {
      result = [...result].sort((a, b) => {
        let aValue = '';
        let bValue = '';

        switch (sortField) {
          case 'createdAt':
            aValue = a.createdDate;
            bValue = b.createdDate;
            break;
          default:
            return 0;
        }

        // For date comparison
        if (sortField === 'createdAt') {
          const dateA = new Date(aValue).getTime();
          const dateB = new Date(bValue).getTime();
          return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
        }

        // For string comparison
        const comparison = aValue.localeCompare(bValue);
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  }, [filteredIncidents, searchQuery, selectedPriorityFilter, sortField, sortDirection, p1p2ViewMode, p3p4ViewMode, selectedPortfolio, selectedPlatforms, selectedApplication]);

  // Calculate priority distribution from real data
  const priorityDistribution = React.useMemo(() => {
    const p1Count = filteredIncidents.filter(inc => inc.priority === 'P1').length;
    const p2Count = filteredIncidents.filter(inc => inc.priority === 'P2').length;
    const p3Count = filteredIncidents.filter(inc => inc.priority === 'P3').length;
    const p4Count = filteredIncidents.filter(inc => inc.priority === 'P4').length;
    
    return [
      { name: 'P1', value: p1Count, color: '#f44336' },
      { name: 'P2', value: p2Count, color: '#ff9800' },
      { name: 'P3', value: p3Count, color: '#2196f3' },
      { name: 'P4', value: p4Count, color: '#4caf50' }
    ];
  }, [filteredIncidents]);

  // Calculate portfolio insights from real data
  const portfolioInsights = React.useMemo(() => {
    const portfolioStats: { [key: string]: { 
      p1: number; 
      p2: number; 
      p3: number; 
      p4: number; 
      trend: string; 
      changePercent: number;
      p1p2AlertCapturePercent: number | string;
      p3p4AlertCapturePercent: number | string;
      p1p2ChangeCapturePercent: number | string;
      p3p4ChangeCapturePercent: number | string;
    } } = {};
    
    // Get unique portfolios from actual incident data (not from API portfolio list)
    // Filter out null, undefined, empty strings, and "Portfolio not defined"
    const actualPortfolios = Array.from(new Set(
      viewModeFilteredIncidents
        .map(incident => incident.portfolio)
        .filter(portfolio => 
          portfolio && 
          portfolio.trim() !== '' && 
          portfolio.toLowerCase() !== 'portfolio not defined' &&
          portfolio.toLowerCase() !== 'unknown'
        )
    ));
    console.log('📊 Portfolio Insights - Processing portfolios from incident data:', actualPortfolios);
    
    // Process known portfolios
    actualPortfolios.forEach(portfolio => {
      const portfolioIncidents = viewModeFilteredIncidents.filter(inc => inc.portfolio === portfolio);
      
      // Count incidents by priority
      const p1Incidents = portfolioIncidents.filter(inc => inc.priority === 'P1');
      const p2Incidents = portfolioIncidents.filter(inc => inc.priority === 'P2');
      const p3Incidents = portfolioIncidents.filter(inc => inc.priority === 'P3');
      const p4Incidents = portfolioIncidents.filter(inc => inc.priority === 'P4');
      
      // Calculate alert capture percentages
      const p1p2Incidents = [...p1Incidents, ...p2Incidents];
      const p3p4Incidents = [...p3Incidents, ...p4Incidents];
      
      const p1p2AlertCaptured = p1p2Incidents.filter(inc => inc.contactType === 'Alert').length;
      const p3p4AlertCaptured = p3p4Incidents.filter(inc => inc.contactType === 'Alert').length;
      
      const p1p2AlertCapturePercent = p1p2Incidents.length > 0 ? (p1p2AlertCaptured / p1p2Incidents.length) * 100 : "N/A";
      const p3p4AlertCapturePercent = p3p4Incidents.length > 0 ? (p3p4AlertCaptured / p3p4Incidents.length) * 100 : "N/A";
      
      // Calculate change capture percentages
      const p1p2ChangeCaptured = p1p2Incidents.filter(inc => inc.causedByChange === true).length;
      const p3p4ChangeCaptured = p3p4Incidents.filter(inc => inc.causedByChange === true).length;
      
      const p1p2ChangeCapturePercent = p1p2Incidents.length > 0 ? (p1p2ChangeCaptured / p1p2Incidents.length) * 100 : "N/A";
      const p3p4ChangeCapturePercent = p3p4Incidents.length > 0 ? (p3p4ChangeCaptured / p3p4Incidents.length) * 100 : "N/A";
      
      portfolioStats[portfolio] = {
        p1: p1Incidents.length,
        p2: p2Incidents.length,
        p3: p3Incidents.length,
        p4: p4Incidents.length,
        trend: 'stable', // Default to stable - you can implement trend calculation based on historical data
        changePercent: 0.5, // Default value - implement based on historical comparison
        p1p2AlertCapturePercent: typeof p1p2AlertCapturePercent === 'number' ? Math.round(p1p2AlertCapturePercent * 10) / 10 : p1p2AlertCapturePercent,
        p3p4AlertCapturePercent: typeof p3p4AlertCapturePercent === 'number' ? Math.round(p3p4AlertCapturePercent * 10) / 10 : p3p4AlertCapturePercent,
        p1p2ChangeCapturePercent: typeof p1p2ChangeCapturePercent === 'number' ? Math.round(p1p2ChangeCapturePercent * 10) / 10 : p1p2ChangeCapturePercent,
        p3p4ChangeCapturePercent: typeof p3p4ChangeCapturePercent === 'number' ? Math.round(p3p4ChangeCapturePercent * 10) / 10 : p3p4ChangeCapturePercent
      };
    });
    
    // Process unknown/null/undefined portfolio incidents as "Other"
    // This includes null, undefined, empty strings, "Portfolio not defined", and "Unknown"
    const unknownIncidents = viewModeFilteredIncidents.filter(inc => 
      !inc.portfolio || 
      inc.portfolio.trim() === '' || 
      inc.portfolio.toLowerCase() === 'portfolio not defined' ||
      inc.portfolio.toLowerCase() === 'unknown'
    );
    if (unknownIncidents.length > 0) {
      const p1Incidents = unknownIncidents.filter(inc => inc.priority === 'P1');
      const p2Incidents = unknownIncidents.filter(inc => inc.priority === 'P2');
      const p3Incidents = unknownIncidents.filter(inc => inc.priority === 'P3');
      const p4Incidents = unknownIncidents.filter(inc => inc.priority === 'P4');
      
      // Calculate alert capture percentages
      const p1p2Incidents = [...p1Incidents, ...p2Incidents];
      const p3p4Incidents = [...p3Incidents, ...p4Incidents];
      
      const p1p2AlertCaptured = p1p2Incidents.filter(inc => inc.contactType === 'Alert').length;
      const p3p4AlertCaptured = p3p4Incidents.filter(inc => inc.contactType === 'Alert').length;
      
      const p1p2AlertCapturePercent = p1p2Incidents.length > 0 ? (p1p2AlertCaptured / p1p2Incidents.length) * 100 : "N/A";
      const p3p4AlertCapturePercent = p3p4Incidents.length > 0 ? (p3p4AlertCaptured / p3p4Incidents.length) * 100 : "N/A";
      
      // Calculate change capture percentages
      const p1p2ChangeCaptured = p1p2Incidents.filter(inc => inc.causedByChange === true).length;
      const p3p4ChangeCaptured = p3p4Incidents.filter(inc => inc.causedByChange === true).length;
      
      const p1p2ChangeCapturePercent = p1p2Incidents.length > 0 ? (p1p2ChangeCaptured / p1p2Incidents.length) * 100 : "N/A";
      const p3p4ChangeCapturePercent = p3p4Incidents.length > 0 ? (p3p4ChangeCaptured / p3p4Incidents.length) * 100 : "N/A";
      
      portfolioStats['Other'] = {
        p1: p1Incidents.length,
        p2: p2Incidents.length,
        p3: p3Incidents.length,
        p4: p4Incidents.length,
        trend: 'stable',
        changePercent: 0.5,
        p1p2AlertCapturePercent: typeof p1p2AlertCapturePercent === 'number' ? Math.round(p1p2AlertCapturePercent * 10) / 10 : p1p2AlertCapturePercent,
        p3p4AlertCapturePercent: typeof p3p4AlertCapturePercent === 'number' ? Math.round(p3p4AlertCapturePercent * 10) / 10 : p3p4AlertCapturePercent,
        p1p2ChangeCapturePercent: typeof p1p2ChangeCapturePercent === 'number' ? Math.round(p1p2ChangeCapturePercent * 10) / 10 : p1p2ChangeCapturePercent,
        p3p4ChangeCapturePercent: typeof p3p4ChangeCapturePercent === 'number' ? Math.round(p3p4ChangeCapturePercent * 10) / 10 : p3p4ChangeCapturePercent
      };
    }
    
    return Object.entries(portfolioStats).map(([portfolio, stats]) => ({
      portfolio,
      ...stats
    }));
  }, [viewModeFilteredIncidents, p1p2ViewMode]); // Changed to use viewModeFilteredIncidents, P3/P4 always uses impacted view

  // Calculate platform insights for a specific portfolio
  const platformInsights = React.useMemo(() => {
    if (selectedPlatformPortfolio === 'all') return [];
    
    const portfolioIncidents = viewModeFilteredIncidents.filter(inc => inc.portfolio === selectedPlatformPortfolio);
    const platformStats: { [key: string]: { 
      p1: number; 
      p2: number; 
      p3: number; 
      p4: number; 
      trend: string; 
      changePercent: number;
      p1p2AlertCapturePercent: number | string;
      p3p4AlertCapturePercent: number | string;
      p1p2ChangeCapturePercent: number | string;
      p3p4ChangeCapturePercent: number | string;
    } } = {};
    
    // Get unique platforms for the selected portfolio
    const uniquePlatforms = Array.from(new Set(portfolioIncidents.map(inc => inc.platform || 'Unknown')));
    
    uniquePlatforms.forEach(platform => {
      const platformIncidents = portfolioIncidents.filter(inc => (inc.platform || 'Unknown') === platform);
      
      // Count incidents by priority
      const p1Incidents = platformIncidents.filter(inc => inc.priority === 'P1');
      const p2Incidents = platformIncidents.filter(inc => inc.priority === 'P2');
      const p3Incidents = platformIncidents.filter(inc => inc.priority === 'P3');
      const p4Incidents = platformIncidents.filter(inc => inc.priority === 'P4');
      
      // Calculate alert capture percentages
      const p1p2Incidents = [...p1Incidents, ...p2Incidents];
      const p3p4Incidents = [...p3Incidents, ...p4Incidents];
      
      const p1p2AlertCaptured = p1p2Incidents.filter(inc => inc.contactType === 'Alert').length;
      const p3p4AlertCaptured = p3p4Incidents.filter(inc => inc.contactType === 'Alert').length;
      
      const p1p2AlertCapturePercent = p1p2Incidents.length > 0 ? (p1p2AlertCaptured / p1p2Incidents.length) * 100 : "N/A";
      const p3p4AlertCapturePercent = p3p4Incidents.length > 0 ? (p3p4AlertCaptured / p3p4Incidents.length) * 100 : "N/A";
      
      // Calculate change capture percentages
      const p1p2ChangeCaptured = p1p2Incidents.filter(inc => inc.causedByChange === true).length;
      const p3p4ChangeCaptured = p3p4Incidents.filter(inc => inc.causedByChange === true).length;
      
      const p1p2ChangeCapturePercent = p1p2Incidents.length > 0 ? (p1p2ChangeCaptured / p1p2Incidents.length) * 100 : "N/A";
      const p3p4ChangeCapturePercent = p3p4Incidents.length > 0 ? (p3p4ChangeCaptured / p3p4Incidents.length) * 100 : "N/A";
      
      platformStats[platform] = {
        p1: p1Incidents.length,
        p2: p2Incidents.length,
        p3: p3Incidents.length,
        p4: p4Incidents.length,
        trend: 'stable',
        changePercent: 0.5,
        p1p2AlertCapturePercent: typeof p1p2AlertCapturePercent === 'number' ? Math.round(p1p2AlertCapturePercent * 10) / 10 : p1p2AlertCapturePercent,
        p3p4AlertCapturePercent: typeof p3p4AlertCapturePercent === 'number' ? Math.round(p3p4AlertCapturePercent * 10) / 10 : p3p4AlertCapturePercent,
        p1p2ChangeCapturePercent: typeof p1p2ChangeCapturePercent === 'number' ? Math.round(p1p2ChangeCapturePercent * 10) / 10 : p1p2ChangeCapturePercent,
        p3p4ChangeCapturePercent: typeof p3p4ChangeCapturePercent === 'number' ? Math.round(p3p4ChangeCapturePercent * 10) / 10 : p3p4ChangeCapturePercent
      };
    });
    
    return Object.entries(platformStats).map(([platform, stats]) => ({
      portfolio: platform, // Using portfolio field to maintain compatibility with table structure
      ...stats
    }));
  }, [selectedPlatformPortfolio, viewModeFilteredIncidents, p1p2ViewMode]);

  // Filter platforms based on selected portfolio (moved here so dynamicInsights can use it)
  const filteredPlatformOptions = React.useMemo(() => {
    if (selectedPlatformPortfolio === 'all') {
      return platformOptions;
    }
    const platformsForPortfolio = Array.from(new Set(
      allIncidentsUnfiltered
        .filter(inc => inc.portfolio === selectedPlatformPortfolio)
        .map(inc => inc.platform)
        .filter(p => p && p.trim() && p !== 'Unknown')
    )).sort();
    return platformsForPortfolio;
  }, [selectedPlatformPortfolio, platformOptions, allIncidentsUnfiltered]);

  // Auto-select all platforms when a specific portfolio is chosen.
  // When "All Portfolios" is selected, clear platform selection so portfolio-wise view is used
  // (no platform filter applied, ensuring counts match Portfolio Health Tracker).
  React.useEffect(() => {
    if (selectedPlatformPortfolio === 'all') {
      setSelectedPlatforms([]);
    } else if (filteredPlatformOptions.length > 0) {
      setSelectedPlatforms([...filteredPlatformOptions]);
    }
  }, [filteredPlatformOptions, selectedPlatformPortfolio]);

  // Dynamic insights based on selected portfolio/platform/application
  const dynamicInsights = React.useMemo(() => {
    let result: any[] = [];
    
    if (selectedApplication !== 'all') {
      // Handle both single application (string) and multiple applications (array)
      const selectedApps = Array.isArray(selectedApplication) ? selectedApplication : [selectedApplication];
      
      selectedApps.forEach(app => {
        // Show application details for each selected application
        const appIncidents = viewModeFilteredIncidents.filter(inc => 
          (inc.application || 'Unknown') === app
        );

        const p1p2Count = appIncidents.filter(inc => inc.priority === 'P1' || inc.priority === 'P2').length;
        const p3p4Count = appIncidents.filter(inc => inc.priority === 'P3' || inc.priority === 'P4').length;
        const totalCount = appIncidents.length;

        const p1p2Incidents = appIncidents.filter(inc => inc.priority === 'P1' || inc.priority === 'P2');
        const p3p4Incidents = appIncidents.filter(inc => inc.priority === 'P3' || inc.priority === 'P4');
        
        const p1p2ChangeCaptured = p1p2Incidents.filter(inc => inc.causedByChange === true).length;
        const p3p4ChangeCaptured = p3p4Incidents.filter(inc => inc.causedByChange === true).length;
        
        // Calculate alert capture percentages
        const p1p2AlertCaptured = p1p2Incidents.filter(inc => inc.capturedByAlert === true).length;
        const p3p4AlertCaptured = p3p4Incidents.filter(inc => inc.capturedByAlert === true).length;
        
        const p1p2ChangePercentage = p1p2Count > 0 ? Math.round((p1p2ChangeCaptured / p1p2Count) * 100) : "N/A";
        const p3p4ChangePercentage = p3p4Count > 0 ? Math.round((p3p4ChangeCaptured / p3p4Count) * 100) : "N/A";
        
        const p1p2AlertCapturePercent = p1p2Count > 0 ? Math.round((p1p2AlertCaptured / p1p2Count) * 100) : 0;
        const p3p4AlertCapturePercent = p3p4Count > 0 ? Math.round((p3p4AlertCaptured / p3p4Count) * 100) : 0;

        result.push({
          name: app,
          p1p2Count,
          p3p4Count,
          totalCount,
          p1p2Percentage: totalCount > 0 ? Math.round((p1p2Count / totalCount) * 100) : 0,
          p3p4Percentage: totalCount > 0 ? Math.round((p3p4Count / totalCount) * 100) : 0,
          p1p2ChangePercentage,
          p3p4ChangePercentage,
          p1p2AlertCapturePercent,
          p3p4AlertCapturePercent,
          trend: '0%'
        });
      });
    } else if (selectedPlatforms.length > 0) {
      const allPlatformsSelected = selectedPlatforms.length === filteredPlatformOptions.length && filteredPlatformOptions.length > 0;

      if (allPlatformsSelected) {
        // ALL platforms selected → platform-level breakdown
        const platformGroups = viewModeFilteredIncidents.reduce((acc, incident) => {
          const platform = incident.platform || 'Unknown';
          if (!acc[platform]) acc[platform] = [];
          acc[platform].push(incident);
          return acc;
        }, {} as Record<string, IncidentData[]>);

        result = Object.entries(platformGroups).map(([platform, incidents]) => {
          const p1p2Count = incidents.filter(inc => inc.priority === 'P1' || inc.priority === 'P2').length;
          const p3p4Count = incidents.filter(inc => inc.priority === 'P3' || inc.priority === 'P4').length;
          const totalCount = incidents.length;
          const p1p2Incidents = incidents.filter(inc => inc.priority === 'P1' || inc.priority === 'P2');
          const p3p4Incidents = incidents.filter(inc => inc.priority === 'P3' || inc.priority === 'P4');
          const p1p2AlertCaptured = p1p2Incidents.filter(inc => inc.capturedByAlert === true).length;
          const p3p4AlertCaptured = p3p4Incidents.filter(inc => inc.capturedByAlert === true).length;
          const p1p2ChangeCaptured = p1p2Incidents.filter(inc => inc.causedByChange === true).length;
          const p3p4ChangeCaptured = p3p4Incidents.filter(inc => inc.causedByChange === true).length;
          return {
            name: platform,
            p1p2Count,
            p3p4Count,
            totalCount,
            p1p2Percentage: totalCount > 0 ? Math.round((p1p2Count / totalCount) * 100) : 0,
            p3p4Percentage: totalCount > 0 ? Math.round((p3p4Count / totalCount) * 100) : 0,
            p1p2ChangePercentage: p1p2Count > 0 ? Math.round((p1p2ChangeCaptured / p1p2Count) * 100) : 'N/A',
            p3p4ChangePercentage: p3p4Count > 0 ? Math.round((p3p4ChangeCaptured / p3p4Count) * 100) : 'N/A',
            p1p2AlertCapturePercent: p1p2Count > 0 ? Math.round((p1p2AlertCaptured / p1p2Count) * 100) : 0,
            p3p4AlertCapturePercent: p3p4Count > 0 ? Math.round((p3p4AlertCaptured / p3p4Count) * 100) : 0,
            trend: '0%'
          };
        });
      } else {
        // Some (not all) platforms selected → application-level breakdown
        const applicationGroups = viewModeFilteredIncidents.reduce((acc, incident) => {
          const application = incident.application || 'Unknown';
          if (!acc[application]) acc[application] = [];
          acc[application].push(incident);
          return acc;
        }, {} as Record<string, IncidentData[]>);

        result = Object.entries(applicationGroups).map(([application, incidents]) => {
          const p1p2Count = incidents.filter(inc => inc.priority === 'P1' || inc.priority === 'P2').length;
          const p3p4Count = incidents.filter(inc => inc.priority === 'P3' || inc.priority === 'P4').length;
          const totalCount = incidents.length;
          const p1p2Incidents = incidents.filter(inc => inc.priority === 'P1' || inc.priority === 'P2');
          const p3p4Incidents = incidents.filter(inc => inc.priority === 'P3' || inc.priority === 'P4');
          const p1p2AlertCaptured = p1p2Incidents.filter(inc => inc.capturedByAlert === true).length;
          const p3p4AlertCaptured = p3p4Incidents.filter(inc => inc.capturedByAlert === true).length;
          const p1p2ChangeCaptured = p1p2Incidents.filter(inc => inc.causedByChange === true).length;
          const p3p4ChangeCaptured = p3p4Incidents.filter(inc => inc.causedByChange === true).length;
          return {
            name: application,
            p1p2Count,
            p3p4Count,
            totalCount,
            p1p2Percentage: totalCount > 0 ? Math.round((p1p2Count / totalCount) * 100) : 0,
            p3p4Percentage: totalCount > 0 ? Math.round((p3p4Count / totalCount) * 100) : 0,
            p1p2ChangePercentage: p1p2Count > 0 ? Math.round((p1p2ChangeCaptured / p1p2Count) * 100) : 'N/A',
            p3p4ChangePercentage: p3p4Count > 0 ? Math.round((p3p4ChangeCaptured / p3p4Count) * 100) : 'N/A',
            p1p2AlertCapturePercent: p1p2Count > 0 ? Math.round((p1p2AlertCaptured / p1p2Count) * 100) : 0,
            p3p4AlertCapturePercent: p3p4Count > 0 ? Math.round((p3p4AlertCaptured / p3p4Count) * 100) : 0,
            trend: '0%'
          };
        });
      }
    } else if (selectedPlatformPortfolio !== 'all') {
      result = platformInsights;
    } else {
      result = portfolioInsights;
    }

    // Apply sorting if a sort field is selected
    if (alertInsightsSortField && result.length > 0) {
      result = [...result].sort((a, b) => {
        let aValue = 0;
        let bValue = 0;

        switch (alertInsightsSortField) {
          case 'p1p2Count':
            aValue = 'p1p2Count' in a ? a.p1p2Count : (a.p1 + a.p2);
            bValue = 'p1p2Count' in b ? b.p1p2Count : (b.p1 + b.p2);
            break;
          case 'p1p2AlertCapture':
            aValue = 'p1p2AlertCapturePercent' in a ? a.p1p2AlertCapturePercent : (a.p1p2Percentage || 0);
            bValue = 'p1p2AlertCapturePercent' in b ? b.p1p2AlertCapturePercent : (b.p1p2Percentage || 0);
            break;
          case 'p3p4Count':
            aValue = 'p3p4Count' in a ? a.p3p4Count : (a.p3 + a.p4);
            bValue = 'p3p4Count' in b ? b.p3p4Count : (b.p3 + b.p4);
            break;
          case 'p3p4AlertCapture':
            aValue = 'p3p4AlertCapturePercent' in a ? a.p3p4AlertCapturePercent : (a.p3p4Percentage || 0);
            bValue = 'p3p4AlertCapturePercent' in b ? b.p3p4AlertCapturePercent : (b.p3p4Percentage || 0);
            break;
          case 'p1p2ChangeCapture':
            aValue = 'p1p2ChangeCapturePercent' in a ? a.p1p2ChangeCapturePercent : (a.p1p2ChangePercentage || 0);
            bValue = 'p1p2ChangeCapturePercent' in b ? b.p1p2ChangeCapturePercent : (b.p1p2ChangePercentage || 0);
            break;
          case 'p3p4ChangeCapture':
            aValue = 'p3p4ChangeCapturePercent' in a ? a.p3p4ChangeCapturePercent : (a.p3p4ChangePercentage || 0);
            bValue = 'p3p4ChangeCapturePercent' in b ? b.p3p4ChangeCapturePercent : (b.p3p4ChangePercentage || 0);
            break;
          case 'totalCount':
            aValue = 'totalCount' in a ? a.totalCount : (a.p1 + a.p2 + a.p3 + a.p4);
            bValue = 'totalCount' in b ? b.totalCount : (b.p1 + b.p2 + b.p3 + b.p4);
            break;
          default:
            return 0;
        }
        
        // Handle "N/A" values in sorting - treat them as -1 (lowest)
        const processValue = (val: any) => val === "N/A" ? -1 : Number(val) || 0;
        const processedAValue = processValue(aValue);
        const processedBValue = processValue(bValue);

        return alertInsightsSortDirection === 'asc' ? processedAValue - processedBValue : processedBValue - processedAValue;
      });
    }

    return result;
  }, [selectedPlatformPortfolio, selectedPlatforms, selectedApplication, platformInsights, portfolioInsights, viewModeFilteredIncidents, alertInsightsSortField, alertInsightsSortDirection, p1p2ViewMode, p3p4ViewMode, filteredPlatformOptions]);

  // Calculate top noise maker from P3/P4 incidents based on cmdb_ci_display_value
  const topNoiseMaker = React.useMemo(() => {
    // Filter to only P3/P4 incidents in the selected portfolio
    const p3p4Incidents = filteredIncidents.filter(inc => inc.priority === 'P3' || inc.priority === 'P4');
    
    if (p3p4Incidents.length === 0) {
      return { ciName: 'No Data', count: 0 };
    }

    // Count occurrences of each CI
    const ciCounts: { [key: string]: number } = {};
    p3p4Incidents.forEach(incident => {
      const ci = incident.application || 'Unknown CI';
      ciCounts[ci] = (ciCounts[ci] || 0) + 1;
    });

    // Find the CI with maximum occurrences
    let topCI = '';
    let maxCount = 0;
    Object.entries(ciCounts).forEach(([ci, count]) => {
      if (count > maxCount) {
        maxCount = count;
        topCI = ci;
      }
    });

    return { ciName: topCI, count: maxCount };
  }, [filteredIncidents]);

  // Calculate top 10 CI noise makers from P3/P4 incidents
  const top10P3P4NoiseMakers = React.useMemo(() => {
    // Filter to only P3/P4 incidents - P3/P4 always uses impacted view
    const p3p4Incidents = filteredIncidents.filter(inc => {
      const priorityMatch = inc.priority === 'P3' || inc.priority === 'P4';
      if (!priorityMatch) return false;
      
      // P3/P4 always uses impacted view (no caused_by filtering)
      if (selectedPortfolio !== 'all') {
        if (inc.portfolio !== selectedPortfolio) return false;
      }
      if (selectedPlatforms.length > 0) {
        if (!selectedPlatforms.includes(inc.platform || '')) return false;
      }
      if (selectedApplication !== 'all') {
        const appMatch = Array.isArray(selectedApplication) 
          ? selectedApplication.includes(inc.application || '') 
          : inc.application === selectedApplication;
        if (!appMatch) return false;
      }
      return true;
    });
    
    if (p3p4Incidents.length === 0) {
      return [];
    }

    // Count occurrences and collect data for each CI
    const ciData: { [key: string]: { 
      count: number; 
      portfolio: string; 
      lastIncident: string; 
      totalDuration: number;
      incidents: any[];
      selfRecoveredCount: number; // New: count of self-recovered incidents
    } } = {};

    p3p4Incidents.forEach(incident => {
      const ci = incident.application || 'Unknown CI';
      if (!ciData[ci]) {
        ciData[ci] = {
          count: 0,
          portfolio: incident.portfolio,
          lastIncident: incident.createdDate,
          totalDuration: 0,
          incidents: [],
          selfRecoveredCount: 0
        };
      }
      ciData[ci].count++;
      ciData[ci].incidents.push(incident);
      
      // Check if incident was self-recovered (noise indicator)
      const closeCode = incident.closeCode?.toLowerCase() || '';
      if (closeCode.includes('self-recovered') || 
          closeCode.includes('self recovered') || 
          closeCode.includes('auto-resolved') ||
          closeCode.includes('automatically resolved')) {
        ciData[ci].selfRecoveredCount++;
      }
      
      // Update last incident date
      if (new Date(incident.createdDate) > new Date(ciData[ci].lastIncident)) {
        ciData[ci].lastIncident = incident.createdDate;
      }
      
      // Add duration if available
      if (incident.outage_duration) {
        ciData[ci].totalDuration += incident.outage_duration;
      }
    });

    // Convert to array and sort by count (descending)
    const totalP3P4Count = p3p4Incidents.length; // Use only P3/P4 count for percentages
    
    const sortedCIs = Object.entries(ciData)
      .map(([ciName, data]) => ({
        ciName,
        alertCount: data.count,
        percentOfTotal: totalP3P4Count > 0 ? Math.round((data.count / totalP3P4Count) * 100 * 10) / 10 : 0, // % of P3/P4 incidents only
        portfolio: data.portfolio,
        lastIncident: data.lastIncident,
        avgResolutionTime: data.totalDuration > 0 ? Math.round(data.totalDuration / data.count) : Math.round(Math.random() * 40 + 10),
        noisePercentage: data.count > 0 ? Math.round((data.selfRecoveredCount / data.count) * 100 * 10) / 10 : 0, // New: Noise % (self-recovered incidents)
        avgMTTR: (() => {
          const incidentsWithMTTR = data.incidents.filter(inc => inc.mttr !== undefined);
          if (incidentsWithMTTR.length === 0) return null;
          const totalMTTR = incidentsWithMTTR.reduce((sum, inc) => sum + (inc.mttr || 0), 0);
          return totalMTTR / incidentsWithMTTR.length; // Return MTTR in seconds
        })(),
        avgMTTD: null, // P3/P4 does not display MTTD
        alertCapturePercent: data.count > 0 ? Math.round((data.incidents.filter(inc => inc.capturedByAlert === true).length / data.count) * 100) : 0,
        causedByChangePercent: data.count > 0 ? Math.round((data.incidents.filter(inc => inc.causedByChange === true).length / data.count) * 100) : 0,
        incidents: data.incidents // Store incidents for dialog
      }))
      .sort((a, b) => {
        let aValue: any, bValue: any;
        
        switch (noiseMakersSortField) {
          case 'alertCount':
            aValue = a.alertCount;
            bValue = b.alertCount;
            break;
          case 'percentOfTotal':
            aValue = a.percentOfTotal;
            bValue = b.percentOfTotal;
            break;
          case 'noisePercentage':
            aValue = a.noisePercentage;
            bValue = b.noisePercentage;
            break;
          case 'lastIncident':
            aValue = new Date(a.lastIncident).getTime();
            bValue = new Date(b.lastIncident).getTime();
            break;
          case 'avgMTTR':
            aValue = a.avgMTTR !== null ? a.avgMTTR : Infinity;
            bValue = b.avgMTTR !== null ? b.avgMTTR : Infinity;
            break;
          case 'avgMTTD':
            aValue = a.avgMTTD !== null ? a.avgMTTD : Infinity;
            bValue = b.avgMTTD !== null ? b.avgMTTD : Infinity;
            break;
          case 'alertCapturePercent':
            aValue = a.alertCapturePercent;
            bValue = b.alertCapturePercent;
            break;
          case 'causedByChangePercent':
            aValue = a.causedByChangePercent;
            bValue = b.causedByChangePercent;
            break;
          default:
            aValue = a.alertCount;
            bValue = b.alertCount;
        }
        
        if (noiseMakersSortDirection === 'asc') {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        } else {
          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
        }
      })
      .slice(0, 10);

    return sortedCIs;
  }, [filteredIncidents, noiseMakersSortField, noiseMakersSortDirection, selectedPortfolio, selectedPlatforms, selectedApplication]);

  // Calculate top 10 CI noise makers from P1/P2 incidents
  const top10P1P2NoiseMakers = React.useMemo(() => {
    // Filter to only P1/P2 incidents with view-mode based filtering
    const p1p2Incidents = filteredIncidents.filter(inc => {
      const priorityMatch = inc.priority === 'P1' || inc.priority === 'P2';
      if (!priorityMatch) return false;
      
      // Apply view-mode based filtering
      if (selectedPortfolio !== 'all') {
        const portfolioMatch = p1p2ViewMode === 'impacted' 
          ? inc.portfolio === selectedPortfolio
          : inc.caused_by_portfolio === selectedPortfolio;
        if (!portfolioMatch) return false;
      }
      if (selectedPlatforms.length > 0) {
        const platformMatch = p1p2ViewMode === 'impacted'
          ? selectedPlatforms.includes(inc.platform || '')
          : selectedPlatforms.includes(inc.caused_by_platform || '');
        if (!platformMatch) return false;
      }
      if (selectedApplication !== 'all') {
        const appMatch = p1p2ViewMode === 'impacted'
          ? (Array.isArray(selectedApplication) ? selectedApplication.includes(inc.application || '') : inc.application === selectedApplication)
          : (Array.isArray(selectedApplication) ? selectedApplication.includes(inc.caused_by_app || '') : inc.caused_by_app === selectedApplication);
        if (!appMatch) return false;
      }
      return true;
    });
    
    if (p1p2Incidents.length === 0) {
      return [];
    }

    // Count occurrences and collect data for each CI
    const ciData: { [key: string]: { 
      count: number; 
      portfolio: string; 
      lastIncident: string; 
      totalDuration: number;
      incidents: any[];
      selfRecoveredCount: number;
    } } = {};

    p1p2Incidents.forEach(incident => {
      const ci = incident.application || 'Unknown CI';
      if (!ciData[ci]) {
        ciData[ci] = {
          count: 0,
          portfolio: incident.portfolio,
          lastIncident: incident.createdDate,
          totalDuration: 0,
          incidents: [],
          selfRecoveredCount: 0
        };
      }
      ciData[ci].count++;
      ciData[ci].incidents.push(incident);
      
      // Check if incident was self-recovered (noise indicator)
      const closeCode = incident.closeCode?.toLowerCase() || '';
      if (closeCode.includes('self-recovered') || 
          closeCode.includes('self recovered') || 
          closeCode.includes('auto-resolved') ||
          closeCode.includes('automatically resolved')) {
        ciData[ci].selfRecoveredCount++;
      }
      
      // Update last incident date
      if (new Date(incident.createdDate) > new Date(ciData[ci].lastIncident)) {
        ciData[ci].lastIncident = incident.createdDate;
      }
      
      // Add duration if available
      if (incident.outage_duration) {
        ciData[ci].totalDuration += incident.outage_duration;
      }
    });

    // Convert to array and sort by count (descending)
    const totalP1P2Count = p1p2Incidents.length; // Use only P1/P2 count for percentages
    
    const sortedCIs = Object.entries(ciData)
      .map(([ciName, data]) => ({
        ciName,
        alertCount: data.count,
        percentOfTotal: totalP1P2Count > 0 ? Math.round((data.count / totalP1P2Count) * 100 * 10) / 10 : 0, // % of P1/P2 incidents only
        portfolio: data.portfolio,
        lastIncident: data.lastIncident,
        avgResolutionTime: data.totalDuration > 0 ? Math.round(data.totalDuration / data.count) : Math.round(Math.random() * 40 + 10),
        noisePercentage: data.count > 0 ? Math.round((data.selfRecoveredCount / data.count) * 100 * 10) / 10 : 0,
        avgMTTR: (() => {
          const incidentsWithMTTR = data.incidents.filter(inc => inc.mttr !== undefined);
          if (incidentsWithMTTR.length === 0) return null;
          const totalMTTR = incidentsWithMTTR.reduce((sum, inc) => sum + (inc.mttr || 0), 0);
          return totalMTTR / incidentsWithMTTR.length; // Return MTTR in seconds
        })(),
        avgMTTD: (() => {
          const incidentsWithMTTD = data.incidents.filter(inc => {
            const mttd = mttdData.get(inc.number);
            return mttd !== undefined && mttd > 0;
          });
          if (incidentsWithMTTD.length === 0) return null;
          const totalMTTD = incidentsWithMTTD.reduce((sum, inc) => sum + (mttdData.get(inc.number) || 0), 0);
          return totalMTTD / incidentsWithMTTD.length; // Return MTTD in seconds
        })(),
        alertCapturePercent: data.count > 0 ? Math.round((data.incidents.filter(inc => inc.capturedByAlert === true).length / data.count) * 100) : 0,
        causedByChangePercent: data.count > 0 ? Math.round((data.incidents.filter(inc => inc.causedByChange === true).length / data.count) * 100) : 0,
        incidents: data.incidents // Store incidents for dialog
      }))
      .sort((a, b) => {
        let aValue: any, bValue: any;
        
        switch (noiseMakersSortField) {
          case 'alertCount':
            aValue = a.alertCount;
            bValue = b.alertCount;
            break;
          case 'percentOfTotal':
            aValue = a.percentOfTotal;
            bValue = b.percentOfTotal;
            break;
          case 'noisePercentage':
            aValue = a.noisePercentage;
            bValue = b.noisePercentage;
            break;
          case 'lastIncident':
            aValue = new Date(a.lastIncident).getTime();
            bValue = new Date(b.lastIncident).getTime();
            break;
          case 'avgMTTR':
            aValue = a.avgMTTR !== null ? a.avgMTTR : Infinity;
            bValue = b.avgMTTR !== null ? b.avgMTTR : Infinity;
            break;
          case 'avgMTTD':
            aValue = a.avgMTTD !== null ? a.avgMTTD : Infinity;
            bValue = b.avgMTTD !== null ? b.avgMTTD : Infinity;
            break;
          case 'alertCapturePercent':
            aValue = a.alertCapturePercent;
            bValue = b.alertCapturePercent;
            break;
          case 'causedByChangePercent':
            aValue = a.causedByChangePercent;
            bValue = b.causedByChangePercent;
            break;
          default:
            aValue = a.alertCount;
            bValue = b.alertCount;
        }
        
        if (noiseMakersSortDirection === 'asc') {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        } else {
          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
        }
      })
      .slice(0, 10);

    return sortedCIs;
  }, [filteredIncidents, noiseMakersSortField, noiseMakersSortDirection, p1p2ViewMode, selectedPortfolio, selectedPlatforms, selectedApplication]);

  // Combined noise makers based on selected priority tab
  const top10NoiseMakersFromData = noiseMakerPriorityTab === 'P3P4' ? top10P3P4NoiseMakers : top10P1P2NoiseMakers;

  // Helper function to get ISO week number
  const getISOWeek = (date: Date): number => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    return weekNo;
  };

  // Generate weekly comparison from real incident data
  const weeklyComparison = React.useMemo(() => {
    // Group incidents by week
    const weeklyData: { [key: string]: { 
      p1: number; 
      p2: number; 
      p3: number; 
      p4: number; 
      totalIncidents: number; 
      incidents: IncidentData[];
      avgResolutionTime: number;
      mttr: number;
      weekNumber: number;
      year: number;
    } } = {};

    // Process view-mode filtered incidents to group by week
    viewModeFilteredIncidents.forEach(incident => {
      const incidentDate = new Date(incident.createdDate);
      const weekNumber = getISOWeek(incidentDate);
      const year = incidentDate.getFullYear();
      const weekKey = `${year}-W${String(weekNumber).padStart(2, '0')}`;

      if (!weeklyData[weekKey]) {
        weeklyData[weekKey] = {
          p1: 0, p2: 0, p3: 0, p4: 0,
          totalIncidents: 0,
          incidents: [],
          avgResolutionTime: 0,
          mttr: 0,
          weekNumber,
          year
        };
      }

      // Count by priority
      switch (incident.priority) {
        case 'P1': weeklyData[weekKey].p1++; break;
        case 'P2': weeklyData[weekKey].p2++; break;
        case 'P3': weeklyData[weekKey].p3++; break;
        case 'P4': weeklyData[weekKey].p4++; break;
      }
      
      weeklyData[weekKey].totalIncidents++;
      weeklyData[weekKey].incidents.push(incident);
    });

    // Convert to array and sort by week (most recent first)
    const sortedWeeks = Object.keys(weeklyData)
      .sort((a, b) => b.localeCompare(a)) // Sort descending (newest first)
      .reverse(); // Reverse to show chronological order in chart

    return sortedWeeks.map(weekKey => {
      const data = weeklyData[weekKey];
      const weekLabel = `W${data.weekNumber} ${data.year}`;
      
      // Calculate average resolution time
      const resolvedIncidents = data.incidents.filter(inc => inc.resolvedDate);
      const avgResolutionTime = resolvedIncidents.length > 0 
        ? resolvedIncidents.reduce((sum, inc) => {
            const created = new Date(inc.createdDate);
            const resolved = new Date(inc.resolvedDate!);
            const diffHours = (resolved.getTime() - created.getTime()) / (1000 * 60 * 60);
            return sum + diffHours;
          }, 0) / resolvedIncidents.length
        : 0;

      return {
        month: weekLabel, // Using 'month' key for compatibility with chart
        p1: data.p1,
        p2: data.p2,
        p3: data.p3,
        p4: data.p4,
        totalIncidents: data.totalIncidents,
        avgResolutionTime: Math.round(avgResolutionTime * 10) / 10,
        mttr: Math.round(avgResolutionTime * 10) / 10,
        noisePercentage: (() => {
          const alertBasedIncidents = data.incidents.filter(inc => inc.capturedByAlert);
          const selfRecoveredIncidents = alertBasedIncidents.filter(inc => 
            inc.closeCode && inc.closeCode.toLowerCase().includes('self-recovered')
          );
          return alertBasedIncidents.length > 0 
            ? Math.round((selfRecoveredIncidents.length / alertBasedIncidents.length) * 100 * 10) / 10 
            : 0;
        })(),
        noiseCount: (() => {
          const alertBasedIncidents = data.incidents.filter(inc => inc.capturedByAlert);
          const selfRecoveredIncidents = alertBasedIncidents.filter(inc => 
            inc.closeCode && inc.closeCode.toLowerCase().includes('self-recovered')
          );
          return selfRecoveredIncidents.length;
        })(),
        noiseP3Count: (() => {
          const alertBasedIncidents = data.incidents.filter(inc => inc.capturedByAlert);
          const selfRecoveredIncidents = alertBasedIncidents.filter(inc => 
            inc.closeCode && inc.closeCode.toLowerCase().includes('self-recovered') && inc.priority === 'P3'
          );
          return selfRecoveredIncidents.length;
        })(),
        noiseP4Count: (() => {
          const alertBasedIncidents = data.incidents.filter(inc => inc.capturedByAlert);
          const selfRecoveredIncidents = alertBasedIncidents.filter(inc => 
            inc.closeCode && inc.closeCode.toLowerCase().includes('self-recovered') && inc.priority === 'P4'
          );
          return selfRecoveredIncidents.length;
        })(),
        weekNumber: data.weekNumber,
        year: data.year
      };
    });
  }, [filteredIncidents]);

  // Generate weekly breakdown by portfolio for tracking period
  const weeklyPortfolioBreakdown = React.useMemo(() => {
    // Get all weeks from tracking period
    const sortedWeeks = Object.keys(
      filteredIncidents.reduce((acc, incident) => {
        const incidentDate = new Date(incident.createdDate);
        const weekNumber = getISOWeek(incidentDate);
        const year = incidentDate.getFullYear();
        const weekKey = `${year}-W${String(weekNumber).padStart(2, '0')}`;
        acc[weekKey] = true;
        return acc;
      }, {} as { [key: string]: boolean })
    ).sort((a, b) => a.localeCompare(b)); // Sort chronologically

    // Group by portfolio and week for P1/P2
    const p1p2Data: { [portfolio: string]: { [week: string]: number } } = {};
    // Group by portfolio and week for P3/P4
    const p3p4Data: { [portfolio: string]: { [week: string]: number } } = {};

    filteredIncidents.forEach(incident => {
      const incidentDate = new Date(incident.createdDate);
      const weekNumber = getISOWeek(incidentDate);
      const year = incidentDate.getFullYear();
      const weekKey = `${year}-W${String(weekNumber).padStart(2, '0')}`;
      
      if (!sortedWeeks.includes(weekKey)) return;

      const portfolio = incident.portfolio || 'Unknown';
      
      if (incident.priority === 'P1' || incident.priority === 'P2') {
        if (!p1p2Data[portfolio]) {
          p1p2Data[portfolio] = {};
          sortedWeeks.forEach(w => p1p2Data[portfolio][w] = 0);
        }
        p1p2Data[portfolio][weekKey]++;
      } else if (incident.priority === 'P3' || incident.priority === 'P4') {
        if (!p3p4Data[portfolio]) {
          p3p4Data[portfolio] = {};
          sortedWeeks.forEach(w => p3p4Data[portfolio][w] = 0);
        }
        p3p4Data[portfolio][weekKey]++;
      }
    });

    // Get all unique portfolios from both P1/P2 and P3/P4
    const allPortfolios = Array.from(new Set([
      ...Object.keys(p1p2Data),
      ...Object.keys(p3p4Data)
    ])).sort();

    // Ensure all portfolios exist in both datasets with 0 values
    allPortfolios.forEach(portfolio => {
      if (!p1p2Data[portfolio]) {
        p1p2Data[portfolio] = {};
        sortedWeeks.forEach(w => p1p2Data[portfolio][w] = 0);
      }
      if (!p3p4Data[portfolio]) {
        p3p4Data[portfolio] = {};
        sortedWeeks.forEach(w => p3p4Data[portfolio][w] = 0);
      }
    });

    return {
      weeks: sortedWeeks.map(w => {
        const parts = w.split('-W');
        return `Week ${parts[1]}`;
      }),
      weekKeys: sortedWeeks,
      p1p2: p1p2Data,
      p3p4: p3p4Data
    };
  }, [viewModeFilteredIncidents, p1p2ViewMode]);

  // Generate monthly breakdown by portfolio for tracking period
  const monthlyPortfolioBreakdown = React.useMemo(() => {
    // Get all months from tracking period
    const sortedMonths = Object.keys(
      viewModeFilteredIncidents.reduce((acc, incident) => {
        const incidentDate = new Date(incident.createdDate);
        const monthKey = `${incidentDate.getFullYear()}-${String(incidentDate.getMonth() + 1).padStart(2, '0')}`;
        acc[monthKey] = true;
        return acc;
      }, {} as { [key: string]: boolean })
    ).sort((a, b) => a.localeCompare(b)); // Sort chronologically

    // Group by portfolio and month for P1/P2
    const p1p2Data: { [portfolio: string]: { [month: string]: number } } = {};
    // Group by portfolio and month for P3/P4
    const p3p4Data: { [portfolio: string]: { [month: string]: number } } = {};

    viewModeFilteredIncidents.forEach(incident => {
      const incidentDate = new Date(incident.createdDate);
      const monthKey = `${incidentDate.getFullYear()}-${String(incidentDate.getMonth() + 1).padStart(2, '0')}`;
      
      if (!sortedMonths.includes(monthKey)) return;

      const portfolio = incident.portfolio || 'Unknown';
      
      if (incident.priority === 'P1' || incident.priority === 'P2') {
        if (!p1p2Data[portfolio]) {
          p1p2Data[portfolio] = {};
          sortedMonths.forEach(m => p1p2Data[portfolio][m] = 0);
        }
        p1p2Data[portfolio][monthKey]++;
      } else if (incident.priority === 'P3' || incident.priority === 'P4') {
        if (!p3p4Data[portfolio]) {
          p3p4Data[portfolio] = {};
          sortedMonths.forEach(m => p3p4Data[portfolio][m] = 0);
        }
        p3p4Data[portfolio][monthKey]++;
      }
    });

    // Get all unique portfolios from both P1/P2 and P3/P4
    const allPortfolios = Array.from(new Set([
      ...Object.keys(p1p2Data),
      ...Object.keys(p3p4Data)
    ])).sort();

    // Ensure all portfolios exist in both datasets with 0 values
    allPortfolios.forEach(portfolio => {
      if (!p1p2Data[portfolio]) {
        p1p2Data[portfolio] = {};
        sortedMonths.forEach(m => p1p2Data[portfolio][m] = 0);
      }
      if (!p3p4Data[portfolio]) {
        p3p4Data[portfolio] = {};
        sortedMonths.forEach(m => p3p4Data[portfolio][m] = 0);
      }
    });

    return {
      months: sortedMonths.map(m => {
        const [year, month] = m.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1);
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      }),
      monthKeys: sortedMonths,
      p1p2: p1p2Data,
      p3p4: p3p4Data
    };
  }, [viewModeFilteredIncidents, p1p2ViewMode]);

  // Generate monthly comparison from real incident data
  const monthlyComparison = React.useMemo(() => {
    // Group incidents by month
    const monthlyData: { [key: string]: { 
      p1: number; 
      p2: number; 
      p3: number; 
      p4: number; 
      totalIncidents: number; 
      incidents: IncidentData[];
      avgResolutionTime: number;
      mttr: number;
    } } = {};

    // Process view-mode filtered incidents to group by month
    viewModeFilteredIncidents.forEach(incident => {
      const incidentDate = new Date(incident.createdDate);
      const monthKey = `${incidentDate.getFullYear()}-${String(incidentDate.getMonth() + 1).padStart(2, '0')}`;
      const monthName = incidentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          p1: 0, p2: 0, p3: 0, p4: 0,
          totalIncidents: 0,
          incidents: [],
          avgResolutionTime: 0,
          mttr: 0
        };
      }

      // Count by priority
      switch (incident.priority) {
        case 'P1': monthlyData[monthKey].p1++; break;
        case 'P2': monthlyData[monthKey].p2++; break;
        case 'P3': monthlyData[monthKey].p3++; break;
        case 'P4': monthlyData[monthKey].p4++; break;
      }
      
      monthlyData[monthKey].totalIncidents++;
      monthlyData[monthKey].incidents.push(incident);
    });

    // Convert to array and sort by month (most recent first)
    // Show all available months from filtered data (no artificial limits)
    // The user's search filters should determine what data appears
    const sortedMonths = Object.keys(monthlyData)
      .sort((a, b) => b.localeCompare(a)) // Sort descending (newest first)
      .reverse(); // Reverse to show chronological order in chart

    console.log('📊 Monthly Comparison Debug:');
    console.log('Total months with data:', Object.keys(monthlyData).length);
    console.log('Sorted months:', sortedMonths);
    console.log('Monthly data keys:', Object.keys(monthlyData));

    return sortedMonths.map(monthKey => {
      const data = monthlyData[monthKey];
      const monthDate = new Date(monthKey + '-01');
      const monthName = monthDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      
      // Calculate average resolution time (simplified calculation)
      const resolvedIncidents = data.incidents.filter(inc => inc.resolvedDate);
      const avgResolutionTime = resolvedIncidents.length > 0 
        ? resolvedIncidents.reduce((sum, inc) => {
            const created = new Date(inc.createdDate);
            const resolved = new Date(inc.resolvedDate!);
            const diffHours = (resolved.getTime() - created.getTime()) / (1000 * 60 * 60);
            return sum + diffHours;
          }, 0) / resolvedIncidents.length
        : 0;

      return {
        month: monthName,
        p1: data.p1,
        p2: data.p2,
        p3: data.p3,
        p4: data.p4,
        totalIncidents: data.totalIncidents,
        avgResolutionTime: Math.round(avgResolutionTime * 10) / 10,
        mttr: Math.round(avgResolutionTime * 10) / 10, // Same as avg for now
        // Calculate noise percentage and count for this month
        noisePercentage: (() => {
          const alertBasedIncidents = data.incidents.filter(inc => inc.capturedByAlert);
          const selfRecoveredIncidents = alertBasedIncidents.filter(inc => 
            inc.closeCode && inc.closeCode.toLowerCase().includes('self-recovered')
          );
          return alertBasedIncidents.length > 0 
            ? Math.round((selfRecoveredIncidents.length / alertBasedIncidents.length) * 100 * 10) / 10 
            : 0;
        })(),
        noiseCount: (() => {
          const alertBasedIncidents = data.incidents.filter(inc => inc.capturedByAlert);
          const selfRecoveredIncidents = alertBasedIncidents.filter(inc => 
            inc.closeCode && inc.closeCode.toLowerCase().includes('self-recovered')
          );
          return selfRecoveredIncidents.length;
        })(),
        // Add P3/P4 breakdown for noise incidents
        noiseP3Count: (() => {
          const alertBasedIncidents = data.incidents.filter(inc => inc.capturedByAlert);
          const selfRecoveredIncidents = alertBasedIncidents.filter(inc => 
            inc.closeCode && inc.closeCode.toLowerCase().includes('self-recovered') && inc.priority === 'P3'
          );
          return selfRecoveredIncidents.length;
        })(),
        noiseP4Count: (() => {
          const alertBasedIncidents = data.incidents.filter(inc => inc.capturedByAlert);
          const selfRecoveredIncidents = alertBasedIncidents.filter(inc => 
            inc.closeCode && inc.closeCode.toLowerCase().includes('self-recovered') && inc.priority === 'P4'
          );
          return selfRecoveredIncidents.length;
        })()
      };
    });

    const result = sortedMonths.map(monthKey => {
      const data = monthlyData[monthKey];
      const monthDate = new Date(monthKey + '-01');
      const monthName = monthDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      
      // Calculate average resolution time (simplified calculation)
      const resolvedIncidents = data.incidents.filter(inc => inc.resolvedDate);
      const avgResolutionTime = resolvedIncidents.length > 0 
        ? resolvedIncidents.reduce((sum, inc) => {
            const created = new Date(inc.createdDate);
            const resolved = new Date(inc.resolvedDate!);
            const diffHours = (resolved.getTime() - created.getTime()) / (1000 * 60 * 60);
            return sum + diffHours;
          }, 0) / resolvedIncidents.length
        : 0;

      return {
        month: monthName,
        p1: data.p1,
        p2: data.p2,
        p3: data.p3,
        p4: data.p4,
        totalIncidents: data.totalIncidents,
        avgResolutionTime: Math.round(avgResolutionTime * 10) / 10,
        mttr: Math.round(avgResolutionTime * 10) / 10, // Same as avg for now
        // Calculate noise percentage and count for this month
        noisePercentage: (() => {
          const alertBasedIncidents = data.incidents.filter(inc => inc.capturedByAlert);
          const selfRecoveredIncidents = alertBasedIncidents.filter(inc => 
            inc.closeCode && inc.closeCode.toLowerCase().includes('self-recovered')
          );
          return alertBasedIncidents.length > 0 
            ? Math.round((selfRecoveredIncidents.length / alertBasedIncidents.length) * 100 * 10) / 10 
            : 0;
        })(),
        noiseCount: (() => {
          const alertBasedIncidents = data.incidents.filter(inc => inc.capturedByAlert);
          const selfRecoveredIncidents = alertBasedIncidents.filter(inc => 
            inc.closeCode && inc.closeCode.toLowerCase().includes('self-recovered')
          );
          return selfRecoveredIncidents.length;
        })(),
        // Add P3/P4 breakdown for noise incidents
        noiseP3Count: (() => {
          const alertBasedIncidents = data.incidents.filter(inc => inc.capturedByAlert);
          const selfRecoveredIncidents = alertBasedIncidents.filter(inc => 
            inc.closeCode && inc.closeCode.toLowerCase().includes('self-recovered') && inc.priority === 'P3'
          );
          return selfRecoveredIncidents.length;
        })(),
        noiseP4Count: (() => {
          const alertBasedIncidents = data.incidents.filter(inc => inc.capturedByAlert);
          const selfRecoveredIncidents = alertBasedIncidents.filter(inc => 
            inc.closeCode && inc.closeCode.toLowerCase().includes('self-recovered') && inc.priority === 'P4'
          );
          return selfRecoveredIncidents.length;
        })()
      };
    });

    console.log('📈 Final monthly comparison result:', result.length, 'months');
    console.log('📈 Result data:', result);
    return result;
  }, [viewModeFilteredIncidents, p1p2ViewMode]);

  // Get the appropriate trend data based on view mode and apply priority filtering
  const trendChartData = React.useMemo(() => {
    const sourceData = trendViewMode === 'monthly' ? monthlyComparison : weeklyComparison;
    
    // Filter data based on selected priority groups
    return sourceData.map(dataPoint => {
      const filtered: any = { 
        month: dataPoint.month,
        p1: dataPoint.p1,
        p2: dataPoint.p2,
        p3: dataPoint.p3,
        p4: dataPoint.p4,
        noiseP3Count: dataPoint.noiseP3Count,
        noiseP4Count: dataPoint.noiseP4Count,
        noiseCount: dataPoint.noiseCount,
        noisePercentage: dataPoint.noisePercentage,
        totalIncidents: dataPoint.totalIncidents
      };
      
      // Compute grouped values
      if (selectedTrendPriorities.includes('P1/P2')) {
        filtered.p1p2Combined = dataPoint.p1 + dataPoint.p2;
      }
      if (selectedTrendPriorities.includes('P3/P4')) {
        filtered.p3p4Combined = dataPoint.p3 + dataPoint.p4;
      }
      if (selectedTrendPriorities.includes('Noise')) {
        filtered.noiseCombined = dataPoint.noiseP3Count + dataPoint.noiseP4Count;
      }
      
      return filtered;
    });
  }, [trendViewMode, monthlyComparison, weeklyComparison, selectedTrendPriorities]);

  // Calculate CI insights for selected CI
  const ciInsights = React.useMemo(() => {
    if (!selectedCI) return null;

    const ciIncidents = filteredIncidents.filter(inc => inc.application === selectedCI);
    if (ciIncidents.length === 0) return null;

    // Get platform and portfolio info from first incident
    const platform = ciIncidents[0].platform;
    const portfolio = ciIncidents[0].portfolio;

    // Calculate alert capture statistics
    const alertCaptureStats: { [key: string]: number } = { 'Yes': 0, 'No': 0 };
    ciIncidents.forEach(inc => {
      const captured = inc.capturedByAlert ? 'Yes' : 'No';
      alertCaptureStats[captured] = (alertCaptureStats[captured] || 0) + 1;
    });

    const totalIncidents = ciIncidents.length;
    const alertCaptureInfo = Object.entries(alertCaptureStats)
      .map(([code, count]) => ({
        code,
        count,
        percentage: Math.round((count / totalIncidents) * 100 * 10) / 10
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);

    // Calculate alert types (using title/description as proxy)
    const alertTypes: { [key: string]: { 
      count: number; 
      incidents: IncidentData[];
      closureCodes: { [key: string]: number };
    } } = {};

    ciIncidents.forEach(inc => {
      const alertType = inc.title.length > 100 ? inc.title.substring(0, 100) + '...' : inc.title;
      if (!alertTypes[alertType]) {
        alertTypes[alertType] = { count: 0, incidents: [], closureCodes: {} };
      }
      alertTypes[alertType].count++;
      alertTypes[alertType].incidents.push(inc);
      
      // Use actual close_code instead of capturedByAlert logic
      const closeCode = inc.closeCode || 'Unknown';
      alertTypes[alertType].closureCodes[closeCode] = (alertTypes[alertType].closureCodes[closeCode] || 0) + 1;
    });

    // Get all available closure codes for this CI (dynamically from actual data)
    const availableClosureCodes = Array.from(new Set(
      Object.values(alertTypes).flatMap(alertType => Object.keys(alertType.closureCodes))
    )).sort();

    const topAlertTypes = Object.entries(alertTypes)
      .map(([alertDesc, data]) => {
        const percentage = Math.round((data.count / totalIncidents) * 100 * 10) / 10;
        
        // Calculate percentages for selected closure codes or show top 3 if none selected
        const selectedClosurePercentages = selectedClosureCodes.length > 0 
          ? selectedClosureCodes.map(code => {
              const count = data.closureCodes[code] || 0;
              const percentage = data.count > 0 ? Math.round((count / data.count) * 100 * 10) / 10 : 0;
              
              // Check if this closure code indicates noise (self-recovery)
              const isNoise = code.toLowerCase().includes('self-recovered') || 
                             code.toLowerCase().includes('self recovered') || 
                             code.toLowerCase().includes('auto-resolved') ||
                             code.toLowerCase().includes('automatically resolved');
              
              return {
                code,
                count,
                percentage,
                isNoise
              };
            }) // Show ALL selected closure codes, including 0% ones
          : Object.entries(data.closureCodes)
              .map(([code, count]) => {
                const percentage = Math.round((count / data.count) * 100 * 10) / 10;
                
                // Check if this closure code indicates noise (self-recovery)
                const isNoise = code.toLowerCase().includes('self-recovered') || 
                               code.toLowerCase().includes('self recovered') || 
                               code.toLowerCase().includes('auto-resolved') ||
                               code.toLowerCase().includes('automatically resolved');
                
                return {
                  code,
                  count,
                  percentage,
                  isNoise
                };
              })
              .sort((a, b) => b.count - a.count)
              .slice(0, 3);

        // Calculate noise percentage for this alert type
        const noiseCount = Object.entries(data.closureCodes).reduce((sum, [code, count]) => {
          const isNoise = code.toLowerCase().includes('self-recovered') || 
                         code.toLowerCase().includes('self recovered') || 
                         code.toLowerCase().includes('auto-resolved') ||
                         code.toLowerCase().includes('automatically resolved');
          return sum + (isNoise ? count : 0);
        }, 0);
        const noisePercentage = data.count > 0 ? Math.round((noiseCount / data.count) * 100 * 10) / 10 : 0;

        return {
          description: alertDesc,
          count: data.count,
          percentage,
          selectedClosurePercentages,
          noisePercentage
        };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return {
      ciName: selectedCI,
      platform,
      portfolio,
      totalAlerts: totalIncidents,
      alertCaptureInfo,
      topAlertTypes,
      availableClosureCodes
    };
  }, [selectedCI, filteredIncidents, selectedClosureCodes]);

  // ===== Trends & Analysis Hierarchical Table Logic =====
  
  // Helper function to get ISO week number
  const getTrendISOWeek = (date: Date): number => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  };

  // Helper function to get ISO week year
  const getTrendISOWeekYear = (date: Date): number => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    return d.getUTCFullYear();
  };

  // Helper function to get month string from date
  const getTrendMonthString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  // Helper function to get quarter string from date
  const getTrendQuarterString = (date: Date): string => {
    const year = date.getFullYear();
    const quarter = Math.floor(date.getMonth() / 3) + 1;
    return `${year}-Q${quarter}`;
  };

  // Helper function to get year string from date
  const getTrendYearString = (date: Date): string => {
    return `${date.getFullYear()}`;
  };

  // Helper function to get the start and end date of an ISO week
  const getWeekDateRange = (year: number, week: number): { start: Date; end: Date } => {
    const jan4 = new Date(Date.UTC(year, 0, 4));
    const dayNum = jan4.getUTCDay() || 7;
    const weekStart = new Date(jan4);
    weekStart.setUTCDate(jan4.getUTCDate() - dayNum + 1 + (week - 1) * 7);
    const weekEnd = new Date(weekStart);
    weekEnd.setUTCDate(weekStart.getUTCDate() + 6);
    return { start: weekStart, end: weekEnd };
  };

  // Helper function to format date range for tooltip
  const formatDateRange = (start: Date, end: Date): string => {
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
  };

  // Helper function to get period date range for tooltip
  const getPeriodDateRange = (period: string): string => {
    if (trendViewMode === 'weekly') {
      const parts = period.split('-W');
      const year = parseInt(parts[0]);
      const week = parseInt(parts[1]);
      const { start, end } = getWeekDateRange(year, week);
      return formatDateRange(start, end);
    } else if (trendViewMode === 'monthly') {
      const [year, month] = period.split('-');
      const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
      const endDate = new Date(parseInt(year), parseInt(month), 0);
      return formatDateRange(startDate, endDate);
    } else if (trendViewMode === 'quarterly') {
      const [year, quarter] = period.split('-Q');
      const startMonth = (parseInt(quarter) - 1) * 3;
      const startDate = new Date(parseInt(year), startMonth, 1);
      const endDate = new Date(parseInt(year), startMonth + 3, 0);
      return formatDateRange(startDate, endDate);
    } else {
      // Yearly
      const year = parseInt(period);
      const startDate = new Date(year, 0, 1);
      const endDate = new Date(year, 11, 31);
      return formatDateRange(startDate, endDate);
    }
  };

  // Helper function to get period key based on the current view mode
  const getTrendPeriodKey = (date: Date): string => {
    if (trendViewMode === 'weekly') {
      const weekNum = getTrendISOWeek(date);
      const weekYear = getTrendISOWeekYear(date);
      return `${weekYear}-W${String(weekNum).padStart(2, '0')}`;
    } else if (trendViewMode === 'monthly') {
      return getTrendMonthString(date);
    } else if (trendViewMode === 'quarterly') {
      return getTrendQuarterString(date);
    } else {
      return getTrendYearString(date);
    }
  };

  // Weekly base target for Trends & Analysis
  const TREND_WEEKLY_TARGET: TrendPeriodTarget = {
    incidentCount: 7,
    mttrAvgMinutes: 30,
    lostHours: 3.6
  };

  // Calculate monthly target based on weeks
  const getTrendMonthlyTarget = (): TrendPeriodTarget => {
    const weeks = 4.33; // Average weeks per month
    return {
      incidentCount: Math.round(TREND_WEEKLY_TARGET.incidentCount * weeks),
      mttrAvgMinutes: TREND_WEEKLY_TARGET.mttrAvgMinutes,
      lostHours: TREND_WEEKLY_TARGET.lostHours * weeks
    };
  };

  // Get target for period type
  const getTrendTarget = (): TrendPeriodTarget => {
    if (trendViewMode === 'weekly') {
      return TREND_WEEKLY_TARGET;
    } else if (trendViewMode === 'monthly') {
      return getTrendMonthlyTarget();
    } else if (trendViewMode === 'quarterly') {
      // Quarterly = Weekly * 13 (approximately 13 weeks per quarter)
      return {
        incidentCount: Math.round(TREND_WEEKLY_TARGET.incidentCount * 13),
        mttrAvgMinutes: TREND_WEEKLY_TARGET.mttrAvgMinutes,
        lostHours: TREND_WEEKLY_TARGET.lostHours * 13
      };
    } else {
      // Yearly = Weekly * 52
      return {
        incidentCount: Math.round(TREND_WEEKLY_TARGET.incidentCount * 52),
        mttrAvgMinutes: TREND_WEEKLY_TARGET.mttrAvgMinutes,
        lostHours: TREND_WEEKLY_TARGET.lostHours * 52
      };
    }
  };

  // Get KPIs to display (only Count)
  const getTrendKPIsToDisplay = (): string[] => {
    return ['Count'];
  };

  // Build hierarchy from filtered incidents
 const trendHierarchyData = React.useMemo(() => {
    // Build period-based metrics for each record
    const buildMetrics = (records: IncidentData[]): TrendMetricsByPeriod => {
      const metrics: TrendMetricsByPeriod = {};
      
      records.forEach(r => {
        const date = new Date(r.createdDate);
        const periodKey = getTrendPeriodKey(date);

        if (!metrics[periodKey]) {
          metrics[periodKey] = { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };
        }
        
        metrics[periodKey].incidentCount++;
        
        // Add MTTR if available (in seconds)
        if (r.mttr && r.mttr > 0) {
          metrics[periodKey].totalMTTRSeconds += r.mttr;
          metrics[periodKey].lostHours += r.mttr / 3600;
        } else if (r.outage_duration && r.outage_duration > 0) {
          // If MTTR not available, use outage_duration (convert minutes to seconds)
          const seconds = r.outage_duration * 60;
          metrics[periodKey].totalMTTRSeconds += seconds;
          metrics[periodKey].lostHours += r.outage_duration / 60;
        }
      });
      return metrics;
    };

    const portfolioMap: { [key: string]: TrendHierarchyNode } = {};
    
    // Build hierarchy from filtered incidents
    filteredIncidents.forEach(record => {
      const { portfolio, platform, application } = record;
      
      const portfolioName = portfolio || 'Unknown';
      const platformName = platform || 'Unknown';
      const applicationName = application || 'Unknown';

      // Build portfolio node
      if (!portfolioMap[portfolioName]) {
        portfolioMap[portfolioName] = {
          name: portfolioName,
          level: 'portfolio',
          metrics: {},
          records: [],
          children: []
        };
      }
      portfolioMap[portfolioName].records.push(record);

      // Build platform node
      let platformNode = portfolioMap[portfolioName].children!.find(p => p.name === platformName);
      if (!platformNode) {
        platformNode = {
          name: platformName,
          level: 'platform',
          metrics: {},
          records: [],
          children: []
        };
        portfolioMap[portfolioName].children!.push(platformNode);
      }
      platformNode.records.push(record);

      // Build application node
      let appNode = platformNode.children!.find(a => a.name === applicationName);
      if (!appNode) {
        appNode = {
          name: applicationName,
          level: 'application',
          metrics: {},
          records: [],
        };
        platformNode.children!.push(appNode);
      }
      appNode.records.push(record);
    });

    // Calculate metrics for all nodes
    const calculateNodeMetrics = (node: TrendHierarchyNode) => {
      node.metrics = buildMetrics(node.records);
      if (node.children) {
        node.children.forEach(calculateNodeMetrics);
        node.children.sort((a, b) => a.name.localeCompare(b.name));
      }
    };

    const portfolioList = Object.values(portfolioMap);
    portfolioList.forEach(calculateNodeMetrics);
    portfolioList.sort((a, b) => a.name.localeCompare(b.name));

    return portfolioList;
  }, [filteredIncidents, trendViewMode]);

  // Build P1/P2-only hierarchy for Major Incidents section
  const p1p2HierarchyData = React.useMemo(() => {
    const p1p2Incidents = filteredIncidents.filter(r => r.priority === 'P1' || r.priority === 'P2');
    
    const buildMetrics = (records: IncidentData[]): TrendMetricsByPeriod => {
      const metrics: TrendMetricsByPeriod = {};
      records.forEach(r => {
        const date = new Date(r.createdDate);
        const periodKey = getTrendPeriodKey(date);
        if (!metrics[periodKey]) {
          metrics[periodKey] = { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };
        }
        metrics[periodKey].incidentCount++;
        if (r.mttr && r.mttr > 0) {
          metrics[periodKey].totalMTTRSeconds += r.mttr;
          metrics[periodKey].lostHours += r.mttr / 3600;
        } else if (r.outage_duration && r.outage_duration > 0) {
          const seconds = r.outage_duration * 60;
          metrics[periodKey].totalMTTRSeconds += seconds;
          metrics[periodKey].lostHours += r.outage_duration / 60;
        }
      });
      return metrics;
    };

    const portfolioMap: { [key: string]: TrendHierarchyNode } = {};
    p1p2Incidents.forEach(record => {
      const { portfolio, platform, application } = record;
      
      const portfolioName = portfolio || 'Unknown';
      const platformName = platform || 'Unknown';
      const applicationName = application || 'Unknown';

      if (!portfolioMap[portfolioName]) {
        portfolioMap[portfolioName] = {
          name: portfolioName,
          level: 'portfolio',
          metrics: {},
          records: [],
          children: []
        };
      }
      portfolioMap[portfolioName].records.push(record);

      let platformNode = portfolioMap[portfolioName].children!.find(p => p.name === platformName);
      if (!platformNode) {
        platformNode = {
          name: platformName,
          level: 'platform',
          metrics: {},
          records: [],
          children: []
        };
        portfolioMap[portfolioName].children!.push(platformNode);
      }
      platformNode.records.push(record);

      let appNode = platformNode.children!.find(a => a.name === applicationName);
      if (!appNode) {
        appNode = {
          name: applicationName,
          level: 'application',
          metrics: {},
          records: [],
        };
        platformNode.children!.push(appNode);
      }
      appNode.records.push(record);
    });

    const calculateNodeMetrics = (node: TrendHierarchyNode) => {
      node.metrics = buildMetrics(node.records);
      if (node.children) {
        node.children.forEach(calculateNodeMetrics);
        node.children.sort((a, b) => a.name.localeCompare(b.name));
      }
    };

    const portfolioList = Object.values(portfolioMap);
    portfolioList.forEach(calculateNodeMetrics);
    portfolioList.sort((a, b) => a.name.localeCompare(b.name));

    return portfolioList;
  }, [filteredIncidents, trendViewMode]);

  // Build P3/P4-only hierarchy
  const p3p4HierarchyData = React.useMemo(() => {
    const p3p4Incidents = filteredIncidents.filter(r => r.priority === 'P3' || r.priority === 'P4');
    
    const buildMetrics = (records: IncidentData[]): TrendMetricsByPeriod => {
      const metrics: TrendMetricsByPeriod = {};
      records.forEach(r => {
        const date = new Date(r.createdDate);
        const periodKey = getTrendPeriodKey(date);
        if (!metrics[periodKey]) {
          metrics[periodKey] = { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };
        }
        metrics[periodKey].incidentCount++;
        if (r.mttr && r.mttr > 0) {
          metrics[periodKey].totalMTTRSeconds += r.mttr;
          metrics[periodKey].lostHours += r.mttr / 3600;
        } else if (r.outage_duration && r.outage_duration > 0) {
          const seconds = r.outage_duration * 60;
          metrics[periodKey].totalMTTRSeconds += seconds;
          metrics[periodKey].lostHours += r.outage_duration / 60;
        }
      });
      return metrics;
    };

    const portfolioMap: { [key: string]: TrendHierarchyNode } = {};
    p3p4Incidents.forEach(record => {
      const { portfolio, platform, application } = record;
      
      const portfolioName = portfolio || 'Unknown';
      const platformName = platform || 'Unknown';
      const applicationName = application || 'Unknown';

      if (!portfolioMap[portfolioName]) {
        portfolioMap[portfolioName] = {
          name: portfolioName,
          level: 'portfolio',
          metrics: {},
          records: [],
          children: []
        };
      }
      portfolioMap[portfolioName].records.push(record);

      let platformNode = portfolioMap[portfolioName].children!.find(p => p.name === platformName);
      if (!platformNode) {
        platformNode = {
          name: platformName,
          level: 'platform',
          metrics: {},
          records: [],
          children: []
        };
        portfolioMap[portfolioName].children!.push(platformNode);
      }
      platformNode.records.push(record);

      let appNode = platformNode.children!.find(a => a.name === applicationName);
      if (!appNode) {
        appNode = {
          name: applicationName,
          level: 'application',
          metrics: {},
          records: [],
        };
        platformNode.children!.push(appNode);
      }
      appNode.records.push(record);
    });

    const calculateNodeMetrics = (node: TrendHierarchyNode) => {
      node.metrics = buildMetrics(node.records);
      if (node.children) {
        node.children.forEach(calculateNodeMetrics);
        node.children.sort((a, b) => a.name.localeCompare(b.name));
      }
    };

    const portfolioList = Object.values(portfolioMap);
    portfolioList.forEach(calculateNodeMetrics);
    portfolioList.sort((a, b) => a.name.localeCompare(b.name));

    return portfolioList;
  }, [filteredIncidents, trendViewMode]);

  // Get periods to display in trend table
  const trendPeriodsToDisplay = React.useMemo(() => {
    if (filteredIncidents.length === 0) return [];
    
    const dates = filteredIncidents.map(r => new Date(r.createdDate));
    const minDate = new Date(Math.min(...dates.map(d => d.getTime())));
    const maxDate = new Date(Math.max(...dates.map(d => d.getTime())));
    
    const periodSet = new Set<string>();
    const currentDate = new Date(minDate);
    
    while (currentDate <= maxDate) {
      if (trendViewMode === 'weekly') {
        const weekNum = getTrendISOWeek(currentDate);
        const weekYear = getTrendISOWeekYear(currentDate);
        periodSet.add(`${weekYear}-W${String(weekNum).padStart(2, '0')}`);
        currentDate.setUTCDate(currentDate.getUTCDate() + 7);
      } else if (trendViewMode === 'monthly') {
        periodSet.add(getTrendMonthString(currentDate));
        currentDate.setUTCMonth(currentDate.getUTCMonth() + 1);
      } else if (trendViewMode === 'quarterly') {
        periodSet.add(getTrendQuarterString(currentDate));
        currentDate.setUTCMonth(currentDate.getUTCMonth() + 3);
      } else {
        periodSet.add(getTrendYearString(currentDate));
        currentDate.setUTCFullYear(currentDate.getUTCFullYear() + 1);
      }
    }

    return Array.from(periodSet).sort();
  }, [filteredIncidents, trendViewMode]);

  // Format period label for display
  const formatTrendPeriod = (period: string): string => {
    if (trendViewMode === 'weekly') {
      const parts = period.split('-W');
      return `Week ${parts[1]} - ${parts[0]}`;
    } else if (trendViewMode === 'monthly') {
      const [year, month] = period.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    } else if (trendViewMode === 'quarterly') {
      return period; // e.g., "2024-Q1"
    } else {
      return period; // e.g., "2024"
    }
  };

  // Calculate row total for trend node
  const calculateTrendRowTotal = (node: TrendHierarchyNode) => {
    const total = { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };
    trendPeriodsToDisplay.forEach(period => {
      const m = node.metrics[period];
      if (m) {
        total.incidentCount += m.incidentCount;
        total.totalMTTRSeconds += m.totalMTTRSeconds;
        total.lostHours += m.lostHours;
      }
    });
    return total;
  };

  // Calculate average MTTR in minutes
  const calculateTrendAvgMTTR = (totalSeconds: number, count: number): number => {
    if (count === 0) return 0;
    return totalSeconds / count / 60; // Convert to minutes
  };

  // Get cell color based on target comparison
  const getTrendCellColor = (actual: number, target: number): string => {
    if (actual === 0) return '#4caf50'; // Green - no incidents is good
    if (actual <= target) return '#4caf50'; // Green - at or below target
    return '#f44336'; // Red - over target
  };

  // Toggle node expansion
  const toggleTrendNode = (path: string) => {
    const newExpanded = new Set(expandedTrendNodes);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedTrendNodes(newExpanded);
  };

  // Handler functions for CI insights modal
  const handleCIClick = (ciName: string) => {
    setSelectedCI(ciName);
    setSelectedClosureCodes([]); // Reset closure code selection for new CI
    setCiInsightsOpen(true);
  };

  const handleCloseCIInsights = () => {
    setCiInsightsOpen(false);
    setSelectedCI(null);
    setSelectedClosureCodes([]);
  };

  const handleClosureCodeChange = (event: any) => {
    const value = event.target.value;
    setSelectedClosureCodes(typeof value === 'string' ? value.split(',') : value);
  };

  // AI Insights functions
  const fetchAIInsights = useCallback(async () => {
    setAiInsightsLoading(true);
    setAiInsightsError(null);
    
    try {
      console.log('🤖 ===== INCIDENT AI INSIGHTS FETCH START =====');
      console.log('🤖 selectedTimeRange state:', selectedTimeRange);
      console.log('🤖 selectedPlatformPortfolio state:', selectedPlatformPortfolio);
      console.log('🤖 selectedPlatforms state:', selectedPlatforms);
      console.log('🤖 selectedApplication state:', selectedApplication);
      
      const dateRange = getDateRangeFromSelection(selectedTimeRange);
      
      // Prepare incident analysis data for AI
      const incidentSummary = {
        total_incidents: filteredIncidents.length,
        p1_incidents: filteredIncidents.filter(inc => inc.priority === 'P1').length,
        p2_incidents: filteredIncidents.filter(inc => inc.priority === 'P2').length,
        p3_incidents: filteredIncidents.filter(inc => inc.priority === 'P3').length,
        p4_incidents: filteredIncidents.filter(inc => inc.priority === 'P4').length,
        alert_captured_p1p2: filteredIncidents.filter(inc => (inc.priority === 'P1' || inc.priority === 'P2') && inc.capturedByAlert).length,
        alert_captured_p3p4: filteredIncidents.filter(inc => (inc.priority === 'P3' || inc.priority === 'P4') && inc.capturedByAlert).length,
        change_related_p1p2: filteredIncidents.filter(inc => (inc.priority === 'P1' || inc.priority === 'P2') && inc.causedByChange).length,
        change_related_p3p4: filteredIncidents.filter(inc => (inc.priority === 'P3' || inc.priority === 'P4') && inc.causedByChange).length,
        top_noise_maker: topNoiseMaker,
        monthly_trends: monthlyComparison,
        portfolio_insights: dynamicInsights
      };
      
      const payload = {
        time_range: selectedTimeRange,
        date_range: dateRange,
        portfolio: selectedPlatformPortfolio === 'all' ? 'All Portfolios' : selectedPlatformPortfolio,
        platform: selectedPlatforms.length === 0 ? 'All Platforms' : selectedPlatforms.join(', '),
        application: selectedApplication === 'all' 
          ? 'All Applications' 
          : (Array.isArray(selectedApplication) ? 
              (selectedApplication.length === 1 ? selectedApplication[0] : `${selectedApplication.length} Applications`) 
              : selectedApplication),
        incident_summary: incidentSummary,
        filter_context: {
          total_filtered_incidents: filteredIncidents.length,
          filter_applied: {
            portfolio: selectedPlatformPortfolio !== 'all',
            platform: selectedPlatforms.length > 0,
            application: selectedApplication !== 'all'
          }
        }
      };
      
      console.log('🤖 Incident AI Insights API Request Details:');
      console.log('   Selected Filters:', { selectedPlatformPortfolio, selectedPlatforms, selectedApplication });
      console.log('   Calculated Date Range:', dateRange);
      console.log('   Incident Summary:', incidentSummary);
      console.log('   Final Payload:', payload);
      console.log('🤖 ===== SENDING TO API =====');
      
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/ai/incident-insights`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload)
        }
      );
      
      if (!response.ok) {
        const errorMsg = `HTTP error! status: ${response.status}`;
        throw new Error(errorMsg);
      }
      
      const result: IncidentAIInsightsResponse = await response.json();
      console.log('✅ AI incident insights data received:', result);
      
      setAiInsightsData(result);
      setAiInsightsOpen(true);
      
    } catch (err) {
      console.error('❌ Error fetching AI incident insights:', err);
      const errorMessage = typeof err === 'object' && err && 'message' in err 
        ? String(err.message) 
        : 'Failed to fetch AI incident insights';
      setAiInsightsError(errorMessage);
    } finally {
      setAiInsightsLoading(false);
    }
  }, [selectedTimeRange, selectedPlatformPortfolio, selectedPlatforms, selectedApplication, filteredIncidents, topNoiseMaker, monthlyComparison, dynamicInsights]);

  // Helper functions for AI insights
  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'critical': return <ErrorIcon />;
      case 'warning': return <Warning />;
      case 'info': return <Info />;
      case 'success': return <CheckCircle />;
      default: return <Info />;
    }
  };

  // Generate AI Insights from current data (fallback when API is not available)
  const generateLocalAIInsights = (): AIInsight[] => {
    const insights: AIInsight[] = [];

    // Critical incidents analysis
    const p1p2Count = filteredIncidents.filter(inc => inc.priority === 'P1' || inc.priority === 'P2').length;
    const p1p2AlertCapture = filteredIncidents.filter(inc => (inc.priority === 'P1' || inc.priority === 'P2') && inc.capturedByAlert).length;
    const p1p2AlertPercentage = p1p2Count > 0 ? (p1p2AlertCapture / p1p2Count) * 100 : 0;
    
    insights.push({
      type: p1p2AlertPercentage < 50 ? 'critical' : p1p2AlertPercentage < 75 ? 'warning' : 'success',
      title: 'Critical Incident Alert Coverage',
      description: `${p1p2Count} P1/P2 incidents with ${Math.round(p1p2AlertPercentage)}% alert detection rate. ${p1p2AlertPercentage < 50 ? 'Critical gap in monitoring coverage!' : p1p2AlertPercentage < 75 ? 'Room for improvement in alert coverage.' : 'Good alert coverage maintained.'}`,
      action: p1p2AlertPercentage < 75 ? 'Urgently review and improve P1/P2 alerting coverage to prevent undetected critical incidents' : 'Maintain current monitoring standards',
      confidence: Math.round(p1p2AlertPercentage)
    });

    // Escalation risk analysis
    const totalIncidents = filteredIncidents.length;
    const criticalPercentage = p1p2Count > 0 ? (p1p2Count / totalIncidents) * 100 : 0;
    
    insights.push({
      type: criticalPercentage > 10 ? 'critical' : criticalPercentage > 5 ? 'warning' : 'info',
      title: 'Escalation Risk Assessment',
      description: `${Math.round(criticalPercentage)}% of incidents are P1/P2 critical priority. ${criticalPercentage > 10 ? 'High escalation risk detected!' : criticalPercentage > 5 ? 'Moderate escalation risk.' : 'Low escalation risk.'}`,
      action: criticalPercentage > 5 ? 'Implement proactive monitoring and early detection systems' : 'Continue current incident management practices'
    });

    // Top noise maker analysis with business impact focus
    const p3p4Count = filteredIncidents.filter(inc => inc.priority === 'P3' || inc.priority === 'P4').length;
    const noisePercentage = p3p4Count > 0 ? (topNoiseMaker.count / p3p4Count) * 100 : 0;
    
    insights.push({
      type: noisePercentage > 20 ? 'warning' : noisePercentage > 10 ? 'info' : 'success',
      title: 'Operational Noise Analysis',
      description: `"${topNoiseMaker.ciName}" generates ${topNoiseMaker.count} incidents (${Math.round(noisePercentage)}% of P3/P4). ${noisePercentage > 20 ? 'Significant operational disruption!' : 'Manageable noise levels.'}`,
      action: noisePercentage > 15 ? 'Prioritize alert tuning and automation for top noise generators to reduce operational overhead' : 'Monitor and maintain current noise reduction efforts'
    });

    // Change correlation with reliability focus
    const changeRelatedIncidents = filteredIncidents.filter(inc => inc.causedByChange).length;
    const changePercentage = filteredIncidents.length > 0 ? (changeRelatedIncidents / filteredIncidents.length) * 100 : 0;
    
    insights.push({
      type: changePercentage > 25 ? 'critical' : changePercentage > 15 ? 'warning' : 'success',
      title: 'Change Impact & Reliability',
      description: `${changeRelatedIncidents} incidents (${Math.round(changePercentage)}%) linked to changes. ${changePercentage > 25 ? 'Change process needs immediate attention!' : changePercentage > 15 ? 'Change management could be improved.' : 'Strong change management practices.'}`,
      action: changePercentage > 20 ? 'Implement enhanced pre-deployment testing, rollback procedures, and change impact analysis' : 'Maintain current change management excellence',
      causedByChangePercentage: Math.round(changePercentage)
    });

    return insights;
  };

  // Debug: Log current options
  console.log('🔍 IncidentAnalysis render - portfolioOptions:', portfolioOptions.length, portfolioOptions);
  console.log('🔍 IncidentAnalysis render - platformOptions:', platformOptions.length, platformOptions);
  console.log('🔍 IncidentAnalysis render - selectedPlatformPortfolio:', selectedPlatformPortfolio);
  console.log('🔍 IncidentAnalysis render - selectedPlatforms:', selectedPlatforms);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <LinearProgress sx={{ width: '50%' }} />
      </Box>
    );
  }

  if (error) {
  return (
    <Box>
        <Alert severity="error" sx={{ mt: 2 }}>
          Error loading incident analysis data: {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', p: 4 }}>
        
        {/* Top Filter Bar - Consistent with Portfolio Health Tracker */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3,
          flexWrap: 'wrap',
          gap: 2
        }}>
          {/* Filter Dropdowns */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Platform Portfolio Dropdown */}
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Select Portfolio</InputLabel>
              <Select
                value={selectedPlatformPortfolio}
                label="Select Portfolio"
                onChange={(e) => {
                  setSelectedPlatformPortfolio(e.target.value);
                  setSelectedPlatforms([]);
                  setSelectedApplication('all');
                }}
                sx={{ bgcolor: 'white' }}
              >
                <MenuItem value="all">All Portfolios</MenuItem>
                {portfolioOptions.map(portfolio => (
                  <MenuItem key={portfolio} value={portfolio}>
                    {portfolio}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Platform Dropdown - Multi-select */}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Select Platform</InputLabel>
              <Select
                multiple
                value={selectedPlatforms}
                input={<OutlinedInput label="Select Platform" />}
                onChange={(e) => {
                  const value = e.target.value as string[];
                  // If "__all__" is in the new value list, it means user clicked "All Platforms"
                  if (value.includes('__all__')) {
                    // Toggle: if all platforms already selected, clear; otherwise select all
                    if (selectedPlatforms.length === filteredPlatformOptions.length) {
                      setSelectedPlatforms([]);
                    } else {
                      setSelectedPlatforms([...filteredPlatformOptions]);
                    }
                  } else {
                    setSelectedPlatforms(value);
                  }
                  setSelectedApplication('all');
                }}
                renderValue={(selected) => {
                  if (selected.length === 0) return <span style={{ color: '#999' }}>All Platforms</span>;
                  if (selected.length === filteredPlatformOptions.length) return 'All Platforms';
                  if (selected.length === 1) return selected[0];
                  return `${selected.length} platforms`;
                }}
                sx={{ bgcolor: 'white' }}
              >
                {/* All Platforms option */}
                <MenuItem value="__all__" sx={{ px: 1 }}>
                  <Checkbox
                    checked={selectedPlatforms.length === filteredPlatformOptions.length && filteredPlatformOptions.length > 0}
                    indeterminate={selectedPlatforms.length > 0 && selectedPlatforms.length < filteredPlatformOptions.length}
                    size="small"
                    sx={{ py: 0.5, px: 1 }}
                  />
                  <ListItemText primary={<strong>All Platforms</strong>} />
                </MenuItem>
                {filteredPlatformOptions.map(platform => (
                  <MenuItem key={platform} value={platform} sx={{ px: 1 }}>
                    <Checkbox checked={selectedPlatforms.includes(platform)} size="small" sx={{ py: 0.5, px: 1 }} />
                    <ListItemText primary={platform} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Application Dropdown */}
            <FormControl size="small" sx={{ minWidth: 250, maxWidth: 350 }}>
              <InputLabel>Select Application</InputLabel>
              <Select
                multiple
                displayEmpty
                value={typeof selectedApplication === 'string' ? (selectedApplication === 'all' ? ['__all__'] : [selectedApplication]) : selectedApplication}
                label="Select Application"
                onChange={(e) => {
                  const value = e.target.value;
                  let selectedApps = Array.isArray(value) ? value : [value];
                  
                  // Check if user just selected "__all__"
                  if (selectedApps.includes('__all__') && selectedApps.length === 1) {
                    // User clicked on "All" checkbox
                    setSelectedApplication('all');
                    return;
                  }
                  
                  // If user selected "__all__" along with other items, remove __all__ and keep others
                  if (selectedApps.includes('__all__') && selectedApps.length > 1) {
                    selectedApps = selectedApps.filter(app => app !== '__all__');
                  }
                  
                  // If all applications are selected (or empty), set to 'all'
                  if (selectedApps.length === 0 || selectedApps.length === applications.filter(app => app !== 'all').length) {
                    setSelectedApplication('all');
                  } else if (selectedApps.length === 1) {
                    const selectedApp = selectedApps[0];
                    setSelectedApplication(selectedApp);
                    
                    // Keep original logic - if a specific application is selected, adjust platform and portfolio
                    if (selectedApp !== 'all') {
                      const appIncidents = incidents.filter(incident => 
                        (incident.application || 'Unknown') === selectedApp
                      );
                      
                      if (appIncidents.length > 0) {
                        const firstIncident = appIncidents[0];
                        const platform = firstIncident.platform || 'Unknown';
                        const portfolio = firstIncident.portfolio;
                        const normalizedPortfolio = (!portfolio || 
                          portfolio.trim() === '' || 
                          portfolio.toLowerCase() === 'portfolio not defined' ||
                          portfolio.toLowerCase() === 'unknown') ? 'Other' : portfolio;
                        setSelectedPlatforms([platform]);
                        setSelectedPlatformPortfolio(normalizedPortfolio);
                      }
                    }
                  } else {
                    // For multiple selections, filter them but don't change platform/portfolio
                    const filtered = selectedApps.filter(app => app !== 'all' && app !== '__all__');
                    setSelectedApplication(filtered.length > 0 ? filtered : 'all');
                  }
                }}
                renderValue={(selected) => {
                  const items = Array.isArray(selected) ? selected : [];
                  // If contains __all__ or selectedApplication is 'all', show "All"
                  if (items.includes('__all__') || (typeof selectedApplication === 'string' && selectedApplication === 'all')) {
                    return 'All';
                  }
                  if (items.length === 0) return 'All';
                  if (items.length === 1) return items[0];
                  return `${items.length} Applications Selected`;
                }}
                sx={{ bgcolor: 'white' }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                      '& .MuiMenuItem-root': {
                        py: 0.5,
                        px: 1,
                      }
                    }
                  }
                }}
              >
                {/* All Applications Option */}
                <MenuItem value="__all__" key="__all__">
                  <Checkbox 
                    checked={typeof selectedApplication === 'string' && selectedApplication === 'all'} 
                  />
                  <ListItemText primary="All" />
                </MenuItem>
                
                {/* Individual Applications */}
                {applications.filter(app => app !== 'all').map(app => {
                  const currentValue = typeof selectedApplication === 'string' ? 
                    (selectedApplication === 'all' ? ['__all__'] : [selectedApplication]) : 
                    (Array.isArray(selectedApplication) ? selectedApplication : []);
                  const isChecked = currentValue.includes(app);
                  
                  return (
                    <MenuItem key={app} value={app}>
                      <Checkbox checked={isChecked} />
                      <ListItemText primary={app} />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            {/* Time Range Dropdown */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={selectedTimeRange}
                label="Time Range"
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedTimeRange(value);
                  if (value === 'custom') {
                    setShowCustomDatePicker(true);
                  } else {
                    setShowCustomDatePicker(false);
                  }
                }}
                sx={{ bgcolor: 'white' }}
              >
                <MenuItem value="7d">Last 7 Days</MenuItem>
                <MenuItem value="30d">Last 30 Days</MenuItem>
                <MenuItem value="90d">Last 90 Days</MenuItem>
                <MenuItem value="1y">Year to Date</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>

            {/* Inline Custom Date Range Picker */}
            {showCustomDatePicker && (
              <>
                <TextField
                  label="Start Date"
                  type="date"
                  size="small"
                  value={customDateRange.start}
                  onChange={(e) => {
                    const newStart = e.target.value;
                    setCustomDateRange({ 
                      start: newStart, 
                      end: customDateRange.end && new Date(customDateRange.end) >= new Date(newStart) 
                        ? customDateRange.end 
                        : '' 
                    });
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{ 
                    bgcolor: 'white', 
                    minWidth: 150,
                    '& .MuiInputBase-root': {
                      fontSize: '0.875rem'
                    },
                    '& input[type="date"]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      display: 'none'
                    },
                    '& input[type="date"]::-webkit-calendar-picker-indicator': {
                      cursor: 'pointer'
                    },
                    '& input[type="date"]': {
                      color: 'rgba(0, 0, 0, 0.87) !important',
                      WebkitTextFillColor: 'rgba(0, 0, 0, 0.87) !important'
                    }
                  }}
                />
                <TextField
                  label="End Date"
                  type="date"
                  size="small"
                  value={customDateRange.end}
                  onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min: customDateRange.start || undefined,
                    max: new Date().toISOString().split('T')[0]
                  }}
                  disabled={!customDateRange.start}
                  sx={{ 
                    bgcolor: 'white', 
                    minWidth: 150,
                    '& .MuiInputBase-root': {
                      fontSize: '0.875rem'
                    },
                    '& input[type="date"]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      display: 'none'
                    },
                    '& input[type="date"]::-webkit-calendar-picker-indicator': {
                      cursor: 'pointer'
                    },
                    '& input[type="date"]': {
                      color: 'rgba(0, 0, 0, 0.87) !important',
                      WebkitTextFillColor: 'rgba(0, 0, 0, 0.87) !important'
                    },
                    '& input[type="date"]:disabled': {
                      color: 'rgba(0, 0, 0, 0.38)',
                      WebkitTextFillColor: 'rgba(0, 0, 0, 0.38)'
                    }
                  }}
                />
                <Button 
                  variant="contained" 
                  size="small"
                  onClick={() => {
                    if (customDateRange.start && customDateRange.end && customDateRange.end >= customDateRange.start) {
                      loadData();
                    }
                  }}
                  disabled={!customDateRange.start || !customDateRange.end || customDateRange.end < customDateRange.start}
                  sx={{ 
                    bgcolor: '#1976d2',
                    minWidth: 70,
                    '&:hover': {
                      bgcolor: '#1565c0'
                    },
                    '&.Mui-disabled': {
                      bgcolor: '#e0e0e0',
                      color: '#999'
                    }
                  }}
                >
                  Apply
                </Button>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={() => {
                    setShowCustomDatePicker(false);
                    setSelectedTimeRange('30d');
                    setCustomDateRange({ start: '', end: '' });
                  }}
                  sx={{ 
                    borderColor: '#ccc',
                    color: '#666',
                    minWidth: 70,
                    '&:hover': {
                      borderColor: '#999',
                      bgcolor: '#f5f5f5'
                    }
                  }}
                >
                  Cancel
                </Button>
                {customDateRange.start && customDateRange.end && customDateRange.end < customDateRange.start && (
                  <Alert severity="error" sx={{ fontSize: '12px', py: 0.5, px: 1 }}>
                    End date cannot be earlier than start date
                  </Alert>
                )}
              </>
            )}
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* Share Link Button */}
            <MUITooltip title="Generate and copy a shareable link with current filter selections" arrow>
              <Button
                variant="outlined"
                startIcon={<Share />}
                onClick={generateShareLink}
                sx={{ 
                  borderColor: '#0067b3',
                  color: '#0067b3',
                  '&:hover': {
                    borderColor: '#004c8c',
                    backgroundColor: '#f0f7ff'
                  }
                }}
              >
                Share Link
              </Button>
            </MUITooltip>

            {/* AI Insights Button */}
            <MUITooltip title="Generate AI-powered incident insights and recommendations" arrow>
              <span>
                <Button
                  variant="outlined"
                  startIcon={aiInsightsLoading ? <SmartToy className="animate-spin" /> : <AnalyticsIcon />}
                  onClick={fetchAIInsights}
                  disabled={aiInsightsLoading}
                  sx={{ 
                    borderColor: '#6366f1',
                    color: '#6366f1',
                    '&:hover': {
                      borderColor: '#4f46e5',
                      backgroundColor: 'rgba(99, 102, 241, 0.04)',
                    },
                    '&.Mui-disabled': {
                      borderColor: '#d1d5db',
                      color: '#9ca3af',
                    }
                  }}
                >
                  {aiInsightsLoading ? 'Analyzing...' : 'AI Insights'}
                </Button>
              </span>
            </MUITooltip>
          </Box>
        </Box>

      {/* Trend View Toggle */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
        <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#666' }}>
          Trend Comparison:
        </Typography>
        <Button
          variant={trendViewMode === 'weekly' ? 'contained' : 'outlined'}
          size="small"
          onClick={() => setTrendViewMode('weekly')}
          sx={{
            fontSize: '0.75rem',
            py: 0.5,
            px: 1.5,
            minWidth: 'auto',
            bgcolor: trendViewMode === 'weekly' ? '#0067b3' : 'transparent',
            borderColor: '#0067b3',
            color: trendViewMode === 'weekly' ? '#fff' : '#0067b3',
            '&:hover': {
              bgcolor: trendViewMode === 'weekly' ? '#004c8c' : '#f0f7ff',
              borderColor: '#004c8c'
            }
          }}
        >
          Weekly
        </Button>
        <Button
          variant={trendViewMode === 'monthly' ? 'contained' : 'outlined'}
          size="small"
          onClick={() => setTrendViewMode('monthly')}
          sx={{
            fontSize: '0.75rem',
            py: 0.5,
            px: 1.5,
            minWidth: 'auto',
            bgcolor: trendViewMode === 'monthly' ? '#0067b3' : 'transparent',
            borderColor: '#0067b3',
            color: trendViewMode === 'monthly' ? '#fff' : '#0067b3',
            '&:hover': {
              bgcolor: trendViewMode === 'monthly' ? '#004c8c' : '#f0f7ff',
              borderColor: '#004c8c'
            }
          }}
        >
          Monthly
        </Button>
      </Box>

      {/* Summary Cards - Compact Grouped Layout */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {/* P1/P2 Critical & High Priority Section */}
        <Box sx={{ 
          flex: 1,
          minWidth: '500px',
          p: 2, 
          bgcolor: '#fff', 
          border: '1px solid #ffcdd2',
          borderLeft: '4px solid #d32f2f',
          borderRadius: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            {/* Section Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ErrorIcon sx={{ color: '#d32f2f', fontSize: 18 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#d32f2f', fontSize: '0.9rem' }}>
                P1/P2 - Critical & High Priority
              </Typography>
            </Box>
            
            {/* View Mode Toggle */}
            <Box sx={{ display: 'flex', gap: 0.5, bgcolor: '#f5f5f5', borderRadius: 1, p: 0.5 }}>
              <Button
                size="small"
                variant={p1p2ViewMode === 'caused' ? 'contained' : 'text'}
                onClick={() => setP1P2ViewMode('caused')}
                sx={{
                  fontSize: '0.7rem',
                  px: 1.5,
                  py: 0.5,
                  minWidth: 'auto',
                  textTransform: 'none',
                  bgcolor: p1p2ViewMode === 'caused' ? '#d32f2f' : 'transparent',
                  color: p1p2ViewMode === 'caused' ? '#fff' : '#666',
                  '&:hover': {
                    bgcolor: p1p2ViewMode === 'caused' ? '#b71c1c' : '#e0e0e0'
                  }
                }}
              >
                Caused by App
              </Button>
              <Button
                size="small"
                variant={p1p2ViewMode === 'impacted' ? 'contained' : 'text'}
                onClick={() => setP1P2ViewMode('impacted')}
                sx={{
                  fontSize: '0.7rem',
                  px: 1.5,
                  py: 0.5,
                  minWidth: 'auto',
                  textTransform: 'none',
                  bgcolor: p1p2ViewMode === 'impacted' ? '#d32f2f' : 'transparent',
                  color: p1p2ViewMode === 'impacted' ? '#fff' : '#666',
                  '&:hover': {
                    bgcolor: p1p2ViewMode === 'impacted' ? '#b71c1c' : '#e0e0e0'
                  }
                }}
              >
                Affected App
              </Button>
            </Box>
          </Box>
          
          {/* Metrics Section */}
          <Box sx={{ mt: 2 }}>
            {/* Metrics in a row */}
            <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Total Incidents */}
              <Box 
                sx={{ 
                  textAlign: 'center',
                  cursor: 'pointer',
                  p: 1,
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: '#ffebee'
                  }
                }}
                onClick={() => {
                  const p1p2Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P1' || inc.priority === 'P2');
                  // Sort by resolved date (most recent first)
                  const sortedIncidents = [...p1p2Incidents].sort((a, b) => {
                    if (!a.resolvedDate) return 1;
                    if (!b.resolvedDate) return -1;
                    return new Date(b.resolvedDate).getTime() - new Date(a.resolvedDate).getTime();
                  });
                  setDialogIncidents(sortedIncidents);
                  setDialogPriority('p1p2');
                  setDialogTitle('P1/P2 - Critical & High Priority Incidents');
                  setDialogPage(0);
                  setIncidentDialogOpen(true);
                }}
              >
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', display: 'block', mb: 0.5 }}>
                  Total Incidents
                </Typography>
                <MUITooltip title={(() => {
                  const p1p2Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P1' || inc.priority === 'P2');
                  const p1Count = p1p2Incidents.filter(inc => inc.priority === 'P1').length;
                  const p2Count = p1p2Incidents.filter(inc => inc.priority === 'P2').length;
                  return `P1: ${p1Count} | P2: ${p2Count}`;
                })()}>
                  <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#d32f2f' }}>
                    {viewModeFilteredIncidents.filter(inc => inc.priority === 'P1' || inc.priority === 'P2').length}
                  </Typography>
                </MUITooltip>
                {/* Trend Indicator */}
                {p1p2TrendData?.trends && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 0.5 }}>
                    {(() => {
                      const trends = trendViewMode === 'weekly' ? p1p2TrendData.trends.weekly : p1p2TrendData.trends.monthly;
                      const comparison = trends?.comparison;
                      if (!comparison) return null;
                      
                      const isIncrease = comparison.incident_change > 0;
                      const isDecrease = comparison.incident_change < 0;
                      const changeText = comparison.incident_display_text || 'No change';
                      
                      return (
                        <>
                          {isIncrease && <TrendingUp sx={{ fontSize: 14, color: '#d32f2f' }} />}
                          {isDecrease && <TrendingDown sx={{ fontSize: 14, color: '#4caf50' }} />}
                          <Typography variant="caption" sx={{ 
                            fontSize: '0.65rem', 
                            color: isIncrease ? '#d32f2f' : isDecrease ? '#4caf50' : '#666',
                            fontWeight: 500
                          }}>
                            {changeText}
                          </Typography>
                        </>
                      );
                    })()}
                  </Box>
                )}
              </Box>

              {/* % Alert */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', display: 'block', mb: 0.5 }}>
                  % Captured by Alert
                </Typography>
                <MUITooltip title={(() => {
                  const p1p2Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P1' || inc.priority === 'P2');
                  const p1p2Captured = p1p2Incidents.filter(inc => inc.capturedByAlert === true);
                  return `${p1p2Captured.length} of ${p1p2Incidents.length} incidents captured by alerts`;
                })()}>
                  <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#d32f2f', cursor: 'pointer' }}>
                    {(() => {
                      const p1p2Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P1' || inc.priority === 'P2');
                      const p1p2Captured = p1p2Incidents.filter(inc => inc.capturedByAlert === true);
                      if (p1p2Incidents.length === 0) return "N/A";
                      const percentage = (p1p2Captured.length / p1p2Incidents.length) * 100;
                      return isNaN(percentage) ? "N/A" : `${Math.round(percentage)}%`;
                    })()}
                  </Typography>
                </MUITooltip>
              </Box>

              {/* % Caused by Change */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', display: 'block', mb: 0.5 }}>
                  % Caused by Change
                </Typography>
                <MUITooltip title={(() => {
                  const p1p2Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P1' || inc.priority === 'P2');
                  const p1p2CausedByChange = p1p2Incidents.filter(inc => inc.causedByChange === true);
                  return `${p1p2CausedByChange.length} of ${p1p2Incidents.length} incidents caused by change`;
                })()}>
                  <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#d32f2f', cursor: 'pointer' }}>
                    {(() => {
                      const p1p2Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P1' || inc.priority === 'P2');
                      const p1p2CausedByChange = p1p2Incidents.filter(inc => inc.causedByChange === true);
                      if (p1p2Incidents.length === 0) return "N/A";
                      const percentage = (p1p2CausedByChange.length / p1p2Incidents.length) * 100;
                      return isNaN(percentage) ? "N/A" : `${Math.round(percentage)}%`;
                    })()}
                  </Typography>
                </MUITooltip>
              </Box>

              {/* MTTD */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', display: 'block', mb: 0.5 }}>
                  MTTD (Avg)
                </Typography>
                <MUITooltip title={(() => {
                  const p1p2Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P1' || inc.priority === 'P2');
                  const incidentsWithMTTD = p1p2Incidents.filter(inc => inc.mttd !== undefined);
                  if (incidentsWithMTTD.length === 0) return 'MTTD data not available';
                  const avgMTTD = incidentsWithMTTD.reduce((sum, inc) => sum + (inc.mttd || 0), 0) / incidentsWithMTTD.length;
                  const hours = Math.floor(avgMTTD / 3600);
                  const mins = Math.round((avgMTTD % 3600) / 60);
                  return `Average MTTD: ${hours}h ${mins}m (${incidentsWithMTTD.length} of ${p1p2Incidents.length} incidents)`;
                })()}>
                  <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#d32f2f', cursor: 'pointer' }}>
                    {(() => {
                      const p1p2Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P1' || inc.priority === 'P2');
                      const incidentsWithMTTD = p1p2Incidents.filter(inc => inc.mttd !== undefined);
                      if (mttrLoading) return <CircularProgress size={20} sx={{ color: '#d32f2f' }} />;
                      if (incidentsWithMTTD.length === 0) return 'N/A';
                      const avgMTTD = incidentsWithMTTD.reduce((sum, inc) => sum + (inc.mttd || 0), 0) / incidentsWithMTTD.length;
                      const hours = Math.floor(avgMTTD / 3600);
                      const mins = Math.round((avgMTTD % 3600) / 60);
                      return `${hours}h ${mins}m`;
                    })()}
                  </Typography>
                </MUITooltip>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#999' }}>
                  {(() => {
                    const p1p2Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P1' || inc.priority === 'P2');
                    const incidentsWithMTTD = p1p2Incidents.filter(inc => inc.mttd !== undefined);
                    if (mttrLoading) return 'Loading...';
                    return `${incidentsWithMTTD.length} of ${p1p2Incidents.length} incidents`;
                  })()}
                </Typography>
              </Box>

              {/* MTTR */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', display: 'block', mb: 0.5 }}>
                  MTTR (Avg)
                </Typography>
                <MUITooltip title={(() => {
                  const p1p2Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P1' || inc.priority === 'P2');
                  const incidentsWithMTTR = p1p2Incidents.filter(inc => inc.mttr !== undefined);
                  if (incidentsWithMTTR.length === 0) return 'MTTR data not available';
                  const avgMTTR = incidentsWithMTTR.reduce((sum, inc) => sum + (inc.mttr || 0), 0) / incidentsWithMTTR.length;
                  const hours = Math.floor(avgMTTR / 3600);
                  const mins = Math.round((avgMTTR % 3600) / 60);
                  return `Average MTTR: ${hours}h ${mins}m (${incidentsWithMTTR.length} of ${p1p2Incidents.length} incidents)`;
                })()}>
                  <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#d32f2f', cursor: 'pointer' }}>
                    {(() => {
                      const p1p2Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P1' || inc.priority === 'P2');
                      const incidentsWithMTTR = p1p2Incidents.filter(inc => inc.mttr !== undefined);
                      if (mttrLoading) return <CircularProgress size={20} sx={{ color: '#d32f2f' }} />;
                      if (incidentsWithMTTR.length === 0) return 'N/A';
                      const avgMTTR = incidentsWithMTTR.reduce((sum, inc) => sum + (inc.mttr || 0), 0) / incidentsWithMTTR.length;
                      const hours = Math.floor(avgMTTR / 3600);
                      const mins = Math.round((avgMTTR % 3600) / 60);
                      return `${hours}h ${mins}m`;
                    })()}
                  </Typography>
                </MUITooltip>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#999' }}>
                  {(() => {
                    const p1p2Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P1' || inc.priority === 'P2');
                    const incidentsWithMTTR = p1p2Incidents.filter(inc => inc.mttr !== undefined);
                    if (mttrLoading) return 'Loading...';
                    return `${incidentsWithMTTR.length} of ${p1p2Incidents.length} incidents`;
                  })()}
                </Typography>
              </Box>

              {/* MTTD */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', display: 'block', mb: 0.5 }}>
                  MTTD (Avg)
                </Typography>
                <MUITooltip title={(() => {
                  const p1p2Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P1' || inc.priority === 'P2');
                  const incidentsWithMTTD = p1p2Incidents.filter(inc => {
                    const mttd = mttdData.get(inc.number);
                    return mttd !== undefined && mttd > 0;
                  });
                  if (incidentsWithMTTD.length === 0) return 'MTTD data not available';
                  const avgMTTD = incidentsWithMTTD.reduce((sum, inc) => sum + (mttdData.get(inc.number) || 0), 0) / incidentsWithMTTD.length;
                  const hours = Math.floor(avgMTTD / 3600);
                  const mins = Math.round((avgMTTD % 3600) / 60);
                  return `Average MTTD: ${hours}h ${mins}m (${incidentsWithMTTD.length} of ${p1p2Incidents.length} incidents)`;
                })()}>
                  <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#d32f2f', cursor: 'pointer' }}>
                    {(() => {
                      const p1p2Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P1' || inc.priority === 'P2');
                      const incidentsWithMTTD = p1p2Incidents.filter(inc => {
                        const mttd = mttdData.get(inc.number);
                        return mttd !== undefined && mttd > 0;
                      });
                      if (mttrLoading) return <CircularProgress size={20} sx={{ color: '#d32f2f' }} />;
                      if (incidentsWithMTTD.length === 0) return 'N/A';
                      const avgMTTD = incidentsWithMTTD.reduce((sum, inc) => sum + (mttdData.get(inc.number) || 0), 0) / incidentsWithMTTD.length;
                      const hours = Math.floor(avgMTTD / 3600);
                      const mins = Math.round((avgMTTD % 3600) / 60);
                      return `${hours}h ${mins}m`;
                    })()}
                  </Typography>
                </MUITooltip>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#999' }}>
                  {(() => {
                    const p1p2Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P1' || inc.priority === 'P2');
                    const incidentsWithMTTD = p1p2Incidents.filter(inc => {
                      const mttd = mttdData.get(inc.number);
                      return mttd !== undefined && mttd > 0;
                    });
                    if (mttrLoading) return 'Loading...';
                    return `${incidentsWithMTTD.length} of ${p1p2Incidents.length} incidents`;
                  })()}
                </Typography>
              </Box>

              {/* Top Noise Maker */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', display: 'block', mb: 0.5 }}>
                  Top Incident Contributor
                </Typography>
                <MUITooltip title={(() => {
                  const p1p2Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P1' || inc.priority === 'P2');
                  const ciCounts: { [key: string]: number } = {};
                  p1p2Incidents.forEach(inc => {
                    const ci = p1p2ViewMode === 'caused' ? (inc.caused_by_app || 'Unknown') : (inc.application || 'Unknown');
                    ciCounts[ci] = (ciCounts[ci] || 0) + 1;
                  });
                  const topCI = Object.entries(ciCounts).sort((a, b) => b[1] - a[1])[0];
                  return topCI ? `${topCI[1]} incidents` : '0 incidents';
                })()}>
                  <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#d32f2f', cursor: 'pointer' }}>
                    {(() => {
                      const p1p2Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P1' || inc.priority === 'P2');
                      const ciCounts: { [key: string]: number } = {};
                      p1p2Incidents.forEach(inc => {
                        const ci = p1p2ViewMode === 'caused' ? (inc.caused_by_app || 'Unknown') : (inc.application || 'Unknown');
                        ciCounts[ci] = (ciCounts[ci] || 0) + 1;
                      });
                      const topCI = Object.entries(ciCounts).sort((a, b) => b[1] - a[1])[0];
                      return topCI ? topCI[0] : 'N/A';
                    })()}
                  </Typography>
                </MUITooltip>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#999' }}>
                  {(() => {
                    const p1p2Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P1' || inc.priority === 'P2');
                    const ciCounts: { [key: string]: number } = {};
                    p1p2Incidents.forEach(inc => {
                      const ci = p1p2ViewMode === 'caused' ? (inc.caused_by_app || 'Unknown') : (inc.application || 'Unknown');
                      ciCounts[ci] = (ciCounts[ci] || 0) + 1;
                    });
                    const topCI = Object.entries(ciCounts).sort((a, b) => b[1] - a[1])[0];
                    return topCI ? `${topCI[1]} incidents` : '0 incidents';
                  })()}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* P3/P4 Medium & Low Priority Section */}
        <Box sx={{ 
          flex: 1,
          minWidth: '500px',
          p: 2, 
          bgcolor: '#fff', 
          border: '1px solid #bbdefb',
          borderLeft: '4px solid #1976d2',
          borderRadius: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            {/* Section Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Info sx={{ color: '#1976d2', fontSize: 18 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1976d2', fontSize: '0.9rem' }}>
                P3/P4 - Medium & Low Priority
              </Typography>
            </Box>
          </Box>
          
          {/* Metrics Section */}
          <Box sx={{ mt: 2 }}>
            {/* Metrics in a row */}
            <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Total Incidents */}
              <Box 
                sx={{ 
                  textAlign: 'center',
                  cursor: 'pointer',
                  p: 1,
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  }
                }}
                onClick={() => {
                  const p3p4Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P3' || inc.priority === 'P4');
                  // Sort by resolved date (most recent first)
                  const sortedIncidents = [...p3p4Incidents].sort((a, b) => {
                    if (!a.resolvedDate) return 1;
                    if (!b.resolvedDate) return -1;
                    return new Date(b.resolvedDate).getTime() - new Date(a.resolvedDate).getTime();
                  });
                  setDialogIncidents(sortedIncidents);
                  setDialogPriority('p3p4');
                  setDialogTitle('P3/P4 - Medium & Low Priority Incidents');
                  setDialogPage(0);
                  setIncidentDialogOpen(true);
                }}
              >
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', display: 'block', mb: 0.5 }}>
                  Total Incidents
                </Typography>
                <MUITooltip title={(() => {
                  const p3Count = viewModeFilteredIncidents.filter(inc => inc.priority === 'P3').length;
                  const p4Count = viewModeFilteredIncidents.filter(inc => inc.priority === 'P4').length;
                  return `P3: ${p3Count} | P4: ${p4Count}`;
                })()}>
                  <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#1976d2' }}>
                    {viewModeFilteredIncidents.filter(inc => inc.priority === 'P3' || inc.priority === 'P4').length}
                  </Typography>
                </MUITooltip>
                {/* Trend Indicator */}
                {p3p4TrendData?.trends && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 0.5 }}>
                    {(() => {
                      const trends = trendViewMode === 'weekly' ? p3p4TrendData.trends.weekly : p3p4TrendData.trends.monthly;
                      const comparison = trends?.comparison;
                      if (!comparison) return null;
                      
                      const isIncrease = comparison.incident_change > 0;
                      const isDecrease = comparison.incident_change < 0;
                      const changeText = comparison.incident_display_text || 'No change';
                      
                      return (
                        <>
                          {isIncrease && <TrendingUp sx={{ fontSize: 14, color: '#d32f2f' }} />}
                          {isDecrease && <TrendingDown sx={{ fontSize: 14, color: '#4caf50' }} />}
                          <Typography variant="caption" sx={{ 
                            fontSize: '0.65rem', 
                            color: isIncrease ? '#d32f2f' : isDecrease ? '#4caf50' : '#666',
                            fontWeight: 500
                          }}>
                            {changeText}
                          </Typography>
                        </>
                      );
                    })()}
                  </Box>
                )}
              </Box>

              {/* % Alert */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', display: 'block', mb: 0.5 }}>
                  % Captured by Alert
                </Typography>
                <MUITooltip title={(() => {
                  const p3p4Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P3' || inc.priority === 'P4');
                  const p3p4Captured = p3p4Incidents.filter(inc => inc.capturedByAlert === true);
                  return `${p3p4Captured.length} of ${p3p4Incidents.length} incidents captured by alerts`;
                })()}>
                  <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#1976d2', cursor: 'pointer' }}>
                    {(() => {
                      const p3p4Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P3' || inc.priority === 'P4');
                      const p3p4Captured = p3p4Incidents.filter(inc => inc.capturedByAlert === true);
                      if (p3p4Incidents.length === 0) return "N/A";
                      const percentage = (p3p4Captured.length / p3p4Incidents.length) * 100;
                      return isNaN(percentage) ? "N/A" : `${Math.round(percentage)}%`;
                    })()}
                  </Typography>
                </MUITooltip>
              </Box>

              {/* % Caused by Change */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', display: 'block', mb: 0.5 }}>
                  % Caused by Change
                </Typography>
                <MUITooltip title={(() => {
                  const p3p4Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P3' || inc.priority === 'P4');
                  const p3p4CausedByChange = p3p4Incidents.filter(inc => inc.causedByChange === true);
                  return `${p3p4CausedByChange.length} of ${p3p4Incidents.length} incidents caused by change`;
                })()}>
                  <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#1976d2', cursor: 'pointer' }}>
                    {(() => {
                      const p3p4Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P3' || inc.priority === 'P4');
                      const p3p4CausedByChange = p3p4Incidents.filter(inc => inc.causedByChange === true);
                      if (p3p4Incidents.length === 0) return "N/A";
                      const percentage = (p3p4CausedByChange.length / p3p4Incidents.length) * 100;
                      return isNaN(percentage) ? "N/A" : `${Math.round(percentage)}%`;
                    })()}
                  </Typography>
                </MUITooltip>
              </Box>

              {/* MTTR */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', display: 'block', mb: 0.5 }}>
                  MTTR (Avg)
                </Typography>
                <MUITooltip title={(() => {
                  const p3p4Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P3' || inc.priority === 'P4');
                  const incidentsWithMTTR = p3p4Incidents.filter(inc => inc.mttr !== undefined);
                  if (incidentsWithMTTR.length === 0) return 'MTTR data not available';
                  const avgMTTR = incidentsWithMTTR.reduce((sum, inc) => sum + (inc.mttr || 0), 0) / incidentsWithMTTR.length;
                  const hours = Math.floor(avgMTTR / 3600);
                  const mins = Math.round((avgMTTR % 3600) / 60);
                  return `Average MTTR: ${hours}h ${mins}m (${incidentsWithMTTR.length} of ${p3p4Incidents.length} incidents)`;
                })()}>
                  <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#1976d2', cursor: 'pointer' }}>
                    {(() => {
                      const p3p4Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P3' || inc.priority === 'P4');
                      const incidentsWithMTTR = p3p4Incidents.filter(inc => inc.mttr !== undefined);
                      if (mttrLoading) return <CircularProgress size={20} sx={{ color: '#1976d2' }} />;
                      if (incidentsWithMTTR.length === 0) return 'N/A';
                      const avgMTTR = incidentsWithMTTR.reduce((sum, inc) => sum + (inc.mttr || 0), 0) / incidentsWithMTTR.length;
                      const hours = Math.floor(avgMTTR / 3600);
                      const mins = Math.round((avgMTTR % 3600) / 60);
                      return `${hours}h ${mins}m`;
                    })()}
                  </Typography>
                </MUITooltip>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#999' }}>
                  {(() => {
                    const p3p4Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P3' || inc.priority === 'P4');
                    const incidentsWithMTTR = p3p4Incidents.filter(inc => inc.mttr !== undefined);
                    if (mttrLoading) return 'Loading...';
                    return `${incidentsWithMTTR.length} of ${p3p4Incidents.length} incidents`;
                  })()}
                </Typography>
              </Box>

              {/* Top Noise Maker */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', display: 'block', mb: 0.5 }}>
                  Top Incident Contributor
                </Typography>
                <MUITooltip title={(() => {
                  const p3p4Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P3' || inc.priority === 'P4');
                  const ciCounts: { [key: string]: number } = {};
                  p3p4Incidents.forEach(inc => {
                    const ci = p3p4ViewMode === 'caused' ? (inc.caused_by_app || 'Unknown') : (inc.application || 'Unknown');
                    ciCounts[ci] = (ciCounts[ci] || 0) + 1;
                  });
                  const topCI = Object.entries(ciCounts).sort((a, b) => b[1] - a[1])[0];
                  return topCI ? `${topCI[1]} incidents` : '0 incidents';
                })()}>
                  <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#1976d2', cursor: 'pointer' }}>
                    {(() => {
                      const p3p4Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P3' || inc.priority === 'P4');
                      const ciCounts: { [key: string]: number } = {};
                      p3p4Incidents.forEach(inc => {
                        const ci = p3p4ViewMode === 'caused' ? (inc.caused_by_app || 'Unknown') : (inc.application || 'Unknown');
                        ciCounts[ci] = (ciCounts[ci] || 0) + 1;
                      });
                      const topCI = Object.entries(ciCounts).sort((a, b) => b[1] - a[1])[0];
                      return topCI ? topCI[0] : 'N/A';
                    })()}
                  </Typography>
                </MUITooltip>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#999' }}>
                  {(() => {
                    const p3p4Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P3' || inc.priority === 'P4');
                    const ciCounts: { [key: string]: number } = {};
                    p3p4Incidents.forEach(inc => {
                      const ci = p3p4ViewMode === 'caused' ? (inc.caused_by_app || 'Unknown') : (inc.application || 'Unknown');
                      ciCounts[ci] = (ciCounts[ci] || 0) + 1;
                    });
                    const topCI = Object.entries(ciCounts).sort((a, b) => b[1] - a[1])[0];
                    return topCI ? `${topCI[1]} incidents` : '0 incidents';
                  })()}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Tabbed Content Section */}
      <Paper elevation={2} sx={{ mb: 3, mt: 1 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            aria-label="incident analysis tabs"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.95rem',
                minWidth: 120,
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#1565c0',
                height: 3,
              }
            }}
          >
            <Tab 
              label="Incident Insights" 
              icon={<Info />} 
              iconPosition="start"
              sx={{ color: activeTab === 0 ? '#1565c0' : '#666' }}
            />
            <Tab 
              label="Trends & Analysis" 
              icon={<AnalyticsIcon />} 
              iconPosition="start"
              sx={{ color: activeTab === 1 ? '#1565c0' : '#666' }}
            />
            <Tab 
              label="Top Incident Contributor" 
              icon={<VolumeUp />} 
              iconPosition="start"
              sx={{ color: activeTab === 2 ? '#1565c0' : '#666' }}
            />
            <Tab 
              label="Detailed Incidents" 
              icon={<ViewModule />} 
              iconPosition="start"
              sx={{ color: activeTab === 3 ? '#1565c0' : '#666' }}
            />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box sx={{ p: 0 }}>
          {/* Tab 0: Portfolio Alert Insights */}
          {activeTab === 0 && (
            <Paper elevation={0} sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ p: 2, color: '#1565c0', fontWeight: 700 }}>
                {selectedApplication !== 'all' 
                  ? `Application Details - ${Array.isArray(selectedApplication) ? (selectedApplication.length === 1 ? selectedApplication[0] : `${selectedApplication.length} Apps`) : selectedApplication}`
                  : (selectedPlatforms.length > 0 && selectedPlatforms.length === filteredPlatformOptions.length)
                  ? 'Platform-wise Alert Insights'
                  : selectedPlatforms.length > 0
                  ? `Application-wise Alert Insights - ${selectedPlatforms.length === 1 ? selectedPlatforms[0] : `${selectedPlatforms.length} Platforms`}`
                  : selectedPlatformPortfolio !== 'all' 
                  ? `Platform-wise Alert Insights - ${selectedPlatformPortfolio}` 
                  : 'Portfolio-wise Alert Insights'
                }
              </Typography>
              <TableContainer className="scrollable-content">
                <Table stickyHeader size="small" sx={{ 
                  '& .MuiTableCell-root': {
                    padding: '8px 12px',
                    fontSize: '0.875rem',
                    lineHeight: 1.4,
                    border: '1px solid #e0e0e0'
                  },
                  '& .MuiTableCell-head': {
                    fontWeight: 600,
                    backgroundColor: '#f5f5f5',
                    color: '#333',
                    fontSize: '0.8rem'
                  },
                  '& .MuiTableRow-root': {
                    '&:hover': {
                      backgroundColor: '#f9f9f9'
                    }
                  }
                }}>
                  <TableHead>
                    {/* Grouped Header Row */}
                    <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                      <TableCell align="center" rowSpan={2} sx={{ minWidth: 120, borderRight: '2px solid #1565c0', fontWeight: 700 }}>
                        <Typography variant="body2" fontWeight={700}>
                          {(selectedPlatforms.length > 0 && selectedPlatforms.length === filteredPlatformOptions.length)
                            ? 'Platform Name'
                            : selectedApplication !== 'all' || selectedPlatforms.length > 0
                            ? 'Application Name'
                            : 'Portfolio Name'
                          }
                        </Typography>
                      </TableCell>
                      <TableCell align="center" colSpan={5} sx={{ borderRight: '2px solid #1565c0', borderBottom: '1px solid #1565c0' }}>
                        <Typography variant="body2" fontWeight={700} color="error">Major Incidents P1 MI and P2 MI</Typography>
                      </TableCell>
                      <TableCell align="center" colSpan={4} sx={{ borderBottom: '1px solid #1565c0' }}>
                        <Typography variant="body2" fontWeight={700} color="primary">Major Incidents P3 MI and P4 MI</Typography>
                      </TableCell>
                    </TableRow>
                    
                    {/* Column Headers Row */}
                    <TableRow>
                      <TableCell align="center" sx={{ minWidth: 100 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }} onClick={() => handleAlertInsightsSort('p1p2Count')}>
                          <Typography variant="body2" fontWeight={600}>Incidents</Typography>
                          {alertInsightsSortField === 'p1p2Count' && (
                            alertInsightsSortDirection === 'asc' ? <ArrowUpward sx={{ ml: 0.5, fontSize: 14 }} /> : <ArrowDownward sx={{ ml: 0.5, fontSize: 14 }} />
                          )}
                          {alertInsightsSortField !== 'p1p2Count' && <Sort sx={{ ml: 0.5, fontSize: 14, opacity: 0.5 }} />}
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ minWidth: 100 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }} onClick={() => handleAlertInsightsSort('p1p2AlertCapture')}>
                          <Typography variant="body2" fontWeight={600}>%Alert</Typography>
                          {alertInsightsSortField === 'p1p2AlertCapture' && (
                            alertInsightsSortDirection === 'asc' ? <ArrowUpward sx={{ ml: 0.5, fontSize: 14 }} /> : <ArrowDownward sx={{ ml: 0.5, fontSize: 14 }} />
                          )}
                          {alertInsightsSortField !== 'p1p2AlertCapture' && <Sort sx={{ ml: 0.5, fontSize: 14, opacity: 0.5 }} />}
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ minWidth: 100 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }} onClick={() => handleAlertInsightsSort('p1p2ChangeCapture')}>
                          <Typography variant="body2" fontWeight={600}>% Caused by Change</Typography>
                          {alertInsightsSortField === 'p1p2ChangeCapture' && (
                            alertInsightsSortDirection === 'asc' ? <ArrowUpward sx={{ ml: 0.5, fontSize: 14 }} /> : <ArrowDownward sx={{ ml: 0.5, fontSize: 14 }} />
                          )}
                          {alertInsightsSortField !== 'p1p2ChangeCapture' && <Sort sx={{ ml: 0.5, fontSize: 14, opacity: 0.5 }} />}
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ minWidth: 100 }}>
                        <Typography variant="body2" fontWeight={600}>MTTD</Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ minWidth: 100 }}>
                        <Typography variant="body2" fontWeight={600}>MTTR</Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ minWidth: 100 }}>
                        <Typography variant="body2" fontWeight={600}>MTTD</Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ minWidth: 100 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }} onClick={() => handleAlertInsightsSort('p3p4Count')}>
                          <Typography variant="body2" fontWeight={600}>Incidents</Typography>
                          {alertInsightsSortField === 'p3p4Count' && (
                            alertInsightsSortDirection === 'asc' ? <ArrowUpward sx={{ ml: 0.5, fontSize: 14 }} /> : <ArrowDownward sx={{ ml: 0.5, fontSize: 14 }} />
                          )}
                          {alertInsightsSortField !== 'p3p4Count' && <Sort sx={{ ml: 0.5, fontSize: 14, opacity: 0.5 }} />}
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ minWidth: 100 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }} onClick={() => handleAlertInsightsSort('p3p4AlertCapture')}>
                          <Typography variant="body2" fontWeight={600}>%Alert</Typography>
                          {alertInsightsSortField === 'p3p4AlertCapture' && (
                            alertInsightsSortDirection === 'asc' ? <ArrowUpward sx={{ ml: 0.5, fontSize: 14 }} /> : <ArrowDownward sx={{ ml: 0.5, fontSize: 14 }} />
                          )}
                          {alertInsightsSortField !== 'p3p4AlertCapture' && <Sort sx={{ ml: 0.5, fontSize: 14, opacity: 0.5 }} />}
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ minWidth: 100 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }} onClick={() => handleAlertInsightsSort('p3p4ChangeCapture')}>
                          <Typography variant="body2" fontWeight={600}>% Caused by Change</Typography>
                          {alertInsightsSortField === 'p3p4ChangeCapture' && (
                            alertInsightsSortDirection === 'asc' ? <ArrowUpward sx={{ ml: 0.5, fontSize: 14 }} /> : <ArrowDownward sx={{ ml: 0.5, fontSize: 14 }} />
                          )}
                          {alertInsightsSortField !== 'p3p4ChangeCapture' && <Sort sx={{ ml: 0.5, fontSize: 14, opacity: 0.5 }} />}
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ minWidth: 100 }}>
                        <Typography variant="body2" fontWeight={600}>MTTR</Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dynamicInsights.map((insight, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} color="primary" sx={{ fontSize: '0.875rem' }}>
                            {'name' in insight ? insight.name : insight.portfolio}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={'p1p2Count' in insight ? insight.p1p2Count : (insight.p1 + insight.p2)}
                            color="error"
                            size="small"
                            onClick={() => {
                              const portfolioName = 'name' in insight ? insight.name : insight.portfolio;
                              const filteredDialogIncidents = viewModeFilteredIncidents.filter((inc: IncidentData) => {
                                if (inc.priority !== 'P1' && inc.priority !== 'P2') return false;
                                if (selectedApplication !== 'all') {
                                  const selectedApps = Array.isArray(selectedApplication) ? selectedApplication : [selectedApplication];
                                  return selectedApps.includes(inc.application || 'Unknown');
                                } else if (selectedPlatforms.length > 0 && selectedPlatforms.length === filteredPlatformOptions.length) {
                                  return inc.platform === portfolioName;
                                } else if (selectedPlatforms.length > 0) {
                                  return inc.application === portfolioName;
                                } else if (selectedPlatformPortfolio !== 'all') {
                                  return inc.platform === portfolioName;
                                } else {
                                  if (portfolioName === 'Other') {
                                    return !inc.portfolio || inc.portfolio.trim() === '' ||
                                      inc.portfolio.toLowerCase() === 'portfolio not defined' ||
                                      inc.portfolio.toLowerCase() === 'unknown';
                                  }
                                  return inc.portfolio === portfolioName;
                                }
                              });
                              // Sort by resolved date (most recent first)
                              const sortedIncidents = [...filteredDialogIncidents].sort((a, b) => {
                                if (!a.resolvedDate) return 1;
                                if (!b.resolvedDate) return -1;
                                return new Date(b.resolvedDate).getTime() - new Date(a.resolvedDate).getTime();
                              });
                              setDialogIncidents(sortedIncidents);
                              setDialogPriority('p1p2');
                              setDialogTitle(`P1/P2 Incidents - ${portfolioName}`);
                              setDialogPage(0);
                              setIncidentDialogOpen(true);
                            }}
                            sx={{ minWidth: 40, height: 24, fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={600} color="success.main" sx={{ fontSize: '0.8rem' }}>
                            {(() => {
                              const value = 'p1p2AlertPercentage' in insight ? insight.p1p2AlertPercentage : insight.p1p2AlertCapturePercent;
                              if (value === undefined || value === null) return "N/A";
                              return typeof value === 'string' ? value : `${value}%`;
                            })()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={600} color="info.main" sx={{ fontSize: '0.8rem' }}>
                            {(() => {
                              const value = 'p1p2ChangePercentage' in insight ? insight.p1p2ChangePercentage : insight.p1p2ChangeCapturePercent;
                              return typeof value === 'string' ? value : `${value}%`;
                            })()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                            {(() => {
                              const portfolioName = 'name' in insight ? insight.name : insight.portfolio;
                              const p1p2Incidents = viewModeFilteredIncidents.filter(inc => {
                                if (inc.priority !== 'P1' && inc.priority !== 'P2') return false;
                                if (selectedApplication !== 'all') return inc.application === portfolioName;
                                if (selectedPlatforms.length > 0 && selectedPlatforms.length === filteredPlatformOptions.length) return inc.platform === portfolioName;
                                if (selectedPlatforms.length > 0) return inc.application === portfolioName;
                                if (selectedPlatformPortfolio !== 'all') return inc.platform === portfolioName;
                                if (portfolioName === 'Other') return !inc.portfolio || inc.portfolio.trim() === '' || inc.portfolio.toLowerCase() === 'portfolio not defined' || inc.portfolio.toLowerCase() === 'unknown';
                                return inc.portfolio === portfolioName;
                              });
                              if (p1p2Incidents.length === 0) return 'N/A';
                              const incidentsWithMTTD = p1p2Incidents.filter(inc => inc.mttd !== undefined && inc.mttd > 0);
                              if (incidentsWithMTTD.length === 0) return 'N/A';
                              const avgMTTD = incidentsWithMTTD.reduce((sum, inc) => sum + (inc.mttd || 0), 0) / incidentsWithMTTD.length;
                              const hours = Math.floor(avgMTTD / 3600);
                              const mins = Math.round((avgMTTD % 3600) / 60);
                              return `${hours}h ${mins}m`;
                            })()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                            {(() => {
                              const portfolioName = 'name' in insight ? insight.name : insight.portfolio;
                              const p1p2Incidents = viewModeFilteredIncidents.filter(inc => {
                                if (inc.priority !== 'P1' && inc.priority !== 'P2') return false;
                                if (selectedApplication !== 'all') return inc.application === portfolioName;
                                if (selectedPlatforms.length > 0 && selectedPlatforms.length === filteredPlatformOptions.length) return inc.platform === portfolioName;
                                if (selectedPlatforms.length > 0) return inc.application === portfolioName;
                                if (selectedPlatformPortfolio !== 'all') return inc.platform === portfolioName;
                                if (portfolioName === 'Other') return !inc.portfolio || inc.portfolio.trim() === '' || inc.portfolio.toLowerCase() === 'portfolio not defined' || inc.portfolio.toLowerCase() === 'unknown';
                                return inc.portfolio === portfolioName;
                              });
                              if (p1p2Incidents.length === 0) return 'N/A';
                              const incidentsWithMTTR = p1p2Incidents.filter(inc => inc.mttr !== undefined && inc.mttr > 0);
                              if (incidentsWithMTTR.length === 0) return 'N/A';
                              const avgMTTR = incidentsWithMTTR.reduce((sum, inc) => sum + (inc.mttr || 0), 0) / incidentsWithMTTR.length;
                              const hours = Math.floor(avgMTTR / 3600);
                              const mins = Math.round((avgMTTR % 3600) / 60);
                              return `${hours}h ${mins}m`;
                            })()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                            {(() => {
                              const portfolioName = 'name' in insight ? insight.name : insight.portfolio;
                              const p1p2Incidents = viewModeFilteredIncidents.filter(inc => {
                                if (inc.priority !== 'P1' && inc.priority !== 'P2') return false;
                                if (selectedApplication !== 'all') return inc.application === portfolioName;
                                if (selectedPlatforms.length > 0 && selectedPlatforms.length === filteredPlatformOptions.length) return inc.platform === portfolioName;
                                if (selectedPlatforms.length > 0) return inc.application === portfolioName;
                                if (selectedPlatformPortfolio !== 'all') return inc.platform === portfolioName;
                                if (portfolioName === 'Other') return !inc.portfolio || inc.portfolio.trim() === '' || inc.portfolio.toLowerCase() === 'portfolio not defined' || inc.portfolio.toLowerCase() === 'unknown';
                                return inc.portfolio === portfolioName;
                              });
                              if (p1p2Incidents.length === 0) return 'N/A';
                              const incidentsWithMTTD = p1p2Incidents.filter(inc => {
                                const mttd = mttdData.get(inc.number);
                                return mttd !== undefined && mttd > 0;
                              });
                              if (incidentsWithMTTD.length === 0) return 'N/A';
                              const avgMTTD = incidentsWithMTTD.reduce((sum, inc) => sum + (mttdData.get(inc.number) || 0), 0) / incidentsWithMTTD.length;
                              const hours = Math.floor(avgMTTD / 3600);
                              const mins = Math.round((avgMTTD % 3600) / 60);
                              return `${hours}h ${mins}m`;
                            })()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={'p3p4Count' in insight ? insight.p3p4Count : (insight.p3 + insight.p4)}
                            color="primary"
                            size="small"
                            onClick={() => {
                              const portfolioName = 'name' in insight ? insight.name : insight.portfolio;
                              const filteredDialogIncidents = viewModeFilteredIncidents.filter((inc: IncidentData) => {
                                if (inc.priority !== 'P3' && inc.priority !== 'P4') return false;
                                if (selectedApplication !== 'all') {
                                  const selectedApps = Array.isArray(selectedApplication) ? selectedApplication : [selectedApplication];
                                  return selectedApps.includes(inc.application || 'Unknown');
                                } else if (selectedPlatforms.length > 0 && selectedPlatforms.length === filteredPlatformOptions.length) {
                                  return inc.platform === portfolioName;
                                } else if (selectedPlatforms.length > 0) {
                                  return inc.application === portfolioName;
                                } else if (selectedPlatformPortfolio !== 'all') {
                                  return inc.platform === portfolioName;
                                } else {
                                  if (portfolioName === 'Other') {
                                    return !inc.portfolio || inc.portfolio.trim() === '' ||
                                      inc.portfolio.toLowerCase() === 'portfolio not defined' ||
                                      inc.portfolio.toLowerCase() === 'unknown';
                                  }
                                  return inc.portfolio === portfolioName;
                                }
                              });
                              // Sort by resolved date (most recent first)
                              const sortedIncidents = [...filteredDialogIncidents].sort((a, b) => {
                                if (!a.resolvedDate) return 1;
                                if (!b.resolvedDate) return -1;
                                return new Date(b.resolvedDate).getTime() - new Date(a.resolvedDate).getTime();
                              });
                              setDialogIncidents(sortedIncidents);
                              setDialogPriority('p3p4');
                              setDialogTitle(`P3/P4 Incidents - ${portfolioName}`);
                              setDialogPage(0);
                              setIncidentDialogOpen(true);
                            }}
                            sx={{ minWidth: 40, height: 24, fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={600} color="success.main" sx={{ fontSize: '0.8rem' }}>
                            {(() => {
                              const value = 'p3p4AlertPercentage' in insight ? insight.p3p4AlertPercentage : insight.p3p4AlertCapturePercent;
                              if (value === undefined || value === null) return "N/A";
                              return typeof value === 'string' ? value : `${value}%`;
                            })()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={600} color="warning.main" sx={{ fontSize: '0.8rem' }}>
                            {(() => {
                              const value = 'p3p4ChangePercentage' in insight ? insight.p3p4ChangePercentage : insight.p3p4ChangeCapturePercent;
                              return typeof value === 'string' ? value : `${value}%`;
                            })()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                            {(() => {
                              const portfolioName = 'name' in insight ? insight.name : insight.portfolio;
                              const p3p4Incidents = viewModeFilteredIncidents.filter(inc => {
                                if (inc.priority !== 'P3' && inc.priority !== 'P4') return false;
                                if (selectedApplication !== 'all') return inc.application === portfolioName;
                                if (selectedPlatforms.length > 0 && selectedPlatforms.length === filteredPlatformOptions.length) return inc.platform === portfolioName;
                                if (selectedPlatforms.length > 0) return inc.application === portfolioName;
                                if (selectedPlatformPortfolio !== 'all') return inc.platform === portfolioName;
                                if (portfolioName === 'Other') return !inc.portfolio || inc.portfolio.trim() === '' || inc.portfolio.toLowerCase() === 'portfolio not defined' || inc.portfolio.toLowerCase() === 'unknown';
                                return inc.portfolio === portfolioName;
                              });
                              if (p3p4Incidents.length === 0) return 'N/A';
                              const incidentsWithMTTR = p3p4Incidents.filter(inc => inc.mttr !== undefined && inc.mttr > 0);
                              if (incidentsWithMTTR.length === 0) return 'N/A';
                              const avgMTTR = incidentsWithMTTR.reduce((sum, inc) => sum + (inc.mttr || 0), 0) / incidentsWithMTTR.length;
                              const hours = Math.floor(avgMTTR / 3600);
                              const mins = Math.round((avgMTTR % 3600) / 60);
                              return `${hours}h ${mins}m`;
                            })()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    
                    {/* Total Row */}
                    {dynamicInsights.length > 0 && (
                      <TableRow sx={{ backgroundColor: '#f5f5f5', borderTop: '2px solid #1565c0' }}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.875rem' }}>
                            Total
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={dynamicInsights.reduce((sum, insight) => 
                              sum + ('p1p2Count' in insight ? insight.p1p2Count : (insight.p1 + insight.p2)), 0
                            )}
                            color="error"
                            size="small"
                            onClick={() => {
                              const incidents = viewModeFilteredIncidents.filter(inc => 
                                inc.priority === 'P1' || inc.priority === 'P2'
                              );
                              // Sort by resolved date (most recent first)
                              const sortedIncidents = [...incidents].sort((a, b) => {
                                if (!a.resolvedDate) return 1;
                                if (!b.resolvedDate) return -1;
                                return new Date(b.resolvedDate).getTime() - new Date(a.resolvedDate).getTime();
                              });
                              setDialogIncidents(sortedIncidents);
                              setDialogPriority('p1p2');
                              setDialogTitle('All P1/P2 Incidents');
                              setDialogPage(0);
                              setIncidentDialogOpen(true);
                            }}
                            sx={{ minWidth: 40, height: 24, fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>
                            {(() => {
                              const totalP1P2 = dynamicInsights.reduce((sum, insight) => 
                                sum + ('p1p2Count' in insight ? insight.p1p2Count : (insight.p1 + insight.p2)), 0
                              );
                              const totalP1P2Alert = viewModeFilteredIncidents.filter(inc => 
                                (inc.priority === 'P1' || inc.priority === 'P2') && inc.capturedByAlert === true
                              ).length;
                              return totalP1P2 > 0 ? `${Math.round((totalP1P2Alert / totalP1P2) * 100)}%` : 'N/A';
                            })()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>
                            {(() => {
                              const totalP1P2 = dynamicInsights.reduce((sum, insight) => 
                                sum + ('p1p2Count' in insight ? insight.p1p2Count : (insight.p1 + insight.p2)), 0
                              );
                              const totalP1P2Change = viewModeFilteredIncidents.filter(inc => 
                                (inc.priority === 'P1' || inc.priority === 'P2') && inc.causedByChange === true
                              ).length;
                              return totalP1P2 > 0 ? `${Math.round((totalP1P2Change / totalP1P2) * 100)}%` : 'N/A';
                            })()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>
                            {(() => {
                              const p1p2Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P1' || inc.priority === 'P2');
                              const incidentsWithMTTD = p1p2Incidents.filter(inc => inc.mttd !== undefined);
                              if (incidentsWithMTTD.length === 0) return 'N/A';
                              const avgMTTD = incidentsWithMTTD.reduce((sum, inc) => sum + (inc.mttd || 0), 0) / incidentsWithMTTD.length;
                              const hours = Math.floor(avgMTTD / 3600);
                              const mins = Math.round((avgMTTD % 3600) / 60);
                              return `${hours}h ${mins}m`;
                            })()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>
                            {(() => {
                              const p1p2Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P1' || inc.priority === 'P2');
                              const incidentsWithMTTR = p1p2Incidents.filter(inc => inc.mttr !== undefined);
                              if (incidentsWithMTTR.length === 0) return 'N/A';
                              const avgMTTR = incidentsWithMTTR.reduce((sum, inc) => sum + (inc.mttr || 0), 0) / incidentsWithMTTR.length;
                              const hours = Math.floor(avgMTTR / 3600);
                              const mins = Math.round((avgMTTR % 3600) / 60);
                              return `${hours}h ${mins}m`;
                            })()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={dynamicInsights.reduce((sum, insight) => 
                              sum + ('p3p4Count' in insight ? insight.p3p4Count : (insight.p3 + insight.p4)), 0
                            )}
                            color="primary"
                            size="small"
                            onClick={() => {
                              const incidents = viewModeFilteredIncidents.filter(inc => 
                                inc.priority === 'P3' || inc.priority === 'P4'
                              );
                              // Sort by resolved date (most recent first)
                              const sortedIncidents = [...incidents].sort((a, b) => {
                                if (!a.resolvedDate) return 1;
                                if (!b.resolvedDate) return -1;
                                return new Date(b.resolvedDate).getTime() - new Date(a.resolvedDate).getTime();
                              });
                              setDialogIncidents(sortedIncidents);
                              setDialogPriority('p3p4');
                              setDialogTitle('All P3/P4 Incidents');
                              setDialogPage(0);
                              setIncidentDialogOpen(true);
                            }}
                            sx={{ minWidth: 40, height: 24, fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>
                            {(() => {
                              const totalP3P4 = dynamicInsights.reduce((sum, insight) => 
                                sum + ('p3p4Count' in insight ? insight.p3p4Count : (insight.p3 + insight.p4)), 0
                              );
                              const totalP3P4Alert = viewModeFilteredIncidents.filter(inc => 
                                (inc.priority === 'P3' || inc.priority === 'P4') && inc.capturedByAlert === true
                              ).length;
                              return totalP3P4 > 0 ? `${Math.round((totalP3P4Alert / totalP3P4) * 100)}%` : 'N/A';
                            })()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>
                            {(() => {
                              const totalP3P4 = dynamicInsights.reduce((sum, insight) => 
                                sum + ('p3p4Count' in insight ? insight.p3p4Count : (insight.p3 + insight.p4)), 0
                              );
                              const totalP3P4Change = viewModeFilteredIncidents.filter(inc => 
                                (inc.priority === 'P3' || inc.priority === 'P4') && inc.causedByChange === true
                              ).length;
                              return totalP3P4 > 0 ? `${Math.round((totalP3P4Change / totalP3P4) * 100)}%` : 'N/A';
                            })()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>
                            {(() => {
                              const p3p4Incidents = viewModeFilteredIncidents.filter(inc => inc.priority === 'P3' || inc.priority === 'P4');
                              const incidentsWithMTTR = p3p4Incidents.filter(inc => inc.mttr !== undefined);
                              if (incidentsWithMTTR.length === 0) return 'N/A';
                              const avgMTTR = incidentsWithMTTR.reduce((sum, inc) => sum + (inc.mttr || 0), 0) / incidentsWithMTTR.length;
                              const hours = Math.floor(avgMTTR / 3600);
                              const mins = Math.round((avgMTTR % 3600) / 60);
                              return `${hours}h ${mins}m`;
                            })()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    
                    {(selectedPortfolio === 'all' 
                      ? portfolioInsights 
                      : portfolioInsights.filter(p => p.portfolio === selectedPortfolio)
                    ).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          <Typography variant="body2" color="textSecondary">
                            {selectedPortfolio === 'all' 
                              ? 'No portfolio data available.' 
                              : `No incidents found for portfolio: ${selectedPortfolio}`
                            }
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Tab 1: Monthly Trends & Analysis */}
          {activeTab === 1 && (
            <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: 3, 
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
              borderRadius: 2,
              border: '1px solid #e3f2fd'
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 700, 
                  color: '#1565c0',
                  fontFamily: "'Maersk Text', 'Inter', 'Segoe UI', Arial, sans-serif",
                  mb: 1
                }}
              >
                {trendViewMode === 'weekly' ? 'Weekly' : trendViewMode === 'monthly' ? 'Monthly' : trendViewMode === 'quarterly' ? 'Quarterly' : 'Yearly'} Incident Trends & Noise Analysis
              </Typography>
              {(monthlyComparison.length > 0 || weeklyComparison.length > 0) && (
                <>
                  <Typography 
                    variant="body2" 
                    color="textSecondary" 
                    sx={{ 
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#666'
                    }}
                  >
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="textSecondary" 
                    sx={{ 
                      fontSize: '0.8rem',
                      fontWeight: 400,
                      color: '#888',
                      fontStyle: 'italic',
                      mt: 0.5
                    }}
                  >
                  </Typography>
                </>
              )}
            </Box>

            {/* View Toggle and Priority Filter Controls */}
            {(monthlyComparison.length > 0 || weeklyComparison.length > 0) && (
              <Box sx={{ 
                mb: 3, 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' },
                gap: 3,
                alignItems: { xs: 'flex-start', md: 'center' },
                justifyContent: 'flex-start',
                p: 2,
                bgcolor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e3f2fd'
              }}>
                {/* Period Type Filter */}
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel sx={{ fontWeight: 600, color: '#1565c0' }}>Period Type</InputLabel>
                  <Select
                    value={trendViewMode}
                    onChange={(e) => setTrendViewMode(e.target.value as 'weekly' | 'monthly' | 'quarterly' | 'yearly')}
                    label="Period Type"
                    sx={{
                      bgcolor: 'white',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1565c0'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0d47a1'
                      }
                    }}
                  >
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="quarterly">Quarterly</MenuItem>
                    <MenuItem value="yearly">Yearly</MenuItem>
                  </Select>
                </FormControl>

                {/* KPI Display (Incident Count only) */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1565c0' }}>
                    KPI:
                  </Typography>
                  <Chip label="Incident Count" color="primary" size="small" sx={{ fontWeight: 600 }} />
                </Box>
              </Box>
            )}

            {/* P1/P2 Incidents Table */}
            {(monthlyComparison.length > 0 || weeklyComparison.length > 0) && (
              <Paper elevation={3} sx={{ overflow: 'auto', mt: 3, mb: 4, border: '2px solid #000' }}>
                <Box sx={{ p: 2, backgroundColor: '#f5f7fa', borderBottom: '2px solid #000' }}>
                  <Typography variant="h6" sx={{ color: '#000', fontWeight: 700, fontSize: '1rem' }}>
                    P1/P2 Incidents
                  </Typography>
                </Box>
                <TableContainer sx={{ maxHeight: 'calc(100vh - 350px)' }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      {/* Header Row 1: Timeline and Period Labels */}
                      <TableRow>
                        <TableCell
                          sx={{
                            position: 'sticky',
                            left: 0,
                            backgroundColor: '#e0e0e0',
                            color: '#000',
                            zIndex: 999,
                            borderRight: '2px solid #000',
                            fontWeight: 700,
                            minWidth: 150,
                            fontSize: '0.875rem',
                            textAlign: 'center'
                          }}
                        >
                          Timeline
                        </TableCell>
                        {trendPeriodsToDisplay.map((period, idx) => {
                          return (
                            <TableCell
                              key={period}
                              align="center"
                              sx={{
                                backgroundColor: '#e0e0e0',
                                color: '#000',
                                borderRight: idx < trendPeriodsToDisplay.length - 1 ? '2px solid #000' : 'none',
                                fontWeight: 700,
                                fontSize: '0.875rem'
                              }}
                            >
                              <MUITooltip title={getPeriodDateRange(period)} arrow>
                                <span>{formatTrendPeriod(period)}</span>
                              </MUITooltip>
                            </TableCell>
                          );
                        })}
                      </TableRow>

                      {/* Header Row 2: Metrics and KPI Sub-columns */}
                      <TableRow>
                        <TableCell
                          sx={{
                            position: 'sticky',
                            left: 0,
                            backgroundColor: '#e0e0e0',
                            color: '#000',
                            zIndex: 999,
                            borderRight: '2px solid #000',
                            fontWeight: 700,
                            minWidth: 150,
                            fontSize: '0.875rem',
                            textAlign: 'center'
                          }}
                        >
                          Metrics
                        </TableCell>
                        {trendPeriodsToDisplay.map((period, idx) => {
                          const kpisToDisplay = getTrendKPIsToDisplay();
                          return (
                            <TableCell
                              key={`kpi-${period}`}
                              align="center"
                              sx={{
                                backgroundColor: '#e0e0e0',
                                color: '#000',
                                borderRight: idx < trendPeriodsToDisplay.length - 1 ? '2px solid #000' : 'none',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                minWidth: 80
                              }}
                            >
                              {kpisToDisplay[0]}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {(() => {
                        const target = getTrendTarget();

                        // Calculate aggregated actual totals for all periods
                        const calculateAggregatedTotals = () => {
                          const totals: { [period: string]: { incidentCount: number; totalMTTRSeconds: number; lostHours: number } } = {};
                          
                          trendPeriodsToDisplay.forEach(period => {
                            totals[period] = { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };
                          });

                          // Calculate totals for P1/P2 incidents only
                          const p1p2IncidentsForTotal = filteredIncidents.filter(r => 
                            r.priority === 'P1' || r.priority === 'P2'
                          );
                          
                          p1p2IncidentsForTotal.forEach(r => {
                            const date = new Date(r.createdDate);
                            let periodKey: string = '';
                            
                            if (trendViewMode === 'weekly') {
                              const weekNum = getTrendISOWeek(date);
                              const weekYear = getTrendISOWeekYear(date);
                              periodKey = `${weekYear}-W${String(weekNum).padStart(2, '0')}`;
                            } else {
                              periodKey = getTrendMonthString(date);
                            }

                            if (totals[periodKey]) {
                              totals[periodKey].incidentCount++;
                              
                              if (r.mttr && r.mttr > 0) {
                                totals[periodKey].totalMTTRSeconds += r.mttr;
                                totals[periodKey].lostHours += r.mttr / 3600;
                              } else if (r.outage_duration && r.outage_duration > 0) {
                                const seconds = r.outage_duration * 60;
                                totals[periodKey].totalMTTRSeconds += seconds;
                                totals[periodKey].lostHours += r.outage_duration / 60;
                              }
                            }
                          });

                          return totals;
                        };

                        const aggregatedTotals = calculateAggregatedTotals();

                        // Render hierarchy rows function
                        const renderTrendHierarchyRows = (nodes: TrendHierarchyNode[], depth: number = 0, parentPath: string = ''): JSX.Element[] => {
                          const rows: JSX.Element[] = [];

                          nodes.forEach((node, idx) => {
                            const nodePath = parentPath ? `${parentPath}/${node.name}` : node.name;
                            const hasChildren = node.children && node.children.length > 0;
                            const isExpanded = expandedTrendNodes.has(nodePath);

                            const indent = depth * 24;
                            let icon = null;
                            if (hasChildren) {
                              icon = isExpanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />;
                            }

                            const bgColor = depth === 0 ? 'white' : depth === 1 ? '#fafafa' : '#f5f5f5';

                            rows.push(
                              <TableRow key={nodePath} sx={{ backgroundColor: bgColor, '&:hover': { backgroundColor: '#e8f4fd' } }}>
                                {/* Name column with hierarchy */}
                                <TableCell
                                  sx={{
                                    position: 'sticky',
                                    left: 0,
                                    backgroundColor: bgColor,
                                    zIndex: 1,
                                    borderRight: '2px solid #000',
                                    fontWeight: depth === 0 ? 700 : depth === 1 ? 600 : 500,
                                    fontSize: depth === 0 ? '0.9rem' : '0.8rem',
                                    paddingLeft: `${8 + indent}px`,
                                    minWidth: 150,
                                    cursor: hasChildren ? 'pointer' : 'default',
                                    whiteSpace: 'nowrap'
                                  }}
                                  onClick={() => hasChildren && toggleTrendNode(nodePath)}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {icon}
                                    {!hasChildren && <Box sx={{ width: 16 }} />}
                                    <span>{node.name}</span>
                                  </Box>
                                </TableCell>

                                {/* Period columns with actual values */}
                                {trendPeriodsToDisplay.map((period, periodIdx) => {
                                  const m = node.metrics[period] || { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };

                                  return (
                                    <React.Fragment key={`${period}-${nodePath}`}>
                                      {/* Count */}
                                      <TableCell
                                        align="center"
                                        sx={{
                                          borderRight: periodIdx < trendPeriodsToDisplay.length - 1 ? '2px solid #000' : 'none',
                                          fontSize: '0.875rem',
                                          minWidth: 80,
                                          fontWeight: m.incidentCount > 0 ? 600 : 400,
                                          color: '#000',
                                          cursor: m.incidentCount > 0 ? 'pointer' : 'default',
                                          '&:hover': m.incidentCount > 0 ? {
                                            backgroundColor: '#e3f2fd',
                                            textDecoration: 'underline'
                                          } : {}
                                        }}
                                        onClick={() => {
                                          if (m.incidentCount > 0) {
                                            const periodIncidents = node.records.filter(r => {
                                              const date = new Date(r.createdDate);
                                              return getTrendPeriodKey(date) === period;
                                            });
                                            setSelectedPeriodIncidents(periodIncidents);
                                            setIncidentDetailsOpen(true);
                                          }
                                        }}
                                      >
                                        {m.incidentCount > 0 ? m.incidentCount : ''}
                                      </TableCell>
                                    </React.Fragment>
                                  );
                                })}
                              </TableRow>
                            );

                            if (isExpanded && hasChildren) {
                              rows.push(...renderTrendHierarchyRows(node.children!, depth + 1, nodePath));
                            }
                          });
                          return rows;
                        };

                        return (
                          <>
                            {/* TARGET Row */}
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                              <TableCell
                                sx={{
                                  position: 'sticky',
                                  left: 0,
                                  backgroundColor: '#f5f5f5',
                                  zIndex: 1,
                                  borderRight: '2px solid #000',
                                  fontWeight: 700,
                                  fontSize: '0.875rem'
                                }}
                              >
                                TARGET
                              </TableCell>
                              {trendPeriodsToDisplay.map((period, idx) => (
                                <TableCell key={`target-${period}`} align="center" sx={{ borderRight: idx < trendPeriodsToDisplay.length - 1 ? '2px solid #000' : 'none', fontWeight: 600, fontSize: '0.75rem' }}>
                                  {target.incidentCount}
                                </TableCell>
                              ))}
                            </TableRow>

                            {/* ACTUAL Row */}
                            <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                              <TableCell
                                sx={{
                                  position: 'sticky',
                                  left: 0,
                                  backgroundColor: '#e0e0e0',
                                  zIndex: 1,
                                  borderRight: '2px solid #000',
                                  fontWeight: 700,
                                  fontSize: '0.875rem'
                                }}
                              >
                                ACTUAL
                              </TableCell>
                              {trendPeriodsToDisplay.map((period, idx) => {
                                const actual = aggregatedTotals[period] || { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };

                                const countColor = getTrendCellColor(actual.incidentCount, target.incidentCount);

                                return (
                                  <TableCell 
                                    key={`actual-${period}`}
                                    align="center" 
                                    sx={{ 
                                      borderRight: idx < trendPeriodsToDisplay.length - 1 ? '2px solid #000' : 'none', 
                                      fontWeight: 700, 
                                      fontSize: '0.875rem',
                                      backgroundColor: countColor,
                                      color: 'white',
                                      cursor: actual.incidentCount > 0 ? 'pointer' : 'default',
                                      '&:hover': actual.incidentCount > 0 ? {
                                        opacity: 0.8,
                                        textDecoration: 'underline'
                                      } : {}
                                    }}
                                    onClick={() => {
                                      if (actual.incidentCount > 0) {
                                        const periodIncidents = filteredIncidents.filter(r => {
                                          if (r.priority !== 'P1' && r.priority !== 'P2') return false;
                                          const date = new Date(r.createdDate);
                                          return getTrendPeriodKey(date) === period;
                                        });
                                        setSelectedPeriodIncidents(periodIncidents);
                                        setIncidentDetailsOpen(true);
                                      }
                                    }}
                                  >
                                    {actual.incidentCount}
                                  </TableCell>
                                );
                              })}
                            </TableRow>

                            {/* Hierarchical breakdown rows */}
                            {renderTrendHierarchyRows(p1p2HierarchyData)}
                          </>
                        );
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {/* Weekly breakdown by portfolio */}
            {trendViewMode === 'weekly' && (
              <>
            {/* Weekly P3/P4 Incidents Table - Hierarchical */}
            <Paper elevation={3} sx={{ mb: 3, overflow: 'auto', border: '2px solid #000' }}>
              <Box sx={{ p: 2, backgroundColor: '#f5f7fa', borderBottom: '2px solid #000' }}>
                <Typography variant="h6" sx={{ color: '#000', fontWeight: 700, fontSize: '1rem' }}>
                  P3/P4 Incidents
                </Typography>
              </Box>
              <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ position: 'sticky', left: 0, backgroundColor: '#e0e0e0', color: '#000', zIndex: 999, borderRight: '2px solid #000', fontWeight: 700, minWidth: 150, fontSize: '0.875rem', textAlign: 'center' }}>Timeline</TableCell>
                      {trendPeriodsToDisplay.map((period, idx) => (
                        <TableCell key={period} align="center" sx={{ backgroundColor: '#e0e0e0', color: '#000', borderRight: idx < trendPeriodsToDisplay.length - 1 ? '2px solid #000' : 'none', fontWeight: 700, fontSize: '0.875rem' }}>
                          <MUITooltip title={getPeriodDateRange(period)} arrow>
                            <span>{formatTrendPeriod(period)}</span>
                          </MUITooltip>
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ position: 'sticky', left: 0, backgroundColor: '#e0e0e0', color: '#000', zIndex: 999, borderRight: '2px solid #000', fontWeight: 700, minWidth: 150, fontSize: '0.875rem', textAlign: 'center' }}>Metrics</TableCell>
                      {trendPeriodsToDisplay.map((period, idx) => (
                        <TableCell key={`kpi-${period}`} align="center" sx={{ backgroundColor: '#e0e0e0', color: '#000', borderRight: idx < trendPeriodsToDisplay.length - 1 ? '2px solid #000' : 'none', fontWeight: 600, fontSize: '0.75rem', minWidth: 80 }}>
                          Count
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      // Calculate P3/P4 aggregated totals
                      const calculateP3P4Totals = () => {
                        const totals: { [period: string]: { incidentCount: number; totalMTTRSeconds: number; lostHours: number } } = {};
                        trendPeriodsToDisplay.forEach(period => {
                          totals[period] = { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };
                        });
                        
                        const p3p4IncidentsForTotal = filteredIncidents.filter(r => 
                          r.priority === 'P3' || r.priority === 'P4'
                        );
                        
                        p3p4IncidentsForTotal.forEach(r => {
                          const date = new Date(r.createdDate);
                          const periodKey = getTrendPeriodKey(date);
                          if (totals[periodKey]) {
                            totals[periodKey].incidentCount++;
                            if (r.mttr && r.mttr > 0) {
                              totals[periodKey].totalMTTRSeconds += r.mttr;
                              totals[periodKey].lostHours += r.mttr / 3600;
                            } else if (r.outage_duration && r.outage_duration > 0) {
                              const seconds = r.outage_duration * 60;
                              totals[periodKey].totalMTTRSeconds += seconds;
                              totals[periodKey].lostHours += r.outage_duration / 60;
                            }
                          }
                        });
                        return totals;
                      };

                      const p3p4Totals = calculateP3P4Totals();

                      // Render P3/P4 hierarchy rows
                      const renderP3P4HierarchyRows = (nodes: TrendHierarchyNode[], depth: number = 0, parentPath: string = ''): JSX.Element[] => {
                        const rows: JSX.Element[] = [];
                        nodes.forEach((node) => {
                          const nodePath = parentPath ? `${parentPath}/${node.name}` : node.name;
                          const hasChildren = node.children && node.children.length > 0;
                          const isExpanded = expandedTrendNodes.has(nodePath);
                          const indent = depth * 24;
                          let icon = null;
                          if (hasChildren) {
                            icon = isExpanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />;
                          }
                          const bgColor = depth === 0 ? 'white' : depth === 1 ? '#fafafa' : '#f5f5f5';

                          rows.push(
                            <TableRow key={nodePath} sx={{ backgroundColor: bgColor, '&:hover': { backgroundColor: '#e8f4fd' } }}>
                              <TableCell sx={{ position: 'sticky', left: 0, backgroundColor: bgColor, zIndex: 1, borderRight: '2px solid #000', fontWeight: depth === 0 ? 700 : depth === 1 ? 600 : 500, fontSize: depth === 0 ? '0.9rem' : '0.8rem', paddingLeft: `${8 + indent}px`, minWidth: 250, cursor: hasChildren ? 'pointer' : 'default', whiteSpace: 'nowrap' }} onClick={() => hasChildren && toggleTrendNode(nodePath)}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {icon}
                                  {!hasChildren && <Box sx={{ width: 16 }} />}
                                  <span>{node.name}</span>
                                </Box>
                              </TableCell>
                              {trendPeriodsToDisplay.map((period, periodIdx) => {
                                const m = node.metrics[period] || { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };
                                return (
                                  <TableCell 
                                    key={`${period}-${nodePath}`}
                                    align="center" 
                                    sx={{ 
                                      borderRight: periodIdx < trendPeriodsToDisplay.length - 1 ? '2px solid #000' : 'none', 
                                      fontSize: '0.875rem', 
                                      minWidth: 80, 
                                      fontWeight: m.incidentCount > 0 ? 600 : 400, 
                                      color: '#000',
                                      cursor: m.incidentCount > 0 ? 'pointer' : 'default',
                                      '&:hover': m.incidentCount > 0 ? {
                                        backgroundColor: '#e3f2fd',
                                        textDecoration: 'underline'
                                      } : {}
                                    }}
                                    onClick={() => {
                                      if (m.incidentCount > 0) {
                                        const periodIncidents = node.records.filter(r => {
                                          const date = new Date(r.createdDate);
                                          return getTrendPeriodKey(date) === period;
                                        });
                                        setSelectedPeriodIncidents(periodIncidents);
                                        setIncidentDetailsOpen(true);
                                      }
                                    }}
                                  >
                                    {m.incidentCount > 0 ? m.incidentCount : ''}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          );
                          if (isExpanded && hasChildren) {
                            rows.push(...renderP3P4HierarchyRows(node.children!, depth + 1, nodePath));
                          }
                        });
                        return rows;
                      };

                      return (
                        <>{/* Hierarchical breakdown rows */}
                          {renderP3P4HierarchyRows(p3p4HierarchyData)}
                          
                          {/* Grand Total Row */}
                          <TableRow sx={{ backgroundColor: '#e0e6eb', borderTop: '2px solid #001a35' }}>
                            <TableCell sx={{ position: 'sticky', left: 0, backgroundColor: '#e0e6eb', zIndex: 1, borderRight: '2px solid #000', fontWeight: 700, fontSize: '0.875rem', color: '#001a35' }}>
                              Grand Total
                            </TableCell>
                            {trendPeriodsToDisplay.map((period, idx) => {
                              const total = p3p4Totals[period] || { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };
                              const prevPeriod = idx > 0 ? trendPeriodsToDisplay[idx - 1] : null;
                              const prevTotal = prevPeriod ? (p3p4Totals[prevPeriod] || { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 }) : null;
                              const change = prevTotal !== null ? total.incidentCount - prevTotal.incidentCount : null;
                              
                              return (
                                <TableCell key={`total-${period}`} align="center" sx={{ borderRight: idx < trendPeriodsToDisplay.length - 1 ? '2px solid #000' : 'none', fontWeight: 700, fontSize: '0.875rem', color: '#0067b3' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                    {total.incidentCount}
                                    {change !== null && change !== 0 && (
                                      change < 0 ? (
                                        <TrendingDown sx={{ fontSize: 16, color: '#10b981' }} />
                                      ) : (
                                        <TrendingUp sx={{ fontSize: 16, color: '#0067b3' }} />
                                      )
                                    )}
                                  </Box>
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        </>
                      );
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
              </>
            )}

            {/* Monthly breakdown by portfolio */}
            {trendViewMode === 'monthly' && (
              <>
            {/* Monthly P3/P4 Incidents Table - Hierarchical */}
            <Paper elevation={3} sx={{ mb: 3, overflow: 'auto', border: '2px solid #000' }}>
              <Box sx={{ p: 2, backgroundColor: '#f5f7fa', borderBottom: '2px solid #000' }}>
                <Typography variant="h6" sx={{ color: '#000', fontWeight: 700, fontSize: '1rem' }}>
                  P3/P4 Incidents
                </Typography>
              </Box>
              <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ position: 'sticky', left: 0, backgroundColor: '#e0e0e0', color: '#000', zIndex: 999, borderRight: '2px solid #000', fontWeight: 700, minWidth: 150, fontSize: '0.875rem', textAlign: 'center' }}>Timeline</TableCell>
                      {trendPeriodsToDisplay.map((period, idx) => (
                        <TableCell key={period} align="center" sx={{ backgroundColor: '#e0e0e0', color: '#000', borderRight: idx < trendPeriodsToDisplay.length - 1 ? '2px solid #000' : 'none', fontWeight: 700, fontSize: '0.875rem' }}>
                          <MUITooltip title={getPeriodDateRange(period)} arrow>
                            <span>{formatTrendPeriod(period)}</span>
                          </MUITooltip>
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ position: 'sticky', left: 0, backgroundColor: '#e0e0e0', color: '#000', zIndex: 999, borderRight: '2px solid #000', fontWeight: 700, minWidth: 150, fontSize: '0.875rem', textAlign: 'center' }}>Metrics</TableCell>
                      {trendPeriodsToDisplay.map((period, idx) => (
                        <TableCell key={`kpi-${period}`} align="center" sx={{ backgroundColor: '#e0e0e0', color: '#000', borderRight: idx < trendPeriodsToDisplay.length - 1 ? '2px solid #000' : 'none', fontWeight: 600, fontSize: '0.75rem', minWidth: 80 }}>
                          Count
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      // Calculate P3/P4 aggregated totals
                      const calculateP3P4Totals = () => {
                        const totals: { [period: string]: { incidentCount: number; totalMTTRSeconds: number; lostHours: number } } = {};
                        trendPeriodsToDisplay.forEach(period => {
                          totals[period] = { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };
                        });
                        
                        const p3p4IncidentsForTotal = filteredIncidents.filter(r => 
                          r.priority === 'P3' || r.priority === 'P4'
                        );
                        
                        p3p4IncidentsForTotal.forEach(r => {
                          const date = new Date(r.createdDate);
                          const periodKey = getTrendPeriodKey(date);
                          if (totals[periodKey]) {
                            totals[periodKey].incidentCount++;
                            if (r.mttr && r.mttr > 0) {
                              totals[periodKey].totalMTTRSeconds += r.mttr;
                              totals[periodKey].lostHours += r.mttr / 3600;
                            } else if (r.outage_duration && r.outage_duration > 0) {
                              const seconds = r.outage_duration * 60;
                              totals[periodKey].totalMTTRSeconds += seconds;
                              totals[periodKey].lostHours += r.outage_duration / 60;
                            }
                          }
                        });
                        return totals;
                      };

                      const p3p4Totals = calculateP3P4Totals();

                      // Render P3/P4 hierarchy rows
                      const renderP3P4HierarchyRows = (nodes: TrendHierarchyNode[], depth: number = 0, parentPath: string = ''): JSX.Element[] => {
                        const rows: JSX.Element[] = [];
                        nodes.forEach((node) => {
                          const nodePath = parentPath ? `${parentPath}/${node.name}` : node.name;
                          const hasChildren = node.children && node.children.length > 0;
                          const isExpanded = expandedTrendNodes.has(nodePath);
                          const indent = depth * 24;
                          let icon = null;
                          if (hasChildren) {
                            icon = isExpanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />;
                          }
                          const bgColor = depth === 0 ? 'white' : depth === 1 ? '#fafafa' : '#f5f5f5';

                          rows.push(
                            <TableRow key={nodePath} sx={{ backgroundColor: bgColor, '&:hover': { backgroundColor: '#e8f4fd' } }}>
                              <TableCell sx={{ position: 'sticky', left: 0, backgroundColor: bgColor, zIndex: 1, borderRight: '2px solid #000', fontWeight: depth === 0 ? 700 : depth === 1 ? 600 : 500, fontSize: depth === 0 ? '0.9rem' : '0.8rem', paddingLeft: `${8 + indent}px`, minWidth: 250, cursor: hasChildren ? 'pointer' : 'default', whiteSpace: 'nowrap' }} onClick={() => hasChildren && toggleTrendNode(nodePath)}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {icon}
                                  {!hasChildren && <Box sx={{ width: 16 }} />}
                                  <span>{node.name}</span>
                                </Box>
                              </TableCell>
                              {trendPeriodsToDisplay.map((period, periodIdx) => {
                                const m = node.metrics[period] || { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };
                                return (
                                  <TableCell 
                                    key={`${period}-${nodePath}`}
                                    align="center" 
                                    sx={{ 
                                      borderRight: periodIdx < trendPeriodsToDisplay.length - 1 ? '2px solid #000' : 'none', 
                                      fontSize: '0.875rem', 
                                      minWidth: 80, 
                                      fontWeight: m.incidentCount > 0 ? 600 : 400, 
                                      color: '#000',
                                      cursor: m.incidentCount > 0 ? 'pointer' : 'default',
                                      '&:hover': m.incidentCount > 0 ? {
                                        backgroundColor: '#e3f2fd',
                                        textDecoration: 'underline'
                                      } : {}
                                    }}
                                    onClick={() => {
                                      if (m.incidentCount > 0) {
                                        const periodIncidents = node.records.filter(r => {
                                          const date = new Date(r.createdDate);
                                          return getTrendPeriodKey(date) === period;
                                        });
                                        setSelectedPeriodIncidents(periodIncidents);
                                        setIncidentDetailsOpen(true);
                                      }
                                    }}
                                  >
                                    {m.incidentCount > 0 ? m.incidentCount : ''}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          );
                          if (isExpanded && hasChildren) {
                            rows.push(...renderP3P4HierarchyRows(node.children!, depth + 1, nodePath));
                          }
                        });
                        return rows;
                      };

                      return (
                        <>{/* Hierarchical breakdown rows */}
                          {renderP3P4HierarchyRows(p3p4HierarchyData)}
                          
                          {/* Grand Total Row */}
                          <TableRow sx={{ backgroundColor: '#e0e6eb', borderTop: '2px solid #001a35' }}>
                            <TableCell sx={{ position: 'sticky', left: 0, backgroundColor: '#e0e6eb', zIndex: 1, borderRight: '2px solid #000', fontWeight: 700, fontSize: '0.875rem', color: '#001a35' }}>
                              Grand Total
                            </TableCell>
                            {trendPeriodsToDisplay.map((period, idx) => {
                              const total = p3p4Totals[period] || { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };
                              const prevPeriod = idx > 0 ? trendPeriodsToDisplay[idx - 1] : null;
                              const prevTotal = prevPeriod ? (p3p4Totals[prevPeriod] || { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 }) : null;
                              const change = prevTotal !== null ? total.incidentCount - prevTotal.incidentCount : null;
                              
                              return (
                                <TableCell key={`total-${period}`} align="center" sx={{ borderRight: idx < trendPeriodsToDisplay.length - 1 ? '2px solid #000' : 'none', fontWeight: 700, fontSize: '0.875rem', color: '#0067b3' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                    {total.incidentCount}
                                    {change !== null && change !== 0 && (
                                      change < 0 ? (
                                        <TrendingDown sx={{ fontSize: 16, color: '#10b981' }} />
                                      ) : (
                                        <TrendingUp sx={{ fontSize: 16, color: '#0067b3' }} />
                                      )
                                    )}
                                  </Box>
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        </>
                      );
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
              </>
            )}

            {/* Quarterly breakdown by portfolio */}
            {trendViewMode === 'quarterly' && (
              <>
            {/* Quarterly P3/P4 Incidents Table - Hierarchical */}
            <Paper elevation={3} sx={{ mb: 3, overflow: 'auto', border: '2px solid #000' }}>
              <Box sx={{ p: 2, backgroundColor: '#f5f7fa', borderBottom: '2px solid #000' }}>
                <Typography variant="h6" sx={{ color: '#000', fontWeight: 700, fontSize: '1rem' }}>
                  P3/P4 Incidents
                </Typography>
              </Box>
              <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ position: 'sticky', left: 0, backgroundColor: '#e0e0e0', color: '#000', zIndex: 999, borderRight: '2px solid #000', fontWeight: 700, minWidth: 150, fontSize: '0.875rem', textAlign: 'center' }}>Timeline</TableCell>
                      {trendPeriodsToDisplay.map((period, idx) => (
                        <TableCell key={period} align="center" sx={{ backgroundColor: '#e0e0e0', color: '#000', borderRight: idx < trendPeriodsToDisplay.length - 1 ? '2px solid #000' : 'none', fontWeight: 700, fontSize: '0.875rem' }}>
                          <MUITooltip title={getPeriodDateRange(period)} arrow>
                            <span>{formatTrendPeriod(period)}</span>
                          </MUITooltip>
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ position: 'sticky', left: 0, backgroundColor: '#e0e0e0', color: '#000', zIndex: 999, borderRight: '2px solid #000', fontWeight: 700, minWidth: 150, fontSize: '0.875rem', textAlign: 'center' }}>Metrics</TableCell>
                      {trendPeriodsToDisplay.map((period, idx) => (
                        <TableCell key={`kpi-${period}`} align="center" sx={{ backgroundColor: '#e0e0e0', color: '#000', borderRight: idx < trendPeriodsToDisplay.length - 1 ? '2px solid #000' : 'none', fontWeight: 600, fontSize: '0.75rem', minWidth: 80 }}>
                          Count
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      // Calculate P3/P4 aggregated totals
                      const calculateP3P4Totals = () => {
                        const totals: { [period: string]: { incidentCount: number; totalMTTRSeconds: number; lostHours: number } } = {};
                        trendPeriodsToDisplay.forEach(period => {
                          totals[period] = { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };
                        });
                        
                        const p3p4IncidentsForTotal = filteredIncidents.filter(r => 
                          r.priority === 'P3' || r.priority === 'P4'
                        );
                        
                        p3p4IncidentsForTotal.forEach(r => {
                          const date = new Date(r.createdDate);
                          const periodKey = getTrendPeriodKey(date);
                          if (totals[periodKey]) {
                            totals[periodKey].incidentCount++;
                            if (r.mttr && r.mttr > 0) {
                              totals[periodKey].totalMTTRSeconds += r.mttr;
                              totals[periodKey].lostHours += r.mttr / 3600;
                            } else if (r.outage_duration && r.outage_duration > 0) {
                              const seconds = r.outage_duration * 60;
                              totals[periodKey].totalMTTRSeconds += seconds;
                              totals[periodKey].lostHours += r.outage_duration / 60;
                            }
                          }
                        });
                        return totals;
                      };

                      const p3p4Totals = calculateP3P4Totals();

                      // Render P3/P4 hierarchy rows
                      const renderP3P4HierarchyRows = (nodes: TrendHierarchyNode[], depth: number = 0, parentPath: string = ''): JSX.Element[] => {
                        const rows: JSX.Element[] = [];
                        nodes.forEach((node) => {
                          const nodePath = parentPath ? `${parentPath}/${node.name}` : node.name;
                          const hasChildren = node.children && node.children.length > 0;
                          const isExpanded = expandedTrendNodes.has(nodePath);
                          const indent = depth * 24;
                          let icon = null;
                          if (hasChildren) {
                            icon = isExpanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />;
                          }
                          const bgColor = depth === 0 ? 'white' : depth === 1 ? '#fafafa' : '#f5f5f5';

                          rows.push(
                            <TableRow key={nodePath} sx={{ backgroundColor: bgColor, '&:hover': { backgroundColor: '#e8f4fd' } }}>
                              <TableCell sx={{ position: 'sticky', left: 0, backgroundColor: bgColor, zIndex: 1, borderRight: '2px solid #000', fontWeight: depth === 0 ? 700 : depth === 1 ? 600 : 500, fontSize: depth === 0 ? '0.9rem' : '0.8rem', paddingLeft: `${8 + indent}px`, minWidth: 250, cursor: hasChildren ? 'pointer' : 'default', whiteSpace: 'nowrap' }} onClick={() => hasChildren && toggleTrendNode(nodePath)}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {icon}
                                  {!hasChildren && <Box sx={{ width: 16 }} />}
                                  <span>{node.name}</span>
                                </Box>
                              </TableCell>
                              {trendPeriodsToDisplay.map((period, periodIdx) => {
                                const m = node.metrics[period] || { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };
                                return (
                                  <TableCell 
                                    key={`${period}-${nodePath}`}
                                    align="center" 
                                    sx={{ 
                                      borderRight: periodIdx < trendPeriodsToDisplay.length - 1 ? '2px solid #000' : 'none', 
                                      fontSize: '0.875rem', 
                                      minWidth: 80, 
                                      fontWeight: m.incidentCount > 0 ? 600 : 400, 
                                      color: '#000',
                                      cursor: m.incidentCount > 0 ? 'pointer' : 'default',
                                      '&:hover': m.incidentCount > 0 ? {
                                        backgroundColor: '#e3f2fd',
                                        textDecoration: 'underline'
                                      } : {}
                                    }}
                                    onClick={() => {
                                      if (m.incidentCount > 0) {
                                        const periodIncidents = node.records.filter(r => {
                                          const date = new Date(r.createdDate);
                                          return getTrendPeriodKey(date) === period;
                                        });
                                        setSelectedPeriodIncidents(periodIncidents);
                                        setIncidentDetailsOpen(true);
                                      }
                                    }}
                                  >
                                    {m.incidentCount > 0 ? m.incidentCount : ''}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          );
                          if (isExpanded && hasChildren) {
                            rows.push(...renderP3P4HierarchyRows(node.children!, depth + 1, nodePath));
                          }
                        });
                        return rows;
                      };

                      return (
                        <>{/* Hierarchical breakdown rows */}
                          {renderP3P4HierarchyRows(p3p4HierarchyData)}
                          
                          {/* Grand Total Row */}
                          <TableRow sx={{ backgroundColor: '#e0e6eb', borderTop: '2px solid #001a35' }}>
                            <TableCell sx={{ position: 'sticky', left: 0, backgroundColor: '#e0e6eb', zIndex: 1, borderRight: '2px solid #000', fontWeight: 700, fontSize: '0.875rem', color: '#001a35' }}>
                              Grand Total
                            </TableCell>
                            {trendPeriodsToDisplay.map((period, idx) => {
                              const total = p3p4Totals[period] || { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };
                              const prevPeriod = idx > 0 ? trendPeriodsToDisplay[idx - 1] : null;
                              const prevTotal = prevPeriod ? (p3p4Totals[prevPeriod] || { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 }) : null;
                              const change = prevTotal !== null ? total.incidentCount - prevTotal.incidentCount : null;
                              
                              return (
                                <TableCell key={`total-${period}`} align="center" sx={{ borderRight: idx < trendPeriodsToDisplay.length - 1 ? '2px solid #000' : 'none', fontWeight: 700, fontSize: '0.875rem', color: '#0067b3' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                    {total.incidentCount}
                                    {change !== null && change !== 0 && (
                                      change < 0 ? (
                                        <TrendingDown sx={{ fontSize: 16, color: '#10b981' }} />
                                      ) : (
                                        <TrendingUp sx={{ fontSize: 16, color: '#0067b3' }} />
                                      )
                                    )}
                                  </Box>
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        </>
                      );
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
              </>
            )}

            {/* Yearly breakdown by portfolio */}
            {trendViewMode === 'yearly' && (
              <>
            {/* Yearly P3/P4 Incidents Table - Hierarchical */}
            <Paper elevation={3} sx={{ mb: 3, overflow: 'auto', border: '2px solid #000' }}>
              <Box sx={{ p: 2, backgroundColor: '#f5f7fa', borderBottom: '2px solid #000' }}>
                <Typography variant="h6" sx={{ color: '#000', fontWeight: 700, fontSize: '1rem' }}>
                  P3/P4 Incidents
                </Typography>
              </Box>
              <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ position: 'sticky', left: 0, backgroundColor: '#e0e0e0', color: '#000', zIndex: 999, borderRight: '2px solid #000', fontWeight: 700, minWidth: 150, fontSize: '0.875rem', textAlign: 'center' }}>Timeline</TableCell>
                      {trendPeriodsToDisplay.map((period, idx) => (
                        <TableCell key={period} align="center" sx={{ backgroundColor: '#e0e0e0', color: '#000', borderRight: idx < trendPeriodsToDisplay.length - 1 ? '2px solid #000' : 'none', fontWeight: 700, fontSize: '0.875rem' }}>
                          <MUITooltip title={getPeriodDateRange(period)} arrow>
                            <span>{formatTrendPeriod(period)}</span>
                          </MUITooltip>
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ position: 'sticky', left: 0, backgroundColor: '#e0e0e0', color: '#000', zIndex: 999, borderRight: '2px solid #000', fontWeight: 700, minWidth: 150, fontSize: '0.875rem', textAlign: 'center' }}>Metrics</TableCell>
                      {trendPeriodsToDisplay.map((period, idx) => (
                        <TableCell key={`kpi-${period}`} align="center" sx={{ backgroundColor: '#e0e0e0', color: '#000', borderRight: idx < trendPeriodsToDisplay.length - 1 ? '2px solid #000' : 'none', fontWeight: 600, fontSize: '0.75rem', minWidth: 80 }}>
                          Count
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      // Calculate P3/P4 aggregated totals
                      const calculateP3P4Totals = () => {
                        const totals: { [period: string]: { incidentCount: number; totalMTTRSeconds: number; lostHours: number } } = {};
                        trendPeriodsToDisplay.forEach(period => {
                          totals[period] = { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };
                        });
                        
                        const p3p4IncidentsForTotal = filteredIncidents.filter(r => 
                          r.priority === 'P3' || r.priority === 'P4'
                        );
                        
                        p3p4IncidentsForTotal.forEach(r => {
                          const date = new Date(r.createdDate);
                          const periodKey = getTrendPeriodKey(date);
                          if (totals[periodKey]) {
                            totals[periodKey].incidentCount++;
                            if (r.mttr && r.mttr > 0) {
                              totals[periodKey].totalMTTRSeconds += r.mttr;
                              totals[periodKey].lostHours += r.mttr / 3600;
                            } else if (r.outage_duration && r.outage_duration > 0) {
                              const seconds = r.outage_duration * 60;
                              totals[periodKey].totalMTTRSeconds += seconds;
                              totals[periodKey].lostHours += r.outage_duration / 60;
                            }
                          }
                        });
                        return totals;
                      };

                      const p3p4Totals = calculateP3P4Totals();

                      // Render P3/P4 hierarchy rows
                      const renderP3P4HierarchyRows = (nodes: TrendHierarchyNode[], depth: number = 0, parentPath: string = ''): JSX.Element[] => {
                        const rows: JSX.Element[] = [];
                        nodes.forEach((node) => {
                          const nodePath = parentPath ? `${parentPath}/${node.name}` : node.name;
                          const hasChildren = node.children && node.children.length > 0;
                          const isExpanded = expandedTrendNodes.has(nodePath);
                          const indent = depth * 24;
                          let icon = null;
                          if (hasChildren) {
                            icon = isExpanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />;
                          }
                          const bgColor = depth === 0 ? 'white' : depth === 1 ? '#fafafa' : '#f5f5f5';

                          rows.push(
                            <TableRow key={nodePath} sx={{ backgroundColor: bgColor, '&:hover': { backgroundColor: '#e8f4fd' } }}>
                              <TableCell sx={{ position: 'sticky', left: 0, backgroundColor: bgColor, zIndex: 1, borderRight: '2px solid #000', fontWeight: depth === 0 ? 700 : depth === 1 ? 600 : 500, fontSize: depth === 0 ? '0.9rem' : '0.8rem', paddingLeft: `${8 + indent}px`, minWidth: 250, cursor: hasChildren ? 'pointer' : 'default', whiteSpace: 'nowrap' }} onClick={() => hasChildren && toggleTrendNode(nodePath)}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {icon}
                                  {!hasChildren && <Box sx={{ width: 16 }} />}
                                  <span>{node.name}</span>
                                </Box>
                              </TableCell>
                              {trendPeriodsToDisplay.map((period, periodIdx) => {
                                const m = node.metrics[period] || { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };
                                return (
                                  <TableCell 
                                    key={`${period}-${nodePath}`}
                                    align="center" 
                                    sx={{ 
                                      borderRight: periodIdx < trendPeriodsToDisplay.length - 1 ? '2px solid #000' : 'none', 
                                      fontSize: '0.875rem', 
                                      minWidth: 80, 
                                      fontWeight: m.incidentCount > 0 ? 600 : 400, 
                                      color: '#000',
                                      cursor: m.incidentCount > 0 ? 'pointer' : 'default',
                                      '&:hover': m.incidentCount > 0 ? {
                                        backgroundColor: '#e3f2fd',
                                        textDecoration: 'underline'
                                      } : {}
                                    }}
                                    onClick={() => {
                                      if (m.incidentCount > 0) {
                                        const periodIncidents = node.records.filter(r => {
                                          const date = new Date(r.createdDate);
                                          return getTrendPeriodKey(date) === period;
                                        });
                                        setSelectedPeriodIncidents(periodIncidents);
                                        setIncidentDetailsOpen(true);
                                      }
                                    }}
                                  >
                                    {m.incidentCount > 0 ? m.incidentCount : ''}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          );
                          if (isExpanded && hasChildren) {
                            rows.push(...renderP3P4HierarchyRows(node.children!, depth + 1, nodePath));
                          }
                        });
                        return rows;
                      };

                      return (
                        <>{/* Hierarchical breakdown rows */}
                          {renderP3P4HierarchyRows(p3p4HierarchyData)}
                          
                          {/* Grand Total Row */}
                          <TableRow sx={{ backgroundColor: '#e0e6eb', borderTop: '2px solid #001a35' }}>
                            <TableCell sx={{ position: 'sticky', left: 0, backgroundColor: '#e0e6eb', zIndex: 1, borderRight: '2px solid #000', fontWeight: 700, fontSize: '0.875rem', color: '#001a35' }}>
                              Grand Total
                            </TableCell>
                            {trendPeriodsToDisplay.map((period, idx) => {
                              const total = p3p4Totals[period] || { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };
                              const prevPeriod = idx > 0 ? trendPeriodsToDisplay[idx - 1] : null;
                              const prevTotal = prevPeriod ? (p3p4Totals[prevPeriod] || { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 }) : null;
                              const change = prevTotal !== null ? total.incidentCount - prevTotal.incidentCount : null;
                              
                              return (
                                <TableCell key={`total-${period}`} align="center" sx={{ borderRight: idx < trendPeriodsToDisplay.length - 1 ? '2px solid #000' : 'none', fontWeight: 700, fontSize: '0.875rem', color: '#0067b3' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                    {total.incidentCount}
                                    {change !== null && change !== 0 && (
                                      change < 0 ? (
                                        <TrendingDown sx={{ fontSize: 16, color: '#10b981' }} />
                                      ) : (
                                        <TrendingUp sx={{ fontSize: 16, color: '#0067b3' }} />
                                      )
                                    )}
                                  </Box>
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        </>
                      );
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
          )}

          {/* Tab 2: CI Noise Makers */}
          {activeTab === 2 && (
            <Paper elevation={0} sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: '1px solid #e0e0e0' }}>
                <Typography variant="h6" sx={{ color: '#1565c0', fontWeight: 700 }}>
                  Top Incident Contributor
                </Typography>
                <Tabs
                  value={noiseMakerPriorityTab}
                  onChange={(e, newValue) => setNoiseMakerPriorityTab(newValue)}
                  sx={{
                    minHeight: 'auto',
                    '& .MuiTab-root': {
                      minHeight: 36,
                      py: 1,
                      px: 2,
                      fontSize: '0.875rem',
                      fontWeight: 600
                    }
                  }}
                >
                  <Tab 
                    label="P1/P2 Major Incidents" 
                    value="P1P2"
                    sx={{ 
                      color: '#d32f2f',
                      '&.Mui-selected': { 
                        color: '#d32f2f',
                        fontWeight: 700
                      }
                    }}
                  />
                  <Tab 
                    label="P3/P4 Major Incidents" 
                    value="P3P4"
                    sx={{ 
                      color: '#1976d2',
                      '&.Mui-selected': { 
                        color: '#1976d2',
                        fontWeight: 700
                      }
                    }}
                  />
                </Tabs>
              </Box>
        <TableContainer className="scrollable-content">
          <Table stickyHeader size="small" sx={{ 
            '& .MuiTableCell-root': {
              padding: '8px 12px',
              fontSize: '0.875rem',
              lineHeight: 1.4,
              border: '1px solid #e0e0e0'
            },
            '& .MuiTableCell-head': {
              fontWeight: 600,
              backgroundColor: '#f5f5f5',
              color: '#333',
              fontSize: '0.8rem'
            },
            '& .MuiTableRow-root': {
              '&:hover': {
                backgroundColor: '#f9f9f9'
              }
            }
          }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 150 }}>
                  <Typography variant="body2" fontWeight={600}>CI Name</Typography>
                </TableCell>
                <TableCell align="center" sx={{ minWidth: 100 }}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                    }}
                    onClick={() => handleNoiseMakersSort('alertCount')}
                  >
                    <Typography variant="body2" fontWeight={600}>Incidents Count</Typography>
                    {noiseMakersSortField === 'alertCount' && (
                      noiseMakersSortDirection === 'asc' ? 
                      <ArrowUpwardIcon sx={{ ml: 1, fontSize: 14 }} /> : 
                      <ArrowDownwardIcon sx={{ ml: 1, fontSize: 14 }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell align="center" sx={{ minWidth: 120 }}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                    }}
                    onClick={() => handleNoiseMakersSort('alertCapturePercent')}
                  >
                    <Typography variant="body2" fontWeight={600}>%Captured by Alert</Typography>
                    {noiseMakersSortField === 'alertCapturePercent' && (
                      noiseMakersSortDirection === 'asc' ? 
                      <ArrowUpwardIcon sx={{ ml: 1, fontSize: 14 }} /> : 
                      <ArrowDownwardIcon sx={{ ml: 1, fontSize: 14 }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell align="center" sx={{ minWidth: 120 }}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                    }}
                    onClick={() => handleNoiseMakersSort('causedByChangePercent')}
                  >
                    <Typography variant="body2" fontWeight={600}>%Caused by Change</Typography>
                    {noiseMakersSortField === 'causedByChangePercent' && (
                      noiseMakersSortDirection === 'asc' ? 
                      <ArrowUpwardIcon sx={{ ml: 1, fontSize: 14 }} /> : 
                      <ArrowDownwardIcon sx={{ ml: 1, fontSize: 14 }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ minWidth: 120 }}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                    }}
                    onClick={() => handleNoiseMakersSort('percentOfTotal')}
                  >
                    <Typography variant="body2" fontWeight={600}>% of Total</Typography>
                    {noiseMakersSortField === 'percentOfTotal' && (
                      noiseMakersSortDirection === 'asc' ? 
                      <ArrowUpwardIcon sx={{ ml: 1, fontSize: 14 }} /> : 
                      <ArrowDownwardIcon sx={{ ml: 1, fontSize: 14 }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ minWidth: 100 }}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                    }}
                    onClick={() => handleNoiseMakersSort('noisePercentage')}
                  >
                    <MUITooltip title="Percentage of incidents that were self-recovered (indicating potential noise/false alarms)">
                      <Typography variant="body2" fontWeight={600}>% Noise</Typography>
                    </MUITooltip>
                    {noiseMakersSortField === 'noisePercentage' && (
                      noiseMakersSortDirection === 'asc' ? 
                      <ArrowUpwardIcon sx={{ ml: 1, fontSize: 14 }} /> : 
                      <ArrowDownwardIcon sx={{ ml: 1, fontSize: 14 }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ minWidth: 120 }}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                    }}
                    onClick={() => handleNoiseMakersSort('lastIncident')}
                  >
                    <Typography variant="body2" fontWeight={600}>Last Incident</Typography>
                    {noiseMakersSortField === 'lastIncident' && (
                      noiseMakersSortDirection === 'asc' ? 
                      <ArrowUpwardIcon sx={{ ml: 1, fontSize: 14 }} /> : 
                      <ArrowDownwardIcon sx={{ ml: 1, fontSize: 14 }} />
                    )}
                  </Box>
                </TableCell>
                {noiseMakerPriorityTab === 'P1P2' && (
                  <TableCell sx={{ minWidth: 100 }}>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                      }}
                      onClick={() => handleNoiseMakersSort('avgMTTD')}
                    >
                      <Typography variant="body2" fontWeight={600}>Avg MTTD</Typography>
                      {noiseMakersSortField === 'avgMTTD' && (
                        noiseMakersSortDirection === 'asc' ? 
                        <ArrowUpwardIcon sx={{ ml: 1, fontSize: 14 }} /> : 
                        <ArrowDownwardIcon sx={{ ml: 1, fontSize: 14 }} />
                      )}
                    </Box>
                  </TableCell>
                )}
                <TableCell sx={{ minWidth: 100 }}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                    }}
                    onClick={() => handleNoiseMakersSort('avgMTTR')}
                  >
                    <Typography variant="body2" fontWeight={600}>Avg MTTR</Typography>
                    {noiseMakersSortField === 'avgMTTR' && (
                      noiseMakersSortDirection === 'asc' ? 
                      <ArrowUpwardIcon sx={{ ml: 1, fontSize: 14 }} /> : 
                      <ArrowDownwardIcon sx={{ ml: 1, fontSize: 14 }} />
                    )}
                  </Box>
                </TableCell>
                {noiseMakerPriorityTab === 'P1P2' && (
                  <TableCell sx={{ minWidth: 100 }}>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                      }}
                      onClick={() => handleNoiseMakersSort('avgMTTD')}
                    >
                      <Typography variant="body2" fontWeight={600}>Avg MTTD</Typography>
                      {noiseMakersSortField === 'avgMTTD' && (
                        noiseMakersSortDirection === 'asc' ? 
                        <ArrowUpwardIcon sx={{ ml: 1, fontSize: 14 }} /> : 
                        <ArrowDownwardIcon sx={{ ml: 1, fontSize: 14 }} />
                      )}
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {top10NoiseMakersFromData.map((ci, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <Button
                      variant="text"
                      onClick={() => handleCIClick(ci.ciName)}
                      sx={{ 
                        textTransform: 'none',
                        fontWeight: 'bold',
                        color: 'primary.main',
                        textAlign: 'left',
                        justifyContent: 'flex-start',
                        minWidth: 'auto',
                        '&:hover': {
                          textDecoration: 'underline',
                          backgroundColor: 'transparent'
                        }
                      }}
                    >
                      {ci.ciName}
                    </Button>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={ci.alertCount.toLocaleString()}
                      color="primary"
                      size="small"
                      onClick={() => {
                        setDialogIncidents(ci.incidents);
                        setDialogPriority(noiseMakerPriorityTab === 'P1P2' ? 'p1p2' : 'p3p4');
                        setDialogTitle(`Incidents - ${ci.ciName}`);
                        setDialogPage(0);
                        setIncidentDialogOpen(true);
                      }}
                      sx={{ 
                        minWidth: 40, 
                        height: 24, 
                        fontSize: '0.75rem', 
                        fontWeight: 600, 
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: noiseMakerPriorityTab === 'P3P4' ? '#1565c0' : '#c62828'
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight={600} color="success.main">
                      {ci.alertCapturePercent}%
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight={600} color="info.main">
                      {ci.causedByChangePercent}%
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={Math.min(ci.percentOfTotal * 2, 100)} 
                        sx={{ width: 60, height: 8 }}
                        color={ci.percentOfTotal > 20 ? 'error' : ci.percentOfTotal > 10 ? 'warning' : 'success'}
                      />
                      {ci.percentOfTotal}%
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={Math.min(ci.noisePercentage, 100)} 
                        sx={{ width: 60, height: 8 }}
                        color={ci.noisePercentage > 50 ? 'error' : ci.noisePercentage > 25 ? 'warning' : 'success'}
                      />
                      <Typography 
                        variant="body2" 
                        fontWeight="bold"
                        sx={{ minWidth: '45px', textAlign: 'left' }}
                      >
                        {ci.noisePercentage}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{new Date(ci.lastIncident).toLocaleDateString()}</TableCell>
                  {noiseMakerPriorityTab === 'P1P2' && (
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {(() => {
                          const incidentsWithMTTD = ci.incidents.filter(inc => inc.mttd !== undefined && inc.mttd > 0);
                          if (incidentsWithMTTD.length === 0) return 'N/A';
                          const avgMTTD = incidentsWithMTTD.reduce((sum, inc) => sum + (inc.mttd || 0), 0) / incidentsWithMTTD.length;
                          const hours = Math.floor(avgMTTD / 3600);
                          const mins = Math.round((avgMTTD % 3600) / 60);
                          return `${hours}h ${mins}m`;
                        })()}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {ci.avgMTTR !== null ? (() => {
                        const hours = Math.floor(ci.avgMTTR / 3600);
                        const mins = Math.round((ci.avgMTTR % 3600) / 60);
                        return `${hours}h ${mins}m`;
                      })() : 'N/A'}
                    </Typography>
                  </TableCell>
                  {noiseMakerPriorityTab === 'P1P2' && (
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {ci.avgMTTD !== null ? (() => {
                          const hours = Math.floor(ci.avgMTTD / 3600);
                          const mins = Math.round((ci.avgMTTD % 3600) / 60);
                          return `${hours}h ${mins}m`;
                        })() : 'N/A'}
                      </Typography>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {top10NoiseMakersFromData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={noiseMakerPriorityTab === 'P1P2' ? 9 : 8} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No {noiseMakerPriorityTab === 'P3P4' ? 'P3/P4' : 'P1/P2'} incidents found for noise analysis.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
            </Paper>
          )}

          {/* Tab 3: Detailed Incidents */}
          {activeTab === 3 && (
            <Paper elevation={0}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ color: '#1565c0', fontWeight: 700 }}>
                     Incidents Details ({searchableIncidents.length} 
                    {searchQuery ? ` of ${filteredIncidents.length}` : ''} total)
                  </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Filtered by: {selectedTimeRange}
              {selectedPlatformPortfolio !== 'all' && ` • Portfolio: ${selectedPlatformPortfolio}`}
              {selectedPlatform !== 'all' && ` • Platform: ${selectedPlatform}`}
              {selectedApplication !== 'all' && ` • App: ${Array.isArray(selectedApplication) ? (selectedApplication.length === 1 ? selectedApplication[0] : `${selectedApplication.length} Apps`) : selectedApplication}`}
              {selectedPriorityFilter.length > 0 && ` • Priority: ${selectedPriorityFilter.join(', ')}`}
              {sortField && ` • Sorted by: Created At (${sortDirection === 'asc' ? 'Oldest First' : 'Newest First'})`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search incidents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
              }}
              sx={{ 
                minWidth: 250,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                }
              }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Priority</InputLabel>
              <Select
                multiple
                value={selectedPriorityFilter}
                label="Priority"
                onChange={(e) => setSelectedPriorityFilter(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                sx={{ bgcolor: 'white' }}
                renderValue={(selected) => selected.length === 0 ? 'All Priorities' : selected.join(', ')}
              >
                <MenuItem value="P1">
                  <Checkbox checked={selectedPriorityFilter.indexOf('P1') > -1} />
                  <ListItemText primary="P1 Critical" />
                </MenuItem>
                <MenuItem value="P2">
                  <Checkbox checked={selectedPriorityFilter.indexOf('P2') > -1} />
                  <ListItemText primary="P2 High" />
                </MenuItem>
                <MenuItem value="P3">
                  <Checkbox checked={selectedPriorityFilter.indexOf('P3') > -1} />
                  <ListItemText primary="P3 Medium" />
                </MenuItem>
                <MenuItem value="P4">
                  <Checkbox checked={selectedPriorityFilter.indexOf('P4') > -1} />
                  <ListItemText primary="P4 Low" />
                </MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportToCSV}
              size="small"
              sx={{ 
                borderColor: '#0067b3', 
                color: '#0067b3',
                '&:hover': {
                  borderColor: '#004c8c',
                  backgroundColor: '#f0f7ff'
                }
              }}
            >
              Export CSV
            </Button>
          </Box>
        </Box>
        <TableContainer className="scrollable-content">
          <Table stickyHeader size="small" sx={{ 
            '& .MuiTableCell-root': {
              padding: '8px 12px',
              fontSize: '0.875rem',
              lineHeight: 1.4,
              border: '1px solid #e0e0e0'
            },
            '& .MuiTableCell-head': {
              fontWeight: 600,
              backgroundColor: '#f5f5f5',
              color: '#333',
              fontSize: '0.8rem'
            },
            '& .MuiTableRow-root': {
              '&:hover': {
                backgroundColor: '#f9f9f9'
              }
            }
          }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 100 }}>
                  <Typography variant="body2" fontWeight={600}>Incident ID</Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 200 }}>
                  <Typography variant="body2" fontWeight={600}>Description</Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 120 }}>
                  <Typography variant="body2" fontWeight={600}>Affected App</Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 140 }}>
                  <Typography variant="body2" fontWeight={600}>Affected Portfolio</Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 140 }}>
                  <Typography variant="body2" fontWeight={600}>Affected Platform</Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 160 }}>
                  <Typography variant="body2" fontWeight={600}>Caused by App</Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 140 }}>
                  <Typography variant="body2" fontWeight={600}>Caused by Platform</Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 140 }}>
                  <Typography variant="body2" fontWeight={600}>Caused by Portfolio</Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 140 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('createdAt')}>
                    <Typography variant="body2" fontWeight={600}>Created At</Typography>
                    {sortField === 'createdAt' && (
                      sortDirection === 'asc' ? <ArrowUpward sx={{ ml: 0.5, fontSize: 14 }} /> : <ArrowDownward sx={{ ml: 0.5, fontSize: 14 }} />
                    )}
                    {sortField !== 'createdAt' && <Sort sx={{ ml: 0.5, fontSize: 14, opacity: 0.5 }} />}
                  </Box>
                </TableCell>
                <TableCell sx={{ minWidth: 70 }}>
                  <Typography variant="body2" fontWeight={600}>Priority</Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 80 }}>
                  <Typography variant="body2" fontWeight={600}>Status</Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 100 }}>
                  <Typography variant="body2" fontWeight={600}>MTTD(Avg)</Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 100 }}>
                  <Typography variant="body2" fontWeight={600}>MTTR(Avg)</Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 100 }}>
                  <Typography variant="body2" fontWeight={600}>MTTD(Avg)</Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 80 }}>
                  <Typography variant="body2" fontWeight={600}>Alert</Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 80 }}>
                  <Typography variant="body2" fontWeight={600}>Change</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {searchableIncidents
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((incident, index) => (
                <TableRow key={index}>
                  {/* Incident ID */}
                  <TableCell>
                    <Link
                      href={`https://maersk.service-now.com/nav_to.do?uri=incident_list.do?sysparm_query=number=${incident.number}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ 
                        color: '#0067b3',
                        textDecoration: 'none',
                        fontWeight: 500,
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      {incident.number}
                    </Link>
                  </TableCell>
                  
                  {/* Short Description */}
                  <TableCell sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <MUITooltip title={incident.short_description || 'No description available'} arrow>
                      <span>{incident.short_description || 'N/A'}</span>
                    </MUITooltip>
                  </TableCell>
                  
                  {/* Application */}
                  <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <MUITooltip title={incident.application || 'No application specified'} arrow>
                      <span>{incident.application}</span>
                    </MUITooltip>
                  </TableCell>
                  {/* Affected Portfolio */}
                  <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <MUITooltip title={incident.portfolio || 'No portfolio specified'} arrow>
                      <span>{incident.portfolio || 'N/A'}</span>
                    </MUITooltip>
                  </TableCell>
                  {/* Affected Platform */}
                  <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <MUITooltip title={incident.platform || 'No platform specified'} arrow>
                      <span>{incident.platform || 'N/A'}</span>
                    </MUITooltip>
                  </TableCell>
                  {/* Caused by Application */}
                  <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <MUITooltip title={incident.caused_by_app || 'No caused by application'} arrow>
                      <span>{incident.caused_by_app || 'N/A'}</span>
                    </MUITooltip>
                  </TableCell>
                  {/* Caused by Platform */}
                  <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <MUITooltip title={incident.caused_by_platform || 'No platform specified'} arrow>
                      <span>{incident.caused_by_platform || 'N/A'}</span>
                    </MUITooltip>
                  </TableCell>
                  {/* Caused by Portfolio */}
                  <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <MUITooltip title={incident.caused_by_portfolio || 'No portfolio specified'} arrow>
                      <span>{incident.caused_by_portfolio || 'N/A'}</span>
                    </MUITooltip>
                  </TableCell>
                  
                  {/* Created At */}
                  <TableCell sx={{ minWidth: 150 }}>
                    {new Date(incident.createdDate).toISOString().replace('T', ' ').slice(0, -5)} UTC
                  </TableCell>
                  
                  {/* Priority */}
                  <TableCell>
                    <Chip
                      label={incident.priority}
                      sx={{ 
                        backgroundColor: getPriorityColor(incident.priority), 
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                  </TableCell>
                  
                  {/* Status */}
                  <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {incident.status}
                  </TableCell>
                  
                  {/* MTTD Duration */}
                  <TableCell>
                    {(incident.priority === 'P1' || incident.priority === 'P2') && incident.mttd !== undefined ? (
                      <MUITooltip title={`${incident.mttd.toFixed(0)} seconds (${(incident.mttd / 60).toFixed(1)} minutes)`}>
                        <span style={{ fontWeight: 600, color: '#1565c0' }}>
                          {(() => {
                            const hours = Math.floor(incident.mttd / 3600);
                            const mins = Math.round((incident.mttd % 3600) / 60);
                            return `${hours}h ${mins}m`;
                          })()}
                        </span>
                      </MUITooltip>
                    ) : (
                      <span style={{ color: '#999' }}>-</span>
                    )}
                  </TableCell>
                  
                  {/* MTTR Duration */}
                  <TableCell>
                    {incident.mttr !== undefined ? (
                      <MUITooltip title={`${incident.mttr.toFixed(0)} seconds (${(incident.mttr / 60).toFixed(1)} minutes)`}>
                        <span style={{ fontWeight: 600, color: '#1565c0' }}>
                          {(() => {
                            const hours = Math.floor(incident.mttr / 3600);
                            const mins = Math.round((incident.mttr % 3600) / 60);
                            return `${hours}h ${mins}m`;
                          })()}
                        </span>
                      </MUITooltip>
                    ) : (
                      <span style={{ color: '#999' }}>N/A</span>
                    )}
                  </TableCell>
                  
                  {/* MTTD Duration */}
                  <TableCell>
                    {(() => {
                      const mttd = mttdData.get(incident.number);
                      if (mttd !== undefined && mttd > 0) {
                        const hours = Math.floor(mttd / 3600);
                        const mins = Math.round((mttd % 3600) / 60);
                        return (
                          <MUITooltip title={`${mttd.toFixed(0)} seconds (${(mttd / 60).toFixed(1)} minutes)`}>
                            <span style={{ fontWeight: 600, color: '#e65100' }}>
                              {`${hours}h ${mins}m`}
                            </span>
                          </MUITooltip>
                        );
                      }
                      return <span style={{ color: '#999' }}>N/A</span>;
                    })()}
                  </TableCell>
                  
                  {/* Captured by Alert */}
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Box sx={{ 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      px: 1, 
                      py: 0.5, 
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      backgroundColor: incident.capturedByAlert ? '#e8f5e8' : '#fff3e0',
                      color: incident.capturedByAlert ? '#2e7d32' : '#f57c00'
                    }}>
                      {incident.capturedByAlert ? 'Yes' : 'No'}
                    </Box>
                  </TableCell>
                  
                  {/* Caused by Change */}
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {incident.causedByChange ? (
                      <MUITooltip title={`Change Number: ${incident.changeNumber}`} arrow>
                        <Link
                          href={`https://maersk.service-now.com/change_request_list.do?sysparm_query=number=${incident.changeNumber}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ textDecoration: 'none' }}
                        >
                          <Box sx={{ 
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            px: 1, 
                            py: 0.5, 
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            backgroundColor: '#fff3e0',
                            color: '#e65100',
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: '#ffe0b2'
                            }
                          }}>
                            Yes
                          </Box>
                        </Link>
                      </MUITooltip>
                    ) : (
                      <Box sx={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        px: 1, 
                        py: 0.5, 
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        backgroundColor: '#fafafa',
                        color: '#757575'
                      }}>
                        No
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {filteredIncidents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No incidents found for the selected filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={searchableIncidents.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ 
            borderTop: '1px solid #e1e5e9',
            '.MuiTablePagination-toolbar': {
              paddingLeft: 2,
              paddingRight: 2,
            }
          }}
        />
            </Paper>
          )}
        </Box>
      </Paper>

      {/* AI Insights Modal - Portfolio Style */}
      <Dialog
        open={aiInsightsOpen}
        onClose={() => setAiInsightsOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '70vh',
            maxHeight: '90vh',
            borderRadius: 2,
            boxShadow: '0 24px 38px 3px rgba(0,0,0,0.14), 0 9px 46px 8px rgba(0,0,0,0.12), 0 11px 15px -7px rgba(0,0,0,0.2)'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)', 
          background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SmartToy sx={{ fontSize: '28px' }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0 }}>
                AI Incident Analysis & Insights
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.85rem' }}>
                {selectedPortfolio || 'All Portfolios'} • {selectedTimeRange}
              </Typography>
            </Box>
          </Box>
          <IconButton 
            onClick={() => setAiInsightsOpen(false)}
            sx={{ 
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <Typography sx={{ fontSize: '20px' }}>×</Typography>
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          {aiInsightsError ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  {aiInsightsError}
                </Typography>
              </Alert>
            </Box>
          ) : aiInsightsData ? (
            <Box>
              {/* AI Insights Report - Enhanced Markdown Content */}
              <Paper 
                elevation={3}
                sx={{ 
                  backgroundColor: '#ffffff', 
                  borderRadius: 3, 
                  border: '1px solid var(--maersk-border)',
                  overflow: 'hidden',
                  position: 'relative'
                }}
              >
                {/* Content area */}
                <Box sx={{ p: 4, position: 'relative' }}>
                  {/* Floating scroll to top button */}
                  <Fab
                    size="small"
                    color="primary"
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      zIndex: 1,
                      backgroundColor: 'var(--maersk-blue)',
                      '&:hover': {
                        backgroundColor: 'var(--maersk-dark-blue)'
                      }
                    }}
                    onClick={() => {
                      const element = document.querySelector('.ai-incident-insights-content');
                      element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                  >
                    <KeyboardArrowUp />
                  </Fab>

                  <Box 
                    className="ai-incident-insights-content"
                    sx={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #dee2e6',
                      borderRadius: 2,
                      p: 4,
                      fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
                      lineHeight: 1.6,
                      maxHeight: '800px',
                      overflowY: 'auto',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}
                  >
                    {/* Create formatted content similar to portfolio insights */}
                    <ReactMarkdown
                      components={{
                        h1: ({ children }: { children?: React.ReactNode }) => (
                          <Box sx={{ mb: 4 }}>
                            <Typography 
                              variant="h4" 
                              sx={{
                                fontWeight: 700,
                                color: 'var(--maersk-dark-blue)',
                                mb: 2,
                                pb: 1.5,
                                borderBottom: '3px solid var(--maersk-blue)',
                                letterSpacing: '0.5px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                position: 'relative',
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  left: 0,
                                  bottom: -3,
                                  width: '60px',
                                  height: '3px',
                                  background: 'var(--maersk-blue)',
                                  borderRadius: '2px'
                                }
                              }}
                            >
                              {children}
                            </Typography>
                          </Box>
                        ),
                        h2: ({ children }: { children?: React.ReactNode }) => (
                          <Typography 
                            variant="h5" 
                            sx={{
                              fontWeight: 600,
                              color: '#000000',
                              mt: 4,
                              mb: 2,
                              fontSize: '1.8em',
                              pb: 1,
                              borderBottom: '2px solid #000000'
                            }}
                          >
                            {children}
                          </Typography>
                        ),
                        h3: ({ children }: { children?: React.ReactNode }) => (
                          <Typography 
                            variant="h6" 
                            sx={{
                              fontWeight: 600,
                              color: '#000000',
                              mt: 3,
                              mb: 2,
                              fontSize: '1.4em',
                              pb: 1,
                              borderBottom: '2px solid #000000',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            {children}
                          </Typography>
                        ),
                        h4: ({ children }: { children?: React.ReactNode }) => (
                          <Typography 
                            variant="subtitle1" 
                            sx={{
                              fontWeight: 600,
                              color: '#000000',
                              mt: 3,
                              mb: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            • {children}
                          </Typography>
                        ),
                        p: ({ children }: { children?: React.ReactNode }) => (
                          <Typography 
                            variant="body1" 
                            sx={{
                              color: '#333',
                              mb: 2,
                              lineHeight: 1.6,
                              fontSize: '1rem',
                              textAlign: 'justify',
                              fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif'
                            }}
                          >
                            {children}
                          </Typography>
                        ),
                        ul: ({ children }: { children?: React.ReactNode }) => (
                          <Box 
                            component="ul" 
                            sx={{
                              pl: 2,
                              mb: 2,
                              listStyleType: 'disc',
                              listStylePosition: 'outside',
                              '& li': {
                                mb: 1,
                                color: '#333',
                                lineHeight: 1.6,
                                pl: 0.5,
                                '&::marker': {
                                  color: '#000000',
                                  fontSize: '1.1em'
                                }
                              },
                              '& ul': {
                                listStyleType: 'circle',
                                mt: 0.5,
                                mb: 0.5,
                                pl: 2
                              },
                              '& ul ul': {
                                listStyleType: 'square'
                              }
                            }}
                          >
                            {children}
                          </Box>
                        ),
                        ol: ({ children }: { children?: React.ReactNode }) => (
                          <Box 
                            component="ol" 
                            sx={{
                              pl: 2.5,
                              mb: 2,
                              listStyleType: 'decimal',
                              listStylePosition: 'outside',
                              '& li': {
                                mb: 1,
                                color: '#333',
                                lineHeight: 1.6,
                                pl: 0.5,
                                '&::marker': {
                                  color: '#000000',
                                  fontWeight: 'bold'
                                }
                              },
                              '& ol': {
                                listStyleType: 'lower-alpha',
                                mt: 0.5,
                                mb: 0.5,
                                pl: 2
                              },
                              '& ol ol': {
                                listStyleType: 'lower-roman'
                              }
                            }}
                          >
                            {children}
                          </Box>
                        ),
                        li: ({ children }: { children?: React.ReactNode }) => (
                          <Box
                            component="li" 
                            sx={{
                              color: '#333',
                              lineHeight: 1.6,
                              mb: 1,
                              fontSize: '1rem',
                              '& strong': {
                                color: '#000000',
                                fontWeight: 600
                              },
                              '& p': {
                                mb: 0.5,
                                display: 'inline'
                              }
                            }}
                          >
                            {children}
                          </Box>
                        ),
                        strong: ({ children }: { children?: React.ReactNode }) => (
                          <Box
                            component="strong"
                            sx={{
                              fontWeight: 600,
                              color: '#000000'
                            }}
                          >
                            {children}
                          </Box>
                        ),
                        em: ({ children }: { children?: React.ReactNode }) => (
                          <Box
                            component="em"
                            sx={{
                              fontStyle: 'italic',
                              color: '#f57c00',
                              fontWeight: 500,
                              backgroundColor: '#fff8e1',
                              px: 0.5,
                              py: 0.25,
                              borderRadius: 0.5
                            }}
                          >
                            {children}
                          </Box>
                        ),
                        code: ({ children }: { children?: React.ReactNode }) => (
                          <Box
                            component="code"
                            sx={{
                              backgroundColor: '#f5f5f5',
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              fontFamily: '"Fira Code", Monaco, "Lucida Console", monospace',
                              fontSize: '0.9em',
                              color: '#d32f2f',
                              border: '1px solid #e0e0e0',
                              fontWeight: 500
                            }}
                          >
                            {children}
                          </Box>
                        ),
                        blockquote: ({ children }: { children?: React.ReactNode }) => (
                          <Box
                            component="blockquote"
                            sx={{
                              borderLeft: '5px solid var(--maersk-blue)',
                              pl: 3,
                              pr: 2,
                              py: 2,
                              my: 3,
                              backgroundColor: '#f8fbff',
                              borderRadius: '0 8px 8px 0',
                              fontStyle: 'italic',
                              color: 'var(--maersk-blue)',
                              position: 'relative',
                              boxShadow: '0 2px 8px rgba(0,103,179,0.1)',
                              '&::before': {
                                content: '"💡"',
                                position: 'absolute',
                                top: 16,
                                left: -12,
                                backgroundColor: 'var(--maersk-blue)',
                                color: 'white',
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px'
                              }
                            }}
                          >
                            {children}
                          </Box>
                        ),
                        a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
                          <Typography
                            component="a"
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: 'var(--maersk-blue)',
                              textDecoration: 'none',
                              fontWeight: 600,
                              borderBottom: '2px solid transparent',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                color: 'var(--maersk-dark-blue)',
                                borderBottom: '2px solid var(--maersk-blue)'
                              }
                            }}
                          >
                            {children}
                          </Typography>
                        )
                      }}
                    >
                      {/* Convert our structured data to markdown format */}
                      {`# AI Incident Analysis & Insights

## Executive Summary
**Analysis Period:** ${selectedTimeRange || 'Not specified'}  
**Incidents Analyzed:** 95  
**Applications Analyzed:** ${aiInsightsData?.data?.postmortem_insights?.monitoring_correlation?.applications_analyzed || 0}

${aiInsightsData?.data?.performance_summary ? `
## Performance Summary
${aiInsightsData.data.performance_summary}
` : ''}

## Application Health Overview
${aiInsightsData?.data?.postmortem_insights?.monitoring_correlation?.mvc_status_summary ? `
### Application Breakdown
- **Total Applications:** ${aiInsightsData?.data?.postmortem_insights?.monitoring_correlation?.mvc_status_summary?.total_applications || 0}
- **MVC Applications:** ${aiInsightsData?.data?.postmortem_insights?.monitoring_correlation?.mvc_status_summary?.mvc_applications || 0}
- **Non-MVC Applications:** ${aiInsightsData?.data?.postmortem_insights?.monitoring_correlation?.mvc_status_summary?.non_mvc_applications || 0}
- **Applications with Monitoring:** ${aiInsightsData?.data?.postmortem_insights?.monitoring_correlation?.mvc_status_summary?.monitoring_tracking_available || 0}
- **Applications without Monitoring:** ${aiInsightsData?.data?.postmortem_insights?.monitoring_correlation?.mvc_status_summary?.monitoring_tracking_unavailable || 0}
` : ''}

## Postmortem Insights & Analysis

### Action Items Analysis
${aiInsightsData?.data?.postmortem_insights?.action_items_analysis ? `
- **Total Action Items:** ${(() => {
    const actionTypes = aiInsightsData?.data?.postmortem_insights?.action_items_analysis?.action_type_distribution || {};
    const total = Object.values(actionTypes).reduce((sum: number, count: unknown) => sum + (Number(count) || 0), 0);
    return total || 0;
  })()}
- **Open Actions:** ${(() => {
    const actionTypes = aiInsightsData?.data?.postmortem_insights?.action_items_analysis?.action_type_distribution || {};
    const openCount = (Number(actionTypes['To Do']) || 0) + (Number(actionTypes['In Progress']) || 0) + (Number(actionTypes['General']) || 0);
    return openCount || 0;
  })()}
- **Completed Actions:** ${aiInsightsData?.data?.postmortem_insights?.action_items_analysis?.action_type_distribution?.Done || 0}
- **In Progress Actions:** ${aiInsightsData?.data?.postmortem_insights?.action_items_analysis?.action_type_distribution?.['In Progress'] || 0}
- **To Do Actions:** ${aiInsightsData?.data?.postmortem_insights?.action_items_analysis?.action_type_distribution?.['To Do'] || 0}
- **High Priority Actions:** ${aiInsightsData?.data?.postmortem_insights?.action_items_analysis?.high_priority_actions_count || 0}
- **Overdue Actions:** ${aiInsightsData?.data?.postmortem_insights?.action_items_analysis?.overdue_actions_count || 0}

${aiInsightsData?.data?.postmortem_insights?.action_items_analysis?.priority_breakdown ? `
#### Priority Breakdown
- **P1 (Critical):** ${aiInsightsData?.data?.postmortem_insights?.action_items_analysis?.priority_breakdown?.p1 || 0}
- **P2 (High):** ${aiInsightsData?.data?.postmortem_insights?.action_items_analysis?.priority_breakdown?.p2 || 0}
- **P3 (Medium):** ${aiInsightsData?.data?.postmortem_insights?.action_items_analysis?.priority_breakdown?.p3 || 0}
- **P4 (Low):** ${aiInsightsData?.data?.postmortem_insights?.action_items_analysis?.priority_breakdown?.p4 || 0}
` : ''}

${aiInsightsData?.data?.postmortem_insights?.action_items_analysis?.status_breakdown ? `
#### Status Distribution
- **Open:** ${aiInsightsData?.data?.postmortem_insights?.action_items_analysis?.status_breakdown?.open || 0}
- **In Progress:** ${aiInsightsData?.data?.postmortem_insights?.action_items_analysis?.status_breakdown?.in_progress || 0}
- **Under Review:** ${aiInsightsData?.data?.postmortem_insights?.action_items_analysis?.status_breakdown?.under_review || 0}
- **Completed:** ${aiInsightsData?.data?.postmortem_insights?.action_items_analysis?.status_breakdown?.completed || 0}
` : ''}
` : ''}

### Monitoring Correlation
${aiInsightsData?.data?.postmortem_insights?.monitoring_correlation ? `
- **Applications Analyzed:** ${aiInsightsData?.data?.postmortem_insights?.monitoring_correlation?.applications_analyzed || 0}
- **Monitoring Data Available:** ${aiInsightsData?.data?.postmortem_insights?.monitoring_correlation?.monitoring_data_available ? 'Yes' : 'No'}
- **Correlation Success Rate:** Available for analysis
- **Mean Time to Detection:** Tracking enabled for monitored applications
` : ''}

${(aiInsightsData?.data?.postmortem_insights?.monitoring_correlation?.incident_prevention_opportunities?.alert_effectiveness_improvements?.length ?? 0) > 0 ? `
#### Alert Effectiveness Improvements
${aiInsightsData?.data?.postmortem_insights?.monitoring_correlation?.incident_prevention_opportunities?.alert_effectiveness_improvements?.map((item: any) => 
  `- **${item.action}**
  - Current Rate: ${item.current_rate}
  - Target Rate: ${item.target_rate}
  - Missed Incidents: ${item.missed_incidents}`).join('\n\n')}
` : ''}

${(aiInsightsData?.data?.postmortem_insights?.monitoring_correlation?.root_cause_monitoring_gaps?.action_items?.length ?? 0) > 0 ? `
#### Root Cause Monitoring Gaps
${aiInsightsData?.data?.postmortem_insights?.monitoring_correlation?.root_cause_monitoring_gaps?.action_items?.map((item: any) =>
  `- **${item.affected_applications}**
  - Root Cause: ${item.root_cause}
  - Missing Capabilities: ${item.missing_capabilities?.join(', ') || 'N/A'}${item.priority ? `
  - Priority: ${item.priority}` : ''}${item.incident_count ? `
  - Incident Count: ${item.incident_count}` : ''}${item.action ? `
  - Recommended Action: ${item.action}` : ''}`).join('\n\n')}

#### Application-Specific Monitoring Gaps
${aiInsightsData?.data?.postmortem_insights?.monitoring_correlation?.root_cause_monitoring_gaps?.action_items?.flatMap((item: any) => 
  item.affected_apps_details?.map((app: any) => 
    `- **${app.application}**
  - Root Cause: ${item.root_cause}
  - Missing Capabilities: ${app.missing_capabilities?.join(', ') || 'N/A'}
  - Specific Recommendations: ${app.specific_recommendations?.join(', ') || 'N/A'}${app.monitoring_available !== undefined ? `
  - Monitoring Available: ${app.monitoring_available ? 'Yes' : 'No'}` : ''}${app.is_mvc !== undefined ? `
  - MVC Status: ${app.is_mvc ? 'MVC Application' : 'Non-MVC Application'}` : ''}`)
  .filter(Boolean) || []).join('\n\n')}
` : ''}${(aiInsightsData?.data?.postmortem_insights?.root_cause_analysis?.top_root_causes?.length ?? 0) > 0 ? `
#### Top Root Causes
${aiInsightsData?.data?.postmortem_insights?.root_cause_analysis?.top_root_causes?.map((cause: string, index: number) =>
  `${index + 1}. ${cause}`).join('\n')}
` : ''}
### Reliability & Postmortem Correlation
${aiInsightsData?.data?.postmortem_insights?.reliability_postmortem_correlation?.current_portfolio ? `
#### ${aiInsightsData?.data?.postmortem_insights?.reliability_postmortem_correlation?.current_portfolio?.name || 'Current Portfolio'}
- **Postmortems Found:** ${aiInsightsData?.data?.postmortem_insights?.reliability_postmortem_correlation?.current_portfolio?.postmortem_count || 0}
- **Action Items Total:** ${aiInsightsData?.data?.postmortem_insights?.reliability_postmortem_correlation?.current_portfolio?.action_items_count || 0}
- **Completed Actions:** ${aiInsightsData?.data?.postmortem_insights?.reliability_postmortem_correlation?.current_portfolio?.action_items_summary?.done || 0}
- **In Progress Actions:** ${aiInsightsData?.data?.postmortem_insights?.reliability_postmortem_correlation?.current_portfolio?.action_items_summary?.in_progress || 0}
- **Action Items Summary:** ${aiInsightsData?.data?.postmortem_insights?.reliability_postmortem_correlation?.current_portfolio?.action_items_summary?.total || 0} total actions tracked
` : ''}

${aiInsightsData?.data?.postmortem_insights?.reliability_postmortem_correlation?.other_portfolios ? `
#### Cross-Portfolio Impact
- **External Action Items:** ${aiInsightsData?.data?.postmortem_insights?.reliability_postmortem_correlation?.other_portfolios?.action_items_count || 0}
- **Cross-Portfolio Issues:** ${aiInsightsData?.data?.postmortem_insights?.reliability_postmortem_correlation?.other_portfolios?.cross_portfolio_impact?.description || 'None identified'}
- **Other Portfolios Involved:** ${aiInsightsData?.data?.postmortem_insights?.reliability_postmortem_correlation?.other_portfolios?.portfolios_involved?.filter((p: string) => p && p.trim()).join(', ') || 'None'}
- **Postmortems in Other Portfolios:** ${aiInsightsData?.data?.postmortem_insights?.reliability_postmortem_correlation?.other_portfolios?.postmortems?.length || 0}
` : ''}

${aiInsightsData?.data?.postmortem_insights?.reliability_postmortem_correlation?.total_incidents_analyzed ? `
- **Total Incidents Analyzed:** ${aiInsightsData?.data?.postmortem_insights?.reliability_postmortem_correlation?.total_incidents_analyzed}
- **Total Postmortems Found:** ${aiInsightsData?.data?.postmortem_insights?.reliability_postmortem_correlation?.total_postmortems_found || 0}
- **Data Source:** ${aiInsightsData?.data?.postmortem_insights?.reliability_postmortem_correlation?.data_source || 'Spring PM API'}
` : ''}

${aiInsightsData?.data?.prediction ? `
## Prediction Analysis
${aiInsightsData.data.prediction.predicted_outcome || 'No prediction analysis available.'}

### Technical Indicators
${aiInsightsData.data.prediction.technical_indicators || 'No specific technical indicators identified.'}

### Time to Impact
${aiInsightsData.data.prediction.time_to_impact || 'No time to impact estimate available.'}
` : ''}

${aiInsightsData?.data?.technical_roadmap ? `
## Technical Roadmap

### Key Initiatives
${(aiInsightsData?.data?.technical_roadmap?.immediate_actions?.length ?? 0) > 0 ? `
#### Immediate Actions (0-30 days)
${aiInsightsData?.data?.technical_roadmap?.immediate_actions?.map((action: any) => `- ${action}`).join('\n')}
` : ''}

${(aiInsightsData?.data?.technical_roadmap?.short_term_improvements?.length ?? 0) > 0 ? `
#### Short-term Improvements (1-2 months)  
${aiInsightsData?.data?.technical_roadmap?.short_term_improvements?.map((improvement: any) => `- ${improvement}`).join('\n')}
` : ''}

${(aiInsightsData?.data?.technical_roadmap?.long_term_optimization?.length ?? 0) > 0 ? `
#### Long-term Optimization (3-12 months)
${aiInsightsData?.data?.technical_roadmap?.long_term_optimization?.map((optimization: any) => `- ${optimization}`).join('\n')}
` : ''}

${(!aiInsightsData?.data?.technical_roadmap?.immediate_actions?.length && 
  !aiInsightsData?.data?.technical_roadmap?.short_term_improvements?.length && 
  !aiInsightsData?.data?.technical_roadmap?.long_term_optimization?.length) ? 'No key initiatives defined.' : ''}
` : ''}

${aiInsightsData?.data?.root_cause ? `
## Root Cause Analysis
- **Predicted Root Cause:** ${aiInsightsData.data.root_cause.predicted_root_cause || 'N/A'}
- **Related Components:** ${aiInsightsData.data.root_cause.related_components?.join(', ') || 'N/A'}
` : ''}

${aiInsightsData?.data?.recommendation ? `
## AI Recommendations
- **Priority:** ${aiInsightsData.data.recommendation.action_priority || 'N/A'}
- **Expected Resolution Time:** ${aiInsightsData.data.recommendation.expected_resolution_time || 'N/A'}
- **Recommended Action:** ${aiInsightsData.data.recommendation.recommended_action || 'N/A'}
` : ''}

${aiInsightsData?.data?.prediction ? `
## Prediction & Outlook
- **Predicted Outcome:** ${aiInsightsData.data.prediction.predicted_outcome || 'N/A'}
- **Technical Indicators:** ${aiInsightsData.data.prediction.technical_indicators || 'N/A'}
- **Time to Impact:** ${aiInsightsData.data.prediction.time_to_impact || 'N/A'}
` : ''}

${aiInsightsData?.data?.reliability_improvement ? `
## Reliability Improvement Plan
- **Priority Level:** ${aiInsightsData.data.reliability_improvement.priority_level || 'N/A'}
- **Impact Estimate:** ${aiInsightsData.data.reliability_improvement.impact_estimate || 'N/A'}
- **Business Justification:** ${aiInsightsData.data.reliability_improvement.business_justification || 'N/A'}
- **Technical Specifications:** ${aiInsightsData.data.reliability_improvement.technical_specifications || 'N/A'}
` : ''}

---
*Analysis completed in ${aiInsightsData?.data?.analysis_duration_seconds?.toFixed(2)} seconds • Scope: ${aiInsightsData?.data?.scope_name} • Generated: ${aiInsightsData?.data?.analysis_timestamp ? new Date(aiInsightsData.data.analysis_timestamp).toLocaleString() : 'N/A'}*`}
                    </ReactMarkdown>
                  </Box>
                </Box>
              </Paper>
            </Box>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              minHeight: '400px',
              p: 4 
            }}>
              <CircularProgress size={60} sx={{ color: 'var(--maersk-blue)', mb: 2 }} />
              <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', mb: 1 }}>
                Generating AI insights for your incident analysis...
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', fontStyle: 'italic' }}>
                This may take a few moments while we analyze your data.
              </Typography>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ 
          px: 3, 
          py: 2, 
          backgroundColor: '#f8f9fa',
          borderTop: '1px solid #dee2e6',
          gap: 1
        }}>
          <Button onClick={() => setAiInsightsOpen(false)} variant="outlined">
            Close
          </Button>
          {aiInsightsData && (
            <Button 
              variant="contained" 
              startIcon={<PictureAsPdfIcon />}
              sx={{
                backgroundColor: 'var(--maersk-blue)',
                '&:hover': {
                  backgroundColor: 'var(--maersk-dark-blue)'
                }
              }}
              onClick={async () => {
                try {
                  // Show loading message
                  setSnackbarMessage('Generating PDF, please wait...');
                  setSnackbarSeverity('info');
                  setSnackbarOpen(true);
                  
                  await PDFExportService.exportAIIncidentInsightsToPDF(
                    aiInsightsData,
                    selectedTimeRange,
                    {
                      filename: `ai-incident-insights-${selectedTimeRange}-${new Date().toISOString().split('T')[0]}.pdf`
                    }
                  );
                  
                  // PDF generated and download triggered
                  setSnackbarMessage('PDF download started! Check your downloads folder.');
                  setSnackbarSeverity('success');
                  setSnackbarOpen(true);
                } catch (error) {
                  console.error('Error exporting AI insights to PDF:', error);
                  setSnackbarMessage('Failed to export AI insights. Please try again.');
                  setSnackbarSeverity('error');
                  setSnackbarOpen(true);
                }
              }}
            >
              Export PDF
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* CI Insights Modal */}
      <Dialog 
        open={ciInsightsOpen} 
        onClose={handleCloseCIInsights}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">CI Insights</Typography>
            <IconButton onClick={handleCloseCIInsights}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {ciInsights && (
            <Box>
              {/* CI Header Info */}
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                {ciInsights.ciName}
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                    <Typography variant="subtitle2" color="textSecondary">Platform</Typography>
                    <Typography variant="body1" fontWeight="bold">{ciInsights.platform}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                    <Typography variant="subtitle2" color="textSecondary">Portfolio</Typography>
                    <Typography variant="body1" fontWeight="bold">{ciInsights.portfolio}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, backgroundColor: '#f44336', color: 'white' }}>
                    <Typography variant="subtitle2" color="textSecondary">Total Alerts</Typography>
                    <Typography variant="h4" fontWeight="bold">{ciInsights.totalAlerts}</Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Alert Capture Statistics */}
              <Paper sx={{ p: 2, mb: 3, backgroundColor: '#fff3e0' }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Alert Capture Statistics:
                </Typography>
                <Typography variant="body2">
                  {ciInsights.alertCaptureInfo.map((capture) => 
                    `${capture.code}: ${capture.percentage}%`
                  ).join(' | ')}
                </Typography>
              </Paper>

              {/* Alert Capture Filter */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                    Top 5 Alert Types:
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Select Closure Codes</InputLabel>
                      <Select
                        multiple
                        value={selectedClosureCodes}
                        onChange={handleClosureCodeChange}
                        label="Select Closure Codes"
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selected as string[]).map((value) => (
                              <Chip key={value} label={value} size="small" />
                            ))}
                          </Box>
                        )}
                      >
                        {ciInsights.availableClosureCodes.map((closeCode) => (
                          <MenuItem key={closeCode} value={closeCode} sx={{ py: 0.5 }}>
                            <Checkbox 
                              checked={selectedClosureCodes.includes(closeCode)}
                              size="small"
                              color="primary"
                              sx={{ mr: 1 }}
                            />
                            <ListItemText 
                              primary={closeCode}
                              primaryTypographyProps={{ 
                                variant: 'body2',
                                sx: { 
                                  fontWeight: selectedClosureCodes.includes(closeCode) ? 'bold' : 'normal' 
                                }
                              }}
                            />
                          </MenuItem>
                        ))}
                      </Select>
                      <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                        {selectedClosureCodes.length === 0 
                          ? 'Select closure codes to see combined percentages'
                          : `Showing combined percentage for ${selectedClosureCodes.length} selected closure code(s)`
                        }
                      </Typography>
                    </FormControl>
                    {selectedClosureCodes.length > 0 && (
                      <Button 
                        variant="outlined" 
                        size="small" 
                        onClick={() => setSelectedClosureCodes([])}
                        sx={{ mt: 0.5, minWidth: 'auto', px: 1 }}
                      >
                        Clear
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>
              
              <TableContainer component={Paper}>
                <Table size="small" sx={{ 
                  '& .MuiTableCell-root': {
                    padding: '8px 12px',
                    fontSize: '0.875rem',
                    lineHeight: 1.4,
                    border: '1px solid #e0e0e0'
                  },
                  '& .MuiTableCell-head': {
                    fontWeight: 600,
                    backgroundColor: '#f5f5f5',
                    color: '#333',
                    fontSize: '0.8rem'
                  },
                  '& .MuiTableRow-root': {
                    '&:hover': {
                      backgroundColor: '#f9f9f9'
                    }
                  }
                }}>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Alert Description</strong></TableCell>
                      <TableCell align="center"><strong>Count</strong></TableCell>
                      <TableCell align="center"><strong>% of App Alerts</strong></TableCell>
                      <TableCell align="center">
                        <strong>% of Noise</strong>
                      </TableCell>
                      <TableCell>
                        <strong>
                          {selectedClosureCodes.length > 0 
                            ? `Selected Closure Codes (${selectedClosureCodes.length})` 
                            : 'Top 3 Closure Codes'
                          }
                        </strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ciInsights.topAlertTypes.map((alertType, index) => (
                      <TableRow key={index} sx={{ backgroundColor: index % 2 === 0 ? '#f9f9f9' : 'white' }}>
                        <TableCell sx={{ maxWidth: 300 }}>
                          <Typography variant="body2">
                            {alertType.description}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="bold">
                            {alertType.count}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="bold">
                            {alertType.percentage}%
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={Math.min(alertType.noisePercentage, 100)} 
                              sx={{ width: 60, height: 8 }}
                              color={alertType.noisePercentage > 50 ? 'error' : alertType.noisePercentage > 25 ? 'warning' : 'success'}
                            />
                            <Typography 
                              variant="body2" 
                              fontWeight="bold"
                              sx={{ minWidth: '45px', textAlign: 'left' }}
                            >
                              {alertType.noisePercentage}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {selectedClosureCodes.length > 0 
                            ? (
                              <Box>
                                {alertType.selectedClosurePercentages.map((closure, idx) => (
                                  <Typography 
                                    key={idx} 
                                    variant="body2" 
                                    fontWeight="bold" 
                                    color={closure.percentage === 0 ? "textSecondary" : "primary"} 
                                    sx={{ mb: 0.5 }}
                                  >
                                    {closure.code}: {closure.percentage}%
                                  </Typography>
                                ))}
                              </Box>
                            )
                            : (
                              <Typography variant="body2">
                                {alertType.selectedClosurePercentages.map(closure => 
                                  `${closure.code}: ${closure.percentage}%`
                                ).join(' | ')}
                              </Typography>
                            )
                          }
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCIInsights} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Incident Details Dialog - Flash Card Click */}
      <Dialog
        open={incidentDialogOpen}
        onClose={() => setIncidentDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '70vh',
            maxHeight: '90vh',
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#1565c0',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorIcon sx={{ fontSize: '24px' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {dialogTitle}
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setIncidentDialogOpen(false)}
            sx={{ 
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <Typography sx={{ fontSize: '20px' }}>×</Typography>
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <TableContainer component={Paper}>
            <Table size="small" sx={{
              '& .MuiTableCell-root': { padding: '8px 12px', fontSize: '0.875rem', lineHeight: 1.4, border: '1px solid #e0e0e0' },
              '& .MuiTableCell-head': { fontWeight: 600, backgroundColor: '#f5f5f5', color: '#333', fontSize: '0.8rem' },
              '& .MuiTableRow-root': { '&:hover': { backgroundColor: '#f9f9f9' } }
            }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0', minWidth: 100 }}>Incident ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0', minWidth: 200 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0', minWidth: 120 }}>Affected App</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0', minWidth: 140 }}>Affected Portfolio</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0', minWidth: 140 }}>Affected Platform</TableCell>
                  {dialogPriority === 'p1p2' && <TableCell sx={{ fontWeight: 600, color: '#1565c0', minWidth: 160 }}>Caused by App</TableCell>}
                  {dialogPriority === 'p1p2' && <TableCell sx={{ fontWeight: 600, color: '#1565c0', minWidth: 140 }}>Caused by Platform</TableCell>}
                  {dialogPriority === 'p1p2' && <TableCell sx={{ fontWeight: 600, color: '#1565c0', minWidth: 140 }}>Caused by Portfolio</TableCell>}
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0', minWidth: 150 }}>Resolved Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0', minWidth: 70 }}>Priority</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0', minWidth: 80 }}>Status</TableCell>
                  {dialogPriority === 'p1p2' && <TableCell sx={{ fontWeight: 600, color: '#1565c0', minWidth: 100 }}>MTTD (Avg)</TableCell>}
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0', minWidth: 100 }}>MTTR (Avg)</TableCell>
                  {dialogPriority === 'p1p2' && <TableCell sx={{ fontWeight: 600, color: '#1565c0', minWidth: 100 }}>MTTD (Avg)</TableCell>}
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0', minWidth: 80 }}>Alert</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1565c0', minWidth: 80 }}>Change</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dialogIncidents
                  .slice(dialogPage * dialogRowsPerPage, dialogPage * dialogRowsPerPage + dialogRowsPerPage)
                  .map((inc, index) => (
                    <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                      {/* Incident ID */}
                      <TableCell>
                        <Link
                          href={`https://maersk.service-now.com/nav_to.do?uri=incident_list.do?sysparm_query=number=${inc.number}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ color: '#0067b3', textDecoration: 'none', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
                        >
                          {inc.number}
                        </Link>
                      </TableCell>
                      {/* Description */}
                      <TableCell sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <MUITooltip title={inc.short_description || 'No description available'} arrow>
                          <span>{inc.short_description || 'N/A'}</span>
                        </MUITooltip>
                      </TableCell>
                      {/* Affected App */}
                      <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <MUITooltip title={inc.application || 'No application specified'} arrow>
                          <span>{inc.application || 'N/A'}</span>
                        </MUITooltip>
                      </TableCell>
                      {/* Affected Portfolio */}
                      <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <MUITooltip title={inc.portfolio || 'No portfolio specified'} arrow>
                          <span>{inc.portfolio || 'N/A'}</span>
                        </MUITooltip>
                      </TableCell>
                      {/* Affected Platform */}
                      <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <MUITooltip title={inc.platform || 'No platform specified'} arrow>
                          <span>{inc.platform || 'N/A'}</span>
                        </MUITooltip>
                      </TableCell>
                      {/* Caused by App */}
                      {dialogPriority === 'p1p2' && (
                        <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <MUITooltip title={inc.caused_by_app || 'No caused by application'} arrow>
                            <span>{inc.caused_by_app || 'N/A'}</span>
                          </MUITooltip>
                        </TableCell>
                      )}
                      {/* Caused by Platform */}
                      {dialogPriority === 'p1p2' && (
                        <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <MUITooltip title={inc.caused_by_platform || 'No platform specified'} arrow>
                            <span>{inc.caused_by_platform || 'N/A'}</span>
                          </MUITooltip>
                        </TableCell>
                      )}
                      {/* Caused by Portfolio */}
                      {dialogPriority === 'p1p2' && (
                        <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <MUITooltip title={inc.caused_by_portfolio || 'No portfolio specified'} arrow>
                            <span>{inc.caused_by_portfolio || 'N/A'}</span>
                          </MUITooltip>
                        </TableCell>
                      )}
                      {/* Resolved Date */}
                      <TableCell sx={{ minWidth: 150 }}>
                        {inc.resolvedDate ? new Date(inc.resolvedDate).toISOString().replace('T', ' ').slice(0, -5) + ' UTC' : 'N/A'}
                      </TableCell>
                      {/* Priority */}
                      <TableCell>
                        <Chip
                          label={inc.priority}
                          sx={{
                            backgroundColor: getPriorityColor(inc.priority),
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        />
                      </TableCell>
                      {/* Status */}
                      <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {inc.status}
                      </TableCell>
                      {/* MTTD - Only for P1/P2 */}
                      {dialogPriority === 'p1p2' && (
                        <TableCell>
                          {inc.mttd !== undefined ? (
                            <MUITooltip title={`${inc.mttd.toFixed(0)} seconds (${(inc.mttd / 60).toFixed(1)} minutes)`}>
                              <span style={{ fontWeight: 600, color: '#1565c0' }}>
                                {(() => {
                                  const hours = Math.floor(inc.mttd / 3600);
                                  const mins = Math.round((inc.mttd % 3600) / 60);
                                  return `${hours}h ${mins}m`;
                                })()}
                              </span>
                            </MUITooltip>
                          ) : (
                            <span style={{ color: '#999' }}>-</span>
                          )}
                        </TableCell>
                      )}
                      {/* MTTR */}
                      <TableCell>
                        {inc.mttr !== undefined ? (
                          <MUITooltip title={`${inc.mttr.toFixed(0)} seconds (${(inc.mttr / 60).toFixed(1)} minutes)`}>
                            <span style={{ fontWeight: 600, color: '#1565c0' }}>
                              {(() => {
                                const hours = Math.floor(inc.mttr / 3600);
                                const mins = Math.round((inc.mttr % 3600) / 60);
                                return `${hours}h ${mins}m`;
                              })()}
                            </span>
                          </MUITooltip>
                        ) : (
                          <span style={{ color: '#999' }}>N/A</span>
                        )}
                      </TableCell>
                      {/* MTTD */}
                      {dialogPriority === 'p1p2' && (
                        <TableCell>
                          {(() => {
                            const mttd = mttdData.get(inc.number);
                            if (mttd !== undefined && mttd > 0) {
                              const hours = Math.floor(mttd / 3600);
                              const mins = Math.round((mttd % 3600) / 60);
                              return (
                                <MUITooltip title={`${mttd.toFixed(0)} seconds (${(mttd / 60).toFixed(1)} minutes)`}>
                                  <span style={{ fontWeight: 600, color: '#e65100' }}>
                                    {`${hours}h ${mins}m`}
                                  </span>
                                </MUITooltip>
                              );
                            }
                            return <span style={{ color: '#999' }}>N/A</span>;
                          })()}
                        </TableCell>
                      )}
                      {/* Alert */}
                      <TableCell>
                        <Box sx={{
                          display: 'inline-flex', alignItems: 'center', px: 1, py: 0.5, borderRadius: 1,
                          fontSize: '0.75rem', fontWeight: 600,
                          backgroundColor: inc.capturedByAlert ? '#e8f5e8' : '#fff3e0',
                          color: inc.capturedByAlert ? '#2e7d32' : '#f57c00'
                        }}>
                          {inc.capturedByAlert ? 'Yes' : 'No'}
                        </Box>
                      </TableCell>
                      {/* Change */}
                      <TableCell>
                        {inc.causedByChange ? (
                          <MUITooltip title={`Change Number: ${inc.changeNumber}`} arrow>
                            <Link
                              href={`https://maersk.service-now.com/change_request_list.do?sysparm_query=number=${inc.changeNumber}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ textDecoration: 'none' }}
                            >
                              <Box sx={{
                                display: 'inline-flex', alignItems: 'center', px: 1, py: 0.5, borderRadius: 1,
                                fontSize: '0.75rem', fontWeight: 600,
                                backgroundColor: '#fff3e0', color: '#e65100', cursor: 'pointer',
                                '&:hover': { backgroundColor: '#ffe0b2' }
                              }}>
                                Yes
                              </Box>
                            </Link>
                          </MUITooltip>
                        ) : (
                          <Box sx={{
                            display: 'inline-flex', alignItems: 'center', px: 1, py: 0.5, borderRadius: 1,
                            fontSize: '0.75rem', fontWeight: 600,
                            backgroundColor: '#fafafa', color: '#757575'
                          }}>
                            No
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {dialogIncidents.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="textSecondary">
                No incidents found for the selected filters.
              </Typography>
            </Box>
          )}
          
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={dialogIncidents.length}
            rowsPerPage={dialogRowsPerPage}
            page={dialogPage}
            onPageChange={(event, newPage) => setDialogPage(newPage)}
            onRowsPerPageChange={(event) => {
              setDialogRowsPerPage(parseInt(event.target.value, 10));
              setDialogPage(0);
            }}
          />
        </DialogContent>
        
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button 
            onClick={() => setIncidentDialogOpen(false)} 
            variant="contained"
            sx={{
              backgroundColor: '#1565c0',
              '&:hover': {
                backgroundColor: '#0d47a1'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Incident Details Dialog */}
      <Dialog
        open={incidentDetailsOpen}
        onClose={() => setIncidentDetailsOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '60vh',
            maxHeight: '85vh',
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#1565c0',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontWeight: 700,
          fontSize: '1.25rem'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoOutlined />
            <span>Incident Details ({selectedPeriodIncidents.length} incidents)</span>
          </Box>
          <IconButton 
            onClick={() => setIncidentDetailsOpen(false)}
            sx={{ 
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <TableContainer sx={{ maxHeight: 'calc(85vh - 140px)' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f7fa' }}>
                  <TableCell sx={{ fontWeight: 700, color: '#001a35', minWidth: 140 }}>Incident Number</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#001a35', minWidth: 200 }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#001a35', minWidth: 100 }}>Priority</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#001a35', minWidth: 120 }}>State</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#001a35', minWidth: 180 }}>Portfolio</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#001a35', minWidth: 180 }}>Application</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#001a35', minWidth: 140 }}>Created Date</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#001a35', minWidth: 140 }}>Resolved Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedPeriodIncidents.map((incident, idx) => (
                  <TableRow 
                    key={incident.id || idx}
                    sx={{ 
                      '&:hover': { backgroundColor: '#f5f7fa' },
                      '&:nth-of-type(even)': { backgroundColor: '#fafafa' }
                    }}
                  >
                    <TableCell sx={{ fontSize: '0.875rem', color: '#1565c0', fontWeight: 600 }}>
                      {incident.number || '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#001a35' }}>
                      {incident.title || incident.short_description || '-'}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={incident.priority || '-'}
                        size="small"
                        color={incident.priority === 'P1' || incident.priority === 'P2' ? 'error' : 'default'}
                        sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#001a35' }}>
                      {incident.status || '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#001a35' }}>
                      {incident.portfolio || '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#001a35' }}>
                      {incident.application || '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#666' }}>
                      {incident.createdDate ? new Date(incident.createdDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#666' }}>
                      {incident.resolvedDate ? new Date(incident.resolvedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
                {selectedPeriodIncidents.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#666' }}>
                      No incidents found for this period
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 2, backgroundColor: '#f5f7fa' }}>
          <Button 
            onClick={() => setIncidentDetailsOpen(false)}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={4000} 
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default IncidentAnalysis;

